import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useAgentListFocus } from "@/hooks/useAgentListFocus";
import {
  caseCheckingCodec,
  normalizeCaseChecking,
  type CaseCheckingDetailQuery,
} from "@/navigation/apps/caseCheckingLocation";
import { useAppLocation } from "@/navigation/appLocation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faChevronRight,
  faMagnifyingGlass,
  faArrowLeft,
  faXmark,
  faFile,
  faSpinner,
  faCircleCheck,
  faTriangleExclamation,
  faFileLines,
  faFileContract,
  faFilePen,
  faFileShield,
  faCircleXmark,
  faUserCheck,
  faScaleBalanced,
  faClipboardCheck,
  faRotate,
  faDownload,
  faCircleMinus,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { faArrowUpRightFromSquare } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import clsx from "clsx";
import { CaseCheckMissionProgressDialog } from "@/components/case-checking/CaseCheckMissionProgressDialog";
import { CaseListProgressBadge } from "@/components/case-checking/CaseListProgressBadge";
import { ClassificationFailedBanner } from "@/components/case-checking/ClassificationFailedBanner";
import {
  buildDocumentClassificationFilterOptions,
  DOCUMENT_CLASSIFICATION_FILTER_ALL,
  type DocumentClassificationFilterId,
} from "@/components/case-checking/documentClassificationFilters";
import {
  collectClassificationFailures,
  documentClassificationLabel,
  isDocumentClassificationFailed,
  isDocumentClassifying,
} from "@/components/case-checking/documentClassificationUi";
import { DocumentContentUnreadableHint } from "@/components/case-checking/DocumentContentUnreadableHint";
import { useCaseCheckingRealtime } from "@/components/case-checking/useCaseCheckingRealtime";
import {
  CaseTenantProvider,
  useCaseCheckingTenantId,
} from "@/components/case-checking/caseTenantContext";
import { CaseNewCaseFileUpload } from "@/components/case-checking/CaseNewCaseFileUpload";
import { ProgenyNewCaseDialog } from "@/components/case-checking/ProgenyNewCaseDialog";
import { SoderbergPlannrNewCaseDialog } from "@/components/case-checking/SoderbergPlannrNewCaseDialog";
import { useCaseNewCaseFileUpload } from "@/components/case-checking/useCaseNewCaseFileUpload";
import { CaseDetailDataProvider, useCaseDetailData } from "@/components/case-checking/CaseDetailDataContext";
import { CaseContextBar } from "@/components/case-checking/CaseContextBar";
import { CaseDocumentFileActions } from "@/components/case-checking/CaseDocumentFileActions";
import { PortfolioInsightsView } from "@/components/case-checking/PortfolioInsightsView";
import { ProgenyPortfolioInsightsView } from "@/components/case-checking/ProgenyPortfolioInsightsView";
import {
  buildCaseContextSummary,
  formatClientsHeading,
  formatClientsSubline,
  getAdviceScopeReasoning,
} from "@/components/case-checking/caseReviewContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table/Table";
import {
  getCaseDocuments,
  getCase,
  deleteCase,
  downloadPeerReviewXlsx,
  downloadCompletenessXlsx,
  downloadAssessmentRulesXlsx,
  downloadRemedialsXlsx,
  downloadRulesAuditXlsx,
  downloadProblemStatementsAgentDocx,
  downloadProblemStatementsComparisonDocx,
  downloadAdviceQualityFileReviewDocx,
  viewAdviceQualityFileReviewPdf,
  viewProblemStatementsAgentPdf,
  viewProblemStatementsComparisonPdf,
  listCaseArtifacts,
  type Case as ApiCase,
  type CaseDocument,
  type CaseDocumentKeyFigureValue,
  type CaseType as ApiCaseType,
  type Assessment as ApiAssessment,
  type CompletenessCheck as ApiCompleteness,
  type CaseReview as ApiCaseReview,
  type RemedialAction as ApiRemedialAction,
  type CaseInconsistency,
  type CaseTimeline,
  type AdviceScope,
  type AdvisedParty,
  listReviewProfiles,
  recoverCaseCheck,
  triggerCaseCheckIngest,
  deleteCaseDocument,
  applyPendingCarryRestore,
  type CaseAgenticMissionLink,
  type CaseMetadata,
  type MissingDocument,
  type CaseRole,
  type RoleGrade,
  CASE_ROLES,
  CASE_ROLE_LABELS,
  type DocumentAdditionQueue,
  type RuleCitationSnapshot,
} from "@/services/api/case-checking";
import {
  caseCheckKeys,
  useCaseCheckAllCases,
  useCaseCheckCasesPage,
  useCaseCheckDetail,
  useCaseCheckMissionStatuses,
} from "@/services/api/case-checking/queries";
import {
  getActiveRuleSetVersion,
  type ChronologyCheck,
  type RuleSetVersion,
} from "@/services/api/case-checking/rule-sets";
import FormInput from "../ui/form-input";
import { Button } from "../ui/button/Button";
import { SegmentedTabs, type SegmentedTab } from "@/components/ui/segmented-tabs";
import {
  buildCaseInsightNavItems,
  computeCaseDetailInsights,
  isOpenRemedialAction,
  pickReviewerNotesArtifact,
  type CaseInsightNavItem,
} from "@/components/case-checking/caseDetailInsights";
import { summarizeChecklistItems } from "@/components/case-checking/checklistSummary";
import {
  countInconsistencyRollups,
  inconsistencyRollupStatus,
  INCONSISTENCY_ROLLUP_BADGE,
  INCONSISTENCY_ROLLUP_DOT,
  INCONSISTENCY_ROLLUP_LABEL,
  isChronologyCheckId,
} from "@/components/case-checking/inconsistencyRollup";
import { findRuleCoverageGaps, ruleIdsFor } from "@/components/case-checking/remedialCoverage";
import {
  activeCaseTypeForScope,
  ruleAppliesToCase,
  rulesTabBadge,
} from "@/components/case-checking/assessmentRuleScope";
import {
  documentRequirementApplies,
  transactionSubtypeLabel,
} from "@/components/case-checking/documentRequirementsScope";
import { MissionControlCard } from "@/components/mission-control/MissionControlCard";
import { SegmentedPillToggle } from "@/components/ui/segmented-tabs/SegmentedPillToggle";
import { CoverageGapBanner } from "@/components/case-checking/coverageGapBanner";
import {
  CC_ACCENT_LINK,
  CC_GHOST_BTN,
  CC_INNER,
  CC_LEGEND,
  CC_PANEL,
  CC_PANEL_FLUSH,
  CC_ROW,
  CC_LIST_CARD_SHELL,
  CC_SEARCH,
  CC_SECTION_LABEL,
  CC_ERROR_TEXT,
  CC_STAT_ATTENTION,
  CC_STAT_NEGATIVE,
  CC_STAT_POSITIVE,
  CC_SUBTLE_LINK,
  CC_TAB_PANEL_PT,
  CC_TOOLBAR,
} from "@/components/case-checking/caseCheckingChrome";
import { Accordion } from "../ui/accordion";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";
import { ConfirmDeleteDialog } from "@/components/ui/dialog/ConfirmDeleteDialog";
import Drawer from "@/components/ui/drawer";
import { OsLink, useOsNavigate } from "@/components/ui/os-layout/OsLink";
import { useCloseWindow, useWindowTitle } from "@/components/ui/os-layout/useWindow";
import { useIsOsMode } from "@/components/ui/os-layout/OsContext";
import { useWindowId } from "@/components/ui/os-layout/OsWindow";
import {
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";
import { DestructiveTrashIconButton } from "@/components/ui/DestructiveTrashIconButton";
import {
  CaseListGradeBadge,
  CaseTypeTagBadge,
  DocumentClassificationStatusBadge,
  HumanReviewStatusChip,
  ICON_CHECK_PASSED,
  caseReviewRollupGrade,
  type CaseCheckingGrade,
  type HumanReviewRouting,
} from "./caseCheckingStatusBadges";
import { AdjudicationTab } from "@/components/case-checking/AdjudicationTab";
import { CaseReviewHeader, type CaseShellMode } from "@/components/case-checking/CaseReviewShell";
import { CaseSummaryView } from "@/components/case-checking/CaseSummaryView";
import {
  CaseReviewWorkflow,
  type ReviewWorkflowStep,
} from "@/components/case-checking/CaseReviewWorkflow";
import { ReviewProblemStatementRulesStep } from "@/components/case-checking/ReviewProblemStatementRulesStep";
import { ReviewOverallAssessmentStep } from "@/components/case-checking/ReviewOverallAssessmentStep";
import { ReviewActionsStep } from "@/components/case-checking/ReviewActionsStep";
import { ReviewCaseSummaryStep } from "@/components/case-checking/ReviewCaseSummaryStep";
import { isOpenEffectiveAction, mergeRemedialActions } from "@/components/case-checking/remedialActionEdits";
import { useGuidedReviewSession } from "@/components/case-checking/useGuidedReviewSession";
import { ConfirmCaseRerunDialog, type CaseRerunConfirmOptions } from "@/components/case-checking/ConfirmCaseRerunDialog";
import { PreviousReviewsModal } from "@/components/case-checking/PreviousReviewsModal";
import { GuidedReviewSaveMenu } from "@/components/case-checking/GuidedReviewSaveMenu";
import { ReviewPostRerunNotice } from "@/components/case-checking/ReviewPostRerunNotice";
import {
  parseCaseReviewOnRerun,
  summarizeReviewProgress,
  type ReviewProgressSummary,
} from "@/components/case-checking/guidedReviewProgress";
import { Alert } from "@/components/ui/alert";
import { useNotification } from "@/context/NotificationContext";
import { ReviewNarrativeStep } from "@/components/case-checking/ReviewNarrativeStep";
import { CompletenessAgentStatusBanner } from "@/components/case-checking/CompletenessAgentStatusBanner";
import {
  ReviewCompletenessVerdict,
  isChecklistItemMissing,
  type AgentCompletenessCall,
  type CompletenessReviewState,
} from "@/components/case-checking/ReviewCompletenessVerdict";
import { TimelineStoryPanel } from "@/components/case-checking/TimelineStoryPanel";
import {
  type CaseOpenIntent,
  CASE_OPEN_REVIEW,
  CASE_OPEN_SUMMARY,
} from "@/components/case-checking/caseReviewNavigation";
import { CaseReviewOverviewPanel } from "@/components/case-checking/CaseReviewOverviewPanel";
import { AvailableDocumentsModal } from "./AvailableDocumentsModal";
import {
  CaseDocumentIngestButtons,
  type CaseDocumentIngestProps,
} from "@/components/case-checking/CaseDocumentIngestButtons";
import { MissingDocumentCards } from "@/components/case-checking/MissingDocumentCards";
import { CaseCheckingListRow } from "@/components/case-checking/CaseCheckingListRow";
import {
  buildChecklistScrollTargetMap,
  CHECKLIST_SECTION_CONDITIONAL,
  CHECKLIST_SECTION_PRIORITY,
  CHECKLIST_SECTION_UNIVERSAL,
  isUploadMissingDocumentAction,
  resolveUploadActionDocument,
  scrollToChecklistItem,
  type ChecklistScrollCandidate,
} from "@/components/case-checking/missingDocumentUpload";
import { useDocumentAdditionQueue } from "@/components/case-checking/useDocumentAdditionQueue";
import { DocumentQueueBatch } from "@/components/case-checking/DocumentQueueBatch";
import { CaseDocumentQueueRow } from "@/components/case-checking/CaseDocumentQueueRow";
import {
  buildDocumentListSections,
  type ReviewDocumentRow,
} from "@/components/case-checking/documentListWithQueue";
import { useCaseProgress } from "@/components/case-checking/useCaseProgress";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import type { Artifact } from "@/services/api/agentic-v2";
import { getFileIconVisual } from "@/utils/files";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { missionControlTaskDetailPath } from "@/navigation/apps/missionControlLocation";
import { buildMissionDetailWindowUrl } from "@/navigation/apps/missionDetailLocation";
import {
  caseCheckAgentName,
  usesAgenticCaseCheck,
  supportsFileReviewExport,
  usesProgenyReviewShell,
} from "./agenticCaseCheck";
import { CaseDocumentsToolbar } from "@/components/case-checking/CaseDocumentsToolbar";
import { formatDocumentListSubtitle, formatDocumentListTitle, type DocumentSortMode } from "./documentListSort";
import { useCompanyId } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import { isAniStaff } from "@/utils/aniPlatformAccess";
import { RuleCitations } from "@/components/case-checking/RuleCitations";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { isCaseCheckingReviewOnly } from "@/utils/caseCheckingAccess";
import {
  caseCheckProgressIndicatorVariant,
  compactCaseProgressLabel,
  isCaseCheckMissionPaused,
  shouldShowAgenticProgress,
} from "@/components/case-checking/agenticCaseProgress";
import { shouldShowMissionAttention } from "@/components/case-checking/caseCheckMissionAttention";
import * as AgenticV2API from "@/services/api/agentic-v2";

// Toggle this to switch between mock data and real API
const USE_MOCK_DATA = false;

type AssessmentPillStatus = "sufficient" | "insufficient" | "needs-info" | "pending";

/** List overview used by mock cases and list rows when API shape is not loaded */
interface MockAssessmentOverview {
  groups: { group_id: string; group_name: string; status: AssessmentPillStatus }[];
}

const DEFAULT_PENDING_OVERVIEW: MockAssessmentOverview = {
  groups: [
    { group_id: "kyc", group_name: "Know Your Client", status: "pending" },
    { group_id: "suitability", group_name: "Suitability", status: "pending" },
    { group_id: "disclosure", group_name: "Supporting Documents / Disclosure", status: "pending" },
  ],
};

function mapAssessmentFromApi(a: ApiAssessment | null | undefined): MockAssessmentOverview {
  if (a?.groups?.length) {
    return {
      groups: a.groups.map((g) => ({
        group_id: g.group_id,
        group_name: g.group_name,
        status: g.status as AssessmentPillStatus,
      })),
    };
  }
  return DEFAULT_PENDING_OVERVIEW;
}

type View = "list" | "detail";
type Grade = CaseCheckingGrade;
type Stage = "ingestion" | "completeness" | "routing" | "assessment" | "grading" | "remedial" | "complete";

type CaseDetailTab =
  | "summary"
  | "documents"
  | "timeline"
  | "completeness"
  | "assessment"
  | "inconsistencies"
  | "remedial"
  | "artefacts"
  | "adjudication";

type CaseListViewMode = "list" | "board" | "insights";

export function CaseCheckingView() {
  const [location, setLocation] = useAppLocation(caseCheckingCodec);
  const caseId = location.caseId;
  const detailLocation = caseId
    ? { tab: location.tab, shell: location.shell, workflow: location.workflow }
    : ({} as CaseCheckingDetailQuery);
  const detailLocationRef = useRef(detailLocation);
  detailLocationRef.current = detailLocation;
  const setDetailLocation = useCallback(
    (patch: Partial<CaseCheckingDetailQuery>) => {
      if (!caseId) return;
      setLocation({
        focus: undefined,
        caseId,
        ...patch,
      });
    },
    [caseId, setLocation],
  );
  const navigate = useNavigate();
  const companyId = useCompanyId();
  const { permissions: modulePermissions } = useModulePermissions();
  const reviewOnly = isCaseCheckingReviewOnly(modulePermissions?.permissions);
  const agenticCaseCheck = usesAgenticCaseCheck(companyId);
  const showProgenyDataShell = usesProgenyReviewShell(companyId);
  const isOsMode = useIsOsMode();
  const windowId = useWindowId();
  const osNavigate = useOsNavigate();
  const closeWindow = useCloseWindow();
  /** Prefer window frame: useIsOsMode() can read false on rare frames (Fast Refresh / timing); WindowContext is stable inside OsWindow. */
  const isOsUiActive = isOsMode || Boolean(windowId);

  const handleListViewModeChange = useCallback(() => {
    const next = normalizeCaseChecking({ focus: location.focus });
    setLocation(next);
  }, [location.focus, setLocation]);

  const [view, setView] = useState<View>(() => (caseId ? "detail" : "list"));
  const [selectedCase, setSelectedCase] = useState<MockCase | null>(null);
  const [initialCaseLoading, setInitialCaseLoading] = useState(Boolean(caseId));
  const [initialCaseError, setInitialCaseError] = useState<string | null>(null);
  const [showNewCaseDialog, setShowNewCaseDialog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const queryClient = useQueryClient();
  const detailFromUrl = useCaseCheckDetail(caseId, {
    enabled: Boolean(caseId) && !USE_MOCK_DATA,
  });
  const [initialTab, setInitialTab] = useState<CaseDetailTab>("summary");
  const [initialShellMode, setInitialShellMode] = useState<CaseShellMode>("review");
  const [initialWorkflowStep, setInitialWorkflowStep] = useState<ReviewWorkflowStep | undefined>(
    undefined,
  );
  const [reviewProfileLabels, setReviewProfileLabels] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );

  useEffect(() => {
    if (USE_MOCK_DATA) return;
    let cancelled = false;
    listReviewProfiles()
      .then((profiles) => {
        if (cancelled) return;
        setReviewProfileLabels(new Map(profiles.map((p) => [p.slug, p.label])));
      })
      .catch(() => {
        /* fallback labels in caseReviewContext */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!caseId) return;
    if (detailLocation.tab) setInitialTab(detailLocation.tab);
    if (detailLocation.shell) setInitialShellMode(detailLocation.shell);
    setInitialWorkflowStep(detailLocation.workflow ?? undefined);
  }, [
    caseId,
    detailLocation.tab,
    detailLocation.shell,
    detailLocation.workflow,
  ]);

  useEffect(() => {
    if (!caseId) {
      setInitialCaseLoading(false);
      setInitialCaseError(null);
      return;
    }

    const fromUrl = detailLocationRef.current;
    const entryTab = fromUrl.tab ?? "summary";
    const entryShell = fromUrl.shell ?? (reviewOnly ? "review" : "summary");
    const entryWorkflow = fromUrl.workflow ?? "narrative";

    if (USE_MOCK_DATA) {
      const found = MOCK_CASES.find((m) => m.id === caseId);
      if (!found) {
        setInitialCaseError("Case not found");
        setInitialCaseLoading(false);
        return;
      }
      setSelectedCase(found);
      setInitialTab(entryTab);
      setInitialShellMode(entryShell);
      setInitialWorkflowStep(entryWorkflow);
      setView("detail");
      setInitialCaseLoading(false);
      setInitialCaseError(null);
      return;
    }

    if (detailFromUrl.isLoading) {
      setInitialCaseLoading(true);
      setInitialCaseError(null);
      return;
    }

    if (detailFromUrl.isError) {
      setInitialCaseError(
        detailFromUrl.error instanceof Error ? detailFromUrl.error.message : "Failed to load case",
      );
      setInitialCaseLoading(false);
      return;
    }

    if (!detailFromUrl.data) {
      setInitialCaseLoading(false);
      return;
    }

    setSelectedCase(mapApiCaseToMockCase(detailFromUrl.data));
    setInitialTab(entryTab);
    setInitialShellMode(entryShell);
    setInitialWorkflowStep(entryWorkflow);
    setView("detail");
    setInitialCaseLoading(false);
    setInitialCaseError(null);
  }, [caseId, detailFromUrl.data, detailFromUrl.error, detailFromUrl.isError, detailFromUrl.isLoading, reviewOnly]);

  const openCaseInOsWindow = (caseId: string, title?: string) => {
    const label = title?.trim() || `Case ${caseId.slice(-6)}`;
    osNavigate(`/case-checking/${encodeURIComponent(caseId)}`, label);
    setRefreshKey((k) => k + 1);
  };

  const handleCaseClick = (caseData: MockCase, intent?: CaseOpenIntent | CaseDetailTab) => {
    const open: CaseOpenIntent = reviewOnly
      ? CASE_OPEN_REVIEW
      : typeof intent === "string"
        ? { shellMode: "detail", detailTab: intent }
        : (intent ?? {});

    if (isOsUiActive && !caseId) {
      openCaseInOsWindow(caseData.id, caseData.client);
      return;
    }
    if (!caseId) {
      navigate(`/case-checking/${encodeURIComponent(caseData.id)}`);
      return;
    }
    setSelectedCase(caseData);
    setInitialTab((open.detailTab ?? "summary") as CaseDetailTab);
    setInitialShellMode(open.shellMode ?? "review");
    setInitialWorkflowStep(open.workflowStep ?? "narrative");
    setView("detail");
  };

  const handleBack = () => {
    if (caseId) {
      if (isOsUiActive) {
        closeWindow();
        return;
      }
      navigate("/case-checking");
      return;
    }
    setView("list");
    setSelectedCase(null);
  };

  const handleMissionLaunched = (missionId: string, taskId: string | null) => {
    setShowNewCaseDialog(false);
    const path = taskId
      ? missionControlTaskDetailPath(taskId)
      : buildMissionDetailWindowUrl(missionId);
    if (isOsUiActive) {
      const title = taskId ? "Mission Control" : "Mission";
      osNavigate(path, title);
      return;
    }
    navigate(path);
  };

  const handleLiveCaseUpdate = useCallback((patch: Partial<MockCase>) => {
    setSelectedCase((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const handleProgenyCaseLaunched = (newCaseId: string) => {
    setShowNewCaseDialog(false);
    if (isOsUiActive && newCaseId) {
      openCaseInOsWindow(newCaseId);
      return;
    }
    setInitialTab("documents");
    setSelectedCase({
      id: newCaseId,
      adviser: "",
      paraplanner: "",
      client: "",
      date: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
      grade: "unclear",
      hasReview: false,
      stage: "ingestion",
      preAdviceRequired: false,
      adviceType: "",
      caseType: "other",
      assessment: DEFAULT_PENDING_OVERVIEW,
      documents: [],
      checklist: [],
      remedialActions: [],
      metadata: null,
    });
    setView("detail");
    setRefreshKey((k) => k + 1);
  };

  const handleCaseCreated = async (newCaseId: string) => {
    // Close dialog first to avoid flash
    setShowNewCaseDialog(false);

    try {
      const docs = await getCaseDocuments(newCaseId);

      const newCase: MockCase = {
        id: newCaseId,
        adviser: "",
        paraplanner: "",
        client: "",
        date: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
        grade: "unclear",
        hasReview: false,
        stage: "ingestion",
        preAdviceRequired: false,
        adviceType: "",
        caseType: "other",
        assessment: DEFAULT_PENDING_OVERVIEW,
        documents: (docs || []).map((d) => ({
          name: d.filename,
          type: mapClassificationToType(d.classification),
          classification: d.classification || "Classifying...",
          valid: d.classification !== null,
        })),
        checklist: [],
        remedialActions: [],
        metadata: null,
      };

      if (isOsUiActive && newCaseId) {
        openCaseInOsWindow(newCaseId, newCase.client);
        return;
      }
      setInitialTab("documents");
      setSelectedCase(newCase);
      setView("detail");
    } catch (err) {
      console.error("handleCaseCreated error:", err);
    }
  };

  if (caseId && initialCaseLoading) {
    return (
      <div className="h-full flex flex-col p-4 gap-4">
        <div className={clsx("flex-1 h-full w-full rounded-2xl flex items-center justify-center", WORKBENCH_SURFACE_CLASS, WORKBENCH_ROOT_TEXT_CLASS)}>
          <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
            <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin" />
            <span>Loading case…</span>
          </div>
        </div>
      </div>
    );
  }

  if (caseId && initialCaseError) {
    return (
      <div className="h-full flex flex-col p-4 gap-4">
        <div className={clsx("flex-1 h-full w-full rounded-2xl flex flex-col items-center justify-center gap-4 p-6", WORKBENCH_SURFACE_CLASS, WORKBENCH_ROOT_TEXT_CLASS)}>
          <p className="text-red-600 dark:text-red-300 text-center">{initialCaseError}</p>
          {!isOsUiActive && (
            <Button color="slate" onClick={() => navigate("/case-checking")}>
              Back to cases
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-4 gap-4 ">
      <div className={clsx("flex-1 h-full min-h-0 w-full rounded-2xl overflow-hidden flex flex-col", WORKBENCH_SURFACE_CLASS, WORKBENCH_ROOT_TEXT_CLASS)}>
        {!caseId && view === "list" && (
          <CasesList
            onCaseClick={handleCaseClick}
            onNewCase={() => setShowNewCaseDialog(true)}
            refreshTrigger={refreshKey}
            reviewOnly={reviewOnly}
            onListViewModeChange={handleListViewModeChange}
          />
        )}
        {view === "detail" && selectedCase && (
          <CaseTenantProvider tenantId={selectedCase.companyId}>
            <CaseDetailDataProvider
              caseId={selectedCase.id}
              enabled={showProgenyDataShell && !USE_MOCK_DATA}
            >
              <CaseDetail
                caseData={selectedCase}
                onBack={handleBack}
                initialTab={initialTab}
                initialShellMode={reviewOnly ? "review" : initialShellMode}
                initialWorkflowStep={initialWorkflowStep}
                showBackButton={!isOsUiActive}
                reviewOnly={reviewOnly}
                profileLabelBySlug={reviewProfileLabels}
                onLiveCaseUpdate={handleLiveCaseUpdate}
                onDetailLocationChange={
                  caseId
                    ? (patch: Partial<CaseCheckingDetailQuery>) => setDetailLocation(patch)
                    : undefined
                }
              />
            </CaseDetailDataProvider>
          </CaseTenantProvider>
        )}
        {showNewCaseDialog && agenticCaseCheck && companyId === "progeny" ? (
          <ProgenyNewCaseDialog
            onClose={() => setShowNewCaseDialog(false)}
            onCaseLaunched={handleProgenyCaseLaunched}
            companyId={companyId}
          />
        ) : null}
        {showNewCaseDialog && agenticCaseCheck && companyId === "soderberg" ? (
          <SoderbergPlannrNewCaseDialog
            onClose={() => setShowNewCaseDialog(false)}
            onCaseLaunched={handleProgenyCaseLaunched}
            companyId={companyId}
          />
        ) : null}
        {showNewCaseDialog &&
        !(agenticCaseCheck && (companyId === "progeny" || companyId === "soderberg")) ? (
          <NewCaseDialog
            onClose={() => setShowNewCaseDialog(false)}
            onCreated={handleCaseCreated}
            agenticLaunch={agenticCaseCheck}
            companyId={companyId}
            onMissionLaunched={handleMissionLaunched}
          />
        ) : null}
      </div>
      {selectedCase && view === "list" && (
        <ResultBar caseData={selectedCase} profileLabelBySlug={reviewProfileLabels} />
      )}
    </div>
  );
}

/* ============================================
   KANBAN BOARD TYPES & CONFIG
   ============================================ */
type KanbanColumnId =
  | "needs_documents"
  | "ready_for_assessment"
  | "unclear"
  | "suitable"
  | "dev_needs"
  | "unsuitable";

interface KanbanColumn {
  id: KanbanColumnId;
  title: string;
  description: string;
}

const KANBAN_COLUMNS: KanbanColumn[] = [
  {
    id: "needs_documents",
    title: "Needs documents",
    description: "Upload or complete the document checklist",
  },
  {
    id: "ready_for_assessment",
    title: "Ready for assessment",
    description: "Documents on file — ready to assess",
  },
  {
    id: "unclear",
    title: "Unclear",
    description: "Need more info to demonstrate suitability",
  },
  {
    id: "suitable",
    title: "Suitable",
    description: "Passed review",
  },
  {
    id: "dev_needs",
    title: "Suitable with dev",
    description: "Passed with development points",
  },
  {
    id: "unsuitable",
    title: "Unsuitable",
    description: "Failed review",
  },
];

function gradeToKanbanColumn(grade: Grade): KanbanColumnId {
  switch (grade) {
    case "suitable":
      return "suitable";
    case "dev-needs":
      return "dev_needs";
    case "unsuitable":
    case "stop":
      return "unsuitable";
    case "unclear":
    default:
      return "unclear";
  }
}

/** Review complete (or terminal stage) — bucket by grade, not pipeline stage. */
function caseHasFinalOutcome(c: MockCase): boolean {
  return (
    c.hasReview ||
    (c.roleGrades?.length ?? 0) > 0 ||
    c.stage === "complete" ||
    c.stage === "grading" ||
    c.stage === "remedial"
  );
}

function getCaseColumnId(c: MockCase): KanbanColumnId {
  if (caseHasFinalOutcome(c)) {
    return gradeToKanbanColumn(c.grade);
  }

  if (c.stage === "ingestion" || c.stage === "completeness") return "needs_documents";
  if (c.stage === "routing" || c.stage === "assessment") return "ready_for_assessment";

  return "needs_documents";
}

/* ============================================
   KANBAN CARD
   ============================================ */
function formatCaseTypeLabel(caseType: string): string {
  return caseType.replace(/-/g, " ");
}

function countOpenHighPriorityActions(
  actions: Array<{ priority: string; status?: string }>,
): number {
  return actions.filter(
    (a) => a.priority === "high" && a.status !== "complete",
  ).length;
}

/** Cases list — client, context line, optional high-priority actions link, grade. */
function CaseListRow({
  caseData,
  onClick,
  onOpenActions,
  showReviewShortcuts = false,
  hideQuickActions = false,
  showTenantBadge = false,
  onViewSummary,
  onReviewCase,
  marvinStatus,
  fetchRunnerProgress = true,
}: {
  caseData: MockCase;
  onClick: () => void;
  onOpenActions: () => void;
  showReviewShortcuts?: boolean;
  hideQuickActions?: boolean;
  showTenantBadge?: boolean;
  onViewSummary?: () => void;
  onReviewCase?: () => void;
  marvinStatus?: CaseAgenticMissionLink | null;
  fetchRunnerProgress?: boolean;
}) {
  const typeLabel =
    caseData.caseType && caseData.caseType !== "other"
      ? formatCaseTypeLabel(caseData.caseType)
      : null;
  const context = [caseData.adviser || "No adviser", caseData.date, typeLabel]
    .filter(Boolean)
    .join(" · ");
  const highPriorityCount = countOpenHighPriorityActions(caseData.remedialActions);
  const showAgenticProgress = shouldShowAgenticProgress(marvinStatus, caseData);

  return (
    <div
      className={clsx(CC_ROW, "flex w-full items-center gap-4")}
      data-focus-ref={`case_check:${caseData.id}`}
    >
      <div className="min-w-0 flex-1">
        <Button
          plain size="unstyled"
          type="button"
          data-testid={`case-row-${caseData.id}`}
          onClick={onClick}
          className="w-full flex-col items-start px-0 py-0 text-left hover:!bg-transparent dark:hover:!bg-transparent"
        >
          <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
            {caseData.client || "Unnamed case"}
          </p>
          <p className="mt-0.5 truncate text-xs text-slate-400">{context}</p>
          {showTenantBadge && caseData.companyId ? (
            <CaseTypeTagBadge label={caseData.companyId} />
          ) : null}
          {caseData.hasReview ? (
            <HumanReviewStatusChip value={caseData.humanReview} className="mt-1.5" />
          ) : null}
        </Button>
        {showReviewShortcuts ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 pl-0.5">
            <Button
              plain
              type="button"
              data-testid={`case-row-${caseData.id}-view-summary`}
              onClick={() => onViewSummary?.()}
              className="px-0 text-xs font-medium text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              View summary
            </Button>
            <Button
              plain
              type="button"
              data-testid={`case-row-${caseData.id}-review-case`}
              onClick={() => onReviewCase?.()}
              className={clsx(CC_ACCENT_LINK, "px-0 text-xs")}
            >
              Review case
            </Button>
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {showAgenticProgress ? (
          <CaseListProgressBadge
            caseId={caseData.id}
            caseData={caseData}
            marvinStatus={marvinStatus}
            enabled={fetchRunnerProgress}
          />
        ) : null}
        {caseData.hasReview || !showAgenticProgress ? <CaseListGradeBadge grade={caseData.grade} /> : null}
        {highPriorityCount > 0 && !hideQuickActions ? (
          <Button
            plain size="unstyled"
            type="button"
            data-testid={`case-row-${caseData.id}-actions`}
            onClick={onOpenActions}
            className={clsx(CC_ACCENT_LINK, "px-0 text-xs whitespace-nowrap")}
          >
            {highPriorityCount} high priority
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function KanbanCard({ caseData, onClick }: { caseData: MockCase; onClick: () => void }) {
  return (
    <Button
      plain size="unstyled"
      type="button"
      onClick={onClick}
      className={clsx(CC_ROW, "w-full flex-col items-start px-4 py-3 text-left")}
    >
      <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
        {caseData.client || "Unnamed case"}
      </p>
      <div className="mt-1 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="block truncate text-xs text-slate-400">
            {caseData.adviser || "No adviser"}
          </span>
          {caseData.hasReview ? (
            <HumanReviewStatusChip value={caseData.humanReview} className="mt-1" />
          ) : null}
        </div>
        <CaseListGradeBadge grade={caseData.grade} />
      </div>
    </Button>
  );
}

/* ============================================
   KANBAN BOARD
   ============================================ */
function KanbanBoard({ cases, onCaseClick }: { cases: MockCase[]; onCaseClick: (c: MockCase) => void }) {
  const columnMap: Record<KanbanColumnId, MockCase[]> = {
    needs_documents: [],
    ready_for_assessment: [],
    unclear: [],
    suitable: [],
    dev_needs: [],
    unsuitable: [],
  };

  for (const c of cases) {
    const columnId = getCaseColumnId(c);
    columnMap[columnId].push(c);
  }

  return (
    <div className="flex gap-3 h-[calc(100vh-220px)] overflow-x-auto pb-4">
      {KANBAN_COLUMNS.map((col) => {
        const columnCases = columnMap[col.id];
        return (
          <div
            key={col.id}
            className="flex flex-col min-w-[260px] max-w-[300px] rounded-xl flex-shrink-0 bg-slate-50 dark:bg-white/[0.02] ring-1 ring-slate-200 dark:ring-white/5"
          >
            <div className="p-3 pb-2 border-b border-slate-200 dark:border-white/5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-300 truncate">
                  {col.title}
                </span>
                <span className="text-xs tabular-nums text-slate-500 shrink-0">
                  {columnCases.length}
                </span>
              </div>
            </div>
            <div className="flex-1 p-2 space-y-2 overflow-y-auto">
              {columnCases.map((caseData) => (
                <KanbanCard
                  key={caseData.id}
                  caseData={caseData}
                  onClick={() => onCaseClick(caseData)}
                />
              ))}
              {columnCases.length === 0 && (
                <p className="text-center text-xs text-slate-400 dark:text-slate-500 py-8">
                  No cases
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ============================================
   CASES LIST
   ============================================ */
const CASE_LIST_VIEW_OPTIONS: { id: CaseListViewMode; label: string }[] = [
  { id: "list", label: "Cases" },
  { id: "board", label: "Board" },
  { id: "insights", label: "Insights" },
];

const CASE_LIST_UI_PAGE_SIZE = 25;
/** Debounce trailing WS bursts before reloading the case list. */
const CASE_LIST_QUIET_REFRESH_DEBOUNCE_MS = 4_000;
/** Never reload the case list more often than this (WS progress can be chatty). */
const CASE_LIST_QUIET_REFRESH_MIN_INTERVAL_MS = 12_000;

function CasesList({
  onCaseClick,
  onNewCase,
  refreshTrigger,
  reviewOnly = false,
  onListViewModeChange,
}: {
  onCaseClick: (c: MockCase, intent?: CaseOpenIntent | CaseDetailTab) => void;
  onNewCase: () => void;
  refreshTrigger?: number;
  reviewOnly?: boolean;
  onListViewModeChange?: () => void;
}) {
  const companyId = useCompanyId();
  const { user } = useAuth();
  const showTenantBadge = isAniStaff(user);
  const agenticCaseCheck = usesAgenticCaseCheck(companyId);
  const progenyInsights = usesProgenyReviewShell(companyId);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<CaseListViewMode>("list");

  const handleListViewChange = useCallback(
    (next: CaseListViewMode) => {
      setViewMode(next);
      onListViewModeChange?.();
    },
    [onListViewModeChange],
  );

  const queryClient = useQueryClient();
  const [listPage, setListPage] = useState(0);
  const quietRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCasesRefreshAtRef = useRef(0);

  const searching = search.trim().length > 0;
  const listUsesServerPage = viewMode === "list" && !searching;
  const casesPageQuery = useCaseCheckCasesPage(
    { skip: listPage * CASE_LIST_UI_PAGE_SIZE, limit: CASE_LIST_UI_PAGE_SIZE },
    { enabled: !USE_MOCK_DATA && listUsesServerPage },
  );
  const allCasesQuery = useCaseCheckAllCases(undefined, {
    enabled: !USE_MOCK_DATA && (viewMode === "board" || viewMode === "insights" || searching),
  });

  const invalidateCaseQueries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: caseCheckKeys.all });
  }, [queryClient]);

  const refreshCasesQuietly = useCallback(() => {
    if (USE_MOCK_DATA) return;
    if (quietRefreshTimerRef.current) {
      clearTimeout(quietRefreshTimerRef.current);
    }

    const msSinceLast = Date.now() - lastCasesRefreshAtRef.current;
    const throttleWait = Math.max(0, CASE_LIST_QUIET_REFRESH_MIN_INTERVAL_MS - msSinceLast);
    const delay = Math.max(CASE_LIST_QUIET_REFRESH_DEBOUNCE_MS, throttleWait);

    quietRefreshTimerRef.current = setTimeout(() => {
      quietRefreshTimerRef.current = null;
      lastCasesRefreshAtRef.current = Date.now();
      invalidateCaseQueries();
    }, delay);
  }, [invalidateCaseQueries]);

  useCaseCheckingRealtime({
    enabled: !USE_MOCK_DATA,
    companyId,
    skipCompanyFilter: showTenantBadge,
    onCaseListChange: refreshCasesQuietly,
  });

  useEffect(() => {
    if (USE_MOCK_DATA || (refreshTrigger ?? 0) === 0) return;
    invalidateCaseQueries();
  }, [refreshTrigger, invalidateCaseQueries]);

  useEffect(
    () => () => {
      if (quietRefreshTimerRef.current) {
        clearTimeout(quietRefreshTimerRef.current);
      }
    },
    [],
  );

  const listServerCases = useMemo(
    () => (casesPageQuery.data?.items ?? []).map(mapApiCaseToMockCase),
    [casesPageQuery.data?.items],
  );
  const boardCases = useMemo(
    () => (allCasesQuery.data ?? []).map(mapApiCaseToMockCase),
    [allCasesQuery.data],
  );

  const casesToShow = USE_MOCK_DATA
    ? MOCK_CASES
    : listUsesServerPage
      ? listServerCases
      : viewMode === "board" || viewMode === "insights" || searching
        ? boardCases
        : [];

  const listTotal = casesPageQuery.data?.total ?? 0;
  const loading = USE_MOCK_DATA
    ? false
    : listUsesServerPage
      ? casesPageQuery.isLoading && !casesPageQuery.data
      : viewMode === "board" || viewMode === "insights" || searching
        ? allCasesQuery.isLoading && !allCasesQuery.data
        : false;
  const refreshing = USE_MOCK_DATA
    ? false
    : listUsesServerPage
      ? casesPageQuery.isFetching && !casesPageQuery.isLoading
      : allCasesQuery.isFetching && !allCasesQuery.isLoading;

  const primeSearchForId = useCallback((id: string) => {
    void getCase(id)
      .then((row) => {
        const client = row.client_name?.trim();
        setSearch(client || id);
      })
      .catch(() => {
        setSearch(id);
      });
  }, []);

  const { focusedId: focusCaseId } = useAgentListFocus({
    focusPrefix: "case_check",
    rows: casesToShow,
    getRowId: (row) => row.id,
    enabled: !loading && casesToShow.length > 0,
    primeSearchForId,
  });

  useEffect(() => {
    if (focusCaseId) setViewMode("list");
  }, [focusCaseId]);

  const filteredCases = casesToShow.filter((c) => {
    if (search) {
      const q = search.toLowerCase();
      const matches =
        c.adviser.toLowerCase().includes(q) ||
        c.client.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q);
      if (!matches) return false;
    }
    return true;
  });

  useEffect(() => {
    setListPage(0);
  }, [search, viewMode]);

  useEffect(() => {
    if (!focusCaseId || filteredCases.length === 0 || listUsesServerPage) return;
    const idx = filteredCases.findIndex((row) => row.id === focusCaseId);
    if (idx >= 0) {
      setListPage(Math.floor(idx / CASE_LIST_UI_PAGE_SIZE));
    }
  }, [focusCaseId, filteredCases, listUsesServerPage]);

  const listPageCount = listUsesServerPage
    ? Math.max(1, Math.ceil(listTotal / CASE_LIST_UI_PAGE_SIZE))
    : Math.max(1, Math.ceil(filteredCases.length / CASE_LIST_UI_PAGE_SIZE));
  const safeListPage = Math.min(listPage, listPageCount - 1);
  const pagedCases = listUsesServerPage
    ? filteredCases
    : filteredCases.slice(
        safeListPage * CASE_LIST_UI_PAGE_SIZE,
        (safeListPage + 1) * CASE_LIST_UI_PAGE_SIZE,
      );

  const missionCaseIds = useMemo(() => {
    if (!agenticCaseCheck || USE_MOCK_DATA) return [] as string[];
    if (listUsesServerPage) return pagedCases.map((row) => row.id);
    if (viewMode === "board") return filteredCases.slice(0, 100).map((row) => row.id);
    return [] as string[];
  }, [agenticCaseCheck, filteredCases, listUsesServerPage, pagedCases, viewMode]);

  const missionStatusQuery = useCaseCheckMissionStatuses(missionCaseIds, {
    enabled: agenticCaseCheck && missionCaseIds.length > 0,
  });
  const missionByCaseId = missionStatusQuery.data ?? {};

  const listRangeStart = listUsesServerPage
    ? listTotal === 0
      ? 0
      : safeListPage * CASE_LIST_UI_PAGE_SIZE + 1
    : filteredCases.length === 0
      ? 0
      : safeListPage * CASE_LIST_UI_PAGE_SIZE + 1;
  const listRangeEnd = listUsesServerPage
    ? Math.min(listTotal, (safeListPage + 1) * CASE_LIST_UI_PAGE_SIZE)
    : Math.min(filteredCases.length, (safeListPage + 1) * CASE_LIST_UI_PAGE_SIZE);
  const paginationTotal = listUsesServerPage ? listTotal : filteredCases.length;

  const listRefreshing = loading || refreshing;

  return (
    <div className={clsx(CC_INNER, "relative flex flex-col gap-0 h-full")}>
      <div className={CC_TOOLBAR}>
        <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-3 overflow-hidden">
          {!reviewOnly ? (
            <SegmentedPillToggle
              options={CASE_LIST_VIEW_OPTIONS}
              value={viewMode}
              onChange={handleListViewChange}
              aria-label="Case list view"
              data-testid="case-list-view-toggle"
              optionTestIdPrefix="case-list-view"
            />
          ) : null}
          {viewMode === "insights" ? (
            <p className="flex-1 min-w-0 text-sm text-slate-400">
              {progenyInsights
                ? "Cross-case coverage review and problem-statement trends"
                : "Cross-case trends and rule failures"}
            </p>
          ) : (
            <div className="flex-1 min-w-0">
              <FormInput
                type="search"
                id="case-list-search"
                aria-label="Search cases"
                placeholder="Search by adviser, client or ID (current page)..."
                value={search}
                icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                iconPosition="start"
                className={CC_SEARCH}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
              />
            </div>
          )}
          <Button
            plain
            type="button"
            onClick={() => invalidateCaseQueries()}
            disabled={listRefreshing}
            aria-label="Refresh cases"
            data-testid="case-list-refresh"
            className={clsx(CC_GHOST_BTN, "shrink-0 px-2.5 py-2")}
          >
            <FontAwesomeIcon
              icon={faRotate}
              className={clsx("w-4 h-4", listRefreshing && "animate-spin")}
            />
          </Button>
          {!reviewOnly ? (
            <Button onClick={onNewCase} color="indigo" className="shrink-0">
              <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
              New Case
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <p className="text-sm text-slate-400 py-8 text-center">Loading cases…</p>
        ) : viewMode === "insights" ? (
          progenyInsights ? (
            <ProgenyPortfolioInsightsView
              cases={casesToShow.map((c) => ({
                id: c.id,
                adviser: c.adviser,
                caseType: c.caseType,
              }))}
            />
          ) : (
            <PortfolioInsightsView
              cases={casesToShow.map((c) => ({
                id: c.id,
                adviser: c.adviser,
                caseType: c.caseType,
              }))}
            />
          )
        ) : viewMode === "list" ? (
          <>
            <ul className="flex flex-col gap-1.5" data-testid="case-list">
              {pagedCases.map((c) => (
                <li key={c.id}>
                  <CaseListRow
                    caseData={c}
                    onClick={() => onCaseClick(c, reviewOnly ? CASE_OPEN_REVIEW : undefined)}
                    onOpenActions={() => onCaseClick(c, "remedial")}
                    showTenantBadge={showTenantBadge}
                    showReviewShortcuts={progenyInsights && !reviewOnly}
                    hideQuickActions={reviewOnly}
                    onViewSummary={() => onCaseClick(c, CASE_OPEN_SUMMARY)}
                    onReviewCase={() => onCaseClick(c, CASE_OPEN_REVIEW)}
                    marvinStatus={missionByCaseId[c.id]}
                    fetchRunnerProgress={!USE_MOCK_DATA}
                  />
                </li>
              ))}
              {filteredCases.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-16">No cases match your search.</p>
              )}
            </ul>
            {paginationTotal > CASE_LIST_UI_PAGE_SIZE || listPageCount > 1 ? (
              <div
                className="flex items-center justify-between gap-3 border-t border-slate-200 dark:border-white/10 px-1 py-3 text-sm text-slate-400"
                data-testid="case-list-pagination"
              >
                <span>
                  Showing {listRangeStart}–{listRangeEnd} of {paginationTotal}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    color="slate"
                    disabled={safeListPage <= 0}
                    onClick={() => setListPage((p) => Math.max(0, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="tabular-nums text-slate-600 dark:text-slate-300">
                    Page {safeListPage + 1} of {listPageCount}
                  </span>
                  <Button
                    type="button"
                    color="slate"
                    disabled={safeListPage >= listPageCount - 1}
                    onClick={() => setListPage((p) => Math.min(listPageCount - 1, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <KanbanBoard cases={filteredCases} onCaseClick={onCaseClick} />
        )}
      </div>
    </div>
  );
}

/* ============================================
   CASE DETAIL VIEW
   ============================================ */

function CaseDetail({
  caseData,
  onBack,
  initialTab = "summary",
  initialShellMode = "review",
  initialWorkflowStep,
  showBackButton = true,
  reviewOnly = false,
  profileLabelBySlug,
  onLiveCaseUpdate,
  onDetailLocationChange,
}: {
  caseData: MockCase;
  onBack: () => void;
  initialTab?: CaseDetailTab;
  initialShellMode?: CaseShellMode;
  /** When set (from URL), applied after the guided review session loads. */
  initialWorkflowStep?: ReviewWorkflowStep;
  showBackButton?: boolean;
  reviewOnly?: boolean;
  profileLabelBySlug?: ReadonlyMap<string, string>;
  onLiveCaseUpdate?: (patch: Partial<MockCase>) => void;
  onDetailLocationChange?: (patch: Partial<CaseCheckingDetailQuery>) => void;
}) {
  const companyId = useCaseCheckingTenantId();
  const viewerCompanyId = useCompanyId();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const osNavigate = useOsNavigate();
  const isOsMode = useIsOsMode();
  const windowId = useWindowId();
  const isOsUiActive = isOsMode || Boolean(windowId);
  const showReviewShell = usesProgenyReviewShell(companyId);
  const agenticCaseCheck = usesAgenticCaseCheck(companyId);
  const [shellMode, setShellMode] = useState<CaseShellMode>(
    reviewOnly ? "review" : initialShellMode,
  );
  const [workflowPreviewDoc, setWorkflowPreviewDoc] = useState<MockDocument | null>(null);

  const inWorkflowMode = showReviewShell && shellMode === "review";
  const inSummaryMode = showReviewShell && shellMode === "summary";
  const shared = useCaseDetailData();
  const { showWarning, showError, showSuccess } = useNotification();
  const guidedReview = useGuidedReviewSession(caseData.id, inWorkflowMode || inSummaryMode, inWorkflowMode);
  const reloadGuidedReviewSession = guidedReview.reloadSession;
  const reviewProgress = useMemo(
    () => summarizeReviewProgress(guidedReview.session, shared?.problemStatementRun ?? null),
    [guidedReview.session, shared?.problemStatementRun],
  );
  const reviewWorkflowStep = guidedReview.reviewWorkflowStep;
  const completedWorkflowSteps = guidedReview.completedWorkflowSteps;
  const checklistDisputes = guidedReview.checklistDisputes;
  const completenessReview = guidedReview.completenessReview;
  const handleWorkflowStepChange = guidedReview.handleWorkflowStepChange;
  const handleCompletenessReviewChange = guidedReview.handleCompletenessReviewChange;
  const [completenessDecisionDirty, setCompletenessDecisionDirty] = useState(false);
  const [assessmentDecisionDirty, setAssessmentDecisionDirty] = useState(true);
  const completenessDecisionSaved =
    completenessReview?.status === "agree" || completenessReview?.status === "disagree";

  useEffect(() => {
    setShellMode(reviewOnly ? "review" : initialShellMode);
  }, [caseData.id, initialShellMode, reviewOnly]);

  useEffect(() => {
    setWorkflowPreviewDoc(null);
    setCompletenessDecisionDirty(false);
    setAssessmentDecisionDirty(true);
  }, [caseData.id]);

  const handleCompletenessSave = useCallback(
    (next: CompletenessReviewState) => {
      handleCompletenessReviewChange(next);
      setCompletenessDecisionDirty(false);
    },
    [handleCompletenessReviewChange],
  );
  const [liveCase, setLiveCase] = useState<MockCase>(() => ({ ...caseData }));

  useEffect(() => {
    setLiveCase({ ...caseData });
  }, [caseData]);

  const displayClientTitle =
    formatClientsHeading({
      clientName: liveCase.client,
      advisedParties: liveCase.advisedParties,
      adviceScope: liveCase.adviceScope,
    }) || `Case #${liveCase.id.slice(-6)}`;

  const entityTitle = displayClientTitle;
  const clientSubline = formatClientsSubline({
    clientName: liveCase.client,
    adviceScope: liveCase.adviceScope,
    advisedParties: liveCase.advisedParties,
  });
  useWindowTitle(!showBackButton ? entityTitle : undefined);
  const [activeTab, setActiveTab] = useState<CaseDetailTab>(initialTab);
  const changeActiveTab = useCallback(
    (id: CaseDetailTab) => {
      setActiveTab(id);
      onDetailLocationChange?.({ tab: id });
    },
    [onDetailLocationChange],
  );
  const changeShellMode = useCallback(
    (mode: CaseShellMode) => {
      setShellMode(mode);
      onDetailLocationChange?.({ shell: mode });
    },
    [onDetailLocationChange],
  );
  const changeWorkflowStep = useCallback(
    (step: ReviewWorkflowStep) => {
      handleWorkflowStepChange(step);
      onDetailLocationChange?.({ workflow: step });
    },
    [handleWorkflowStepChange, onDetailLocationChange],
  );

  const guidedReviewSessionIdRef = useRef(guidedReview.session?.id);
  useEffect(() => {
    const nextId = guidedReview.session?.id;
    const prevId = guidedReviewSessionIdRef.current;
    guidedReviewSessionIdRef.current = nextId;
    if (prevId && nextId && prevId !== nextId) {
      changeWorkflowStep("narrative");
      setCompletenessDecisionDirty(false);
      setAssessmentDecisionDirty(true);
    }
  }, [guidedReview.session?.id, changeWorkflowStep]);

  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null);
  const [documents, setDocuments] = useState<MockDocument[]>(caseData.documents);
  const [apiDocuments, setApiDocuments] = useState<CaseDocument[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [detectedCaseType, setDetectedCaseType] = useState<ApiCaseType | null>(caseData.caseType as ApiCaseType || null);
  const [currentAssessment, setCurrentAssessment] = useState<ApiAssessment | null>(null);
  const [currentCompleteness, setCurrentCompleteness] = useState<ApiCompleteness | null>(null);
  const agentCompletenessCall = useMemo((): AgentCompletenessCall => {
    if (!currentCompleteness || currentCompleteness.status === "pending") return "pending";
    const missingCount = currentCompleteness.missing_documents?.length ?? 0;
    if (missingCount > 0 || currentCompleteness.status !== "complete") return "incomplete";
    return "complete";
  }, [currentCompleteness]);
  const completenessCheckedForReview = useMemo(
    () => currentCompleteness != null && currentCompleteness.status !== "pending",
    [currentCompleteness],
  );

  const completenessAdvanceBlocked = useMemo(
    () =>
      reviewWorkflowStep === "checklist" &&
      (!completenessCheckedForReview ||
        !completenessDecisionSaved ||
        completenessDecisionDirty),
    [
      reviewWorkflowStep,
      completenessCheckedForReview,
      completenessDecisionSaved,
      completenessDecisionDirty,
    ],
  );

  const assessmentAdvanceBlocked = useMemo(
    () => reviewWorkflowStep === "assessment" && assessmentDecisionDirty,
    [assessmentDecisionDirty, reviewWorkflowStep],
  );

  const reviewWorkflowAdvanceLabel = useMemo(() => {
    if (reviewWorkflowStep === "rules") return "Happy with rules";
    if (reviewWorkflowStep === "assessment" && assessmentDecisionDirty) return "Save assessment to continue";
    if (reviewWorkflowStep !== "checklist") return undefined;
    if (!completenessCheckedForReview) return "Waiting for check";
    if (!completenessDecisionSaved || completenessDecisionDirty) {
      return completenessDecisionDirty && completenessDecisionSaved ? "Save decision" : "Record decision";
    }
    if (agentCompletenessCall === "incomplete" || completenessReview?.status === "disagree") {
      return "Continue anyway";
    }
    return "Next step";
  }, [
    reviewWorkflowStep,
    completenessReview?.status,
    agentCompletenessCall,
    completenessDecisionSaved,
    completenessDecisionDirty,
    completenessCheckedForReview,
    assessmentDecisionDirty,
  ]);
  const [currentReview, setCurrentReview] = useState<ApiCaseReview | null>(null);
  const [remedialActions, setRemedialActions] = useState<ApiRemedialAction[]>([]);
  const [inconsistencies, setInconsistencies] = useState<CaseInconsistency[]>([]);
  const [timeline, setTimeline] = useState<CaseTimeline | null>(null);
  const [displayAdviser, setDisplayAdviser] = useState<string>(caseData.adviser || "");
  const [ruleSet, setRuleSet] = useState<RuleSetVersion | null>(null);
  const [ruleSetLoading, setRuleSetLoading] = useState(true);
  const [ruleSetError, setRuleSetError] = useState<string | null>(null);
  const [crmMetadata, setCrmMetadata] = useState<CaseMetadata | null>(() => caseData.metadata ?? null);
  const [caseArtifacts, setCaseArtifacts] = useState<Artifact[]>([]);

  const onLiveCaseUpdateRef = useRef(onLiveCaseUpdate);
  onLiveCaseUpdateRef.current = onLiveCaseUpdate;

  const applyApiCaseContext = useCallback((c: ApiCase) => {
    const patch = contextPatchFromApi(c);
    setLiveCase((prev) => ({ ...prev, ...patch }));
    onLiveCaseUpdateRef.current?.(patch);
    return patch;
  }, []);

  useEffect(() => {
    setCrmMetadata(caseData.metadata ?? null);
  }, [caseData.id, caseData.metadata]);

  useEffect(() => {
    if (USE_MOCK_DATA) return;
    let cancelled = false;
    void listCaseArtifacts(caseData.id)
      .then((rows) => {
        if (!cancelled) setCaseArtifacts(rows);
      })
      .catch(() => {
        if (!cancelled) setCaseArtifacts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [caseData.id]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const handleWorkflowStepChangeRef = useRef(handleWorkflowStepChange);
  handleWorkflowStepChangeRef.current = handleWorkflowStepChange;
  const appliedInitialWorkflowKeyRef = useRef<string | null>(null);

  useEffect(() => {
    appliedInitialWorkflowKeyRef.current = null;
  }, [caseData.id]);

  useEffect(() => {
    if (!initialWorkflowStep || guidedReview.loading) return;
    const key = `${caseData.id}:${initialWorkflowStep}`;
    if (appliedInitialWorkflowKeyRef.current === key) return;
    appliedInitialWorkflowKeyRef.current = key;
    handleWorkflowStepChangeRef.current(initialWorkflowStep);
  }, [caseData.id, initialWorkflowStep, guidedReview.loading]);

  const workflowSyncedToUrlRef = useRef(false);
  useEffect(() => {
    workflowSyncedToUrlRef.current = false;
  }, [caseData.id]);
  useEffect(() => {
    if (initialWorkflowStep || guidedReview.loading || !guidedReview.session?.current_step) return;
    if (workflowSyncedToUrlRef.current) return;
    workflowSyncedToUrlRef.current = true;
    const step = guidedReview.reviewWorkflowStep;
    onDetailLocationChange?.({ workflow: step });
  }, [
    guidedReview.loading,
    guidedReview.reviewWorkflowStep,
    guidedReview.session?.current_step,
    initialWorkflowStep,
    onDetailLocationChange,
  ]);

  useEffect(() => {
    let cancelled = false;
    setRuleSetLoading(true);
    setRuleSetError(null);
    getActiveRuleSetVersion()
      .then((v) => { if (!cancelled) setRuleSet(v); })
      .catch((err) => {
        if (!cancelled) setRuleSetError(err instanceof Error ? err.message : "Failed to load rule set");
      })
      .finally(() => { if (!cancelled) setRuleSetLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Map API document to UI document format
  const mapApiDoc = useCallback((d: {
    id?: string;
    filename: string;
    file_type?: string;
    classification: string | null;
    classification_confidence?: number | null;
    classification_reasoning?: string | null;
    classification_error?: string | null;
    summary?: string | null;
    red_flags?: RedFlag[] | null;
    key_dates?: Record<string, string> | null;
    key_parties?: Record<string, string> | null;
    key_figures?: Record<string, CaseDocumentKeyFigureValue> | null;
    valid?: boolean;
    created_at?: string;
    metadata?: {
      content_readable?: boolean;
      unreadable_reason?: string | null;
      classification_source?: string;
    } | null;
  }): MockDocument => ({
    id: d.id,
    name: d.filename,
    fileType: d.file_type || "",
    type: mapClassificationToType(d.classification),
    classification: documentClassificationLabel(d),
    classificationError: d.classification_error ?? null,
    valid: d.valid !== false,
    summary: d.summary,
    confidence: d.classification_confidence,
    reasoning: d.classification_reasoning,
    redFlags: d.red_flags,
    keyDates: d.key_dates,
    keyParties: d.key_parties,
    keyFigures: d.key_figures,
    createdAt: d.created_at ?? null,
    metadata: d.metadata ?? null,
  }), []);

  const documentsFetchRef = useRef<Promise<void> | null>(null);
  const caseFetchRef = useRef<Promise<void> | null>(null);

  // Fetch documents (can be called manually to refresh)
  const fetchDocuments = useCallback(async () => {
    if (USE_MOCK_DATA) return;
    if (documentsFetchRef.current) return documentsFetchRef.current;

    const promise = (async () => {
      setIsLoadingDocs(true);
      try {
        const docs = await getCaseDocuments(caseData.id);
        const rows = docs || [];
        setApiDocuments(rows);
        setDocuments(rows.map(mapApiDoc));
      } catch (err) {
        console.error("Failed to fetch documents:", err);
      } finally {
        setIsLoadingDocs(false);
      }
    })();

    documentsFetchRef.current = promise.finally(() => {
      documentsFetchRef.current = null;
    });
    return documentsFetchRef.current;
  }, [caseData.id, mapApiDoc]);

  const applyApiCaseFields = useCallback(
    (c: ApiCase) => {
      setCrmMetadata(c.metadata ?? null);
      applyApiCaseContext(c);
      if (c.case_type && c.case_type !== "unknown") {
        setDetectedCaseType(c.case_type);
      }
      if (c.assessment) {
        setCurrentAssessment(c.assessment);
      }
      if (c.completeness) {
        setCurrentCompleteness(c.completeness);
      }
      if (c.review) {
        setCurrentReview(c.review);
      }
      if (c.remedial_actions) {
        setRemedialActions(c.remedial_actions);
      }
      if (c.inconsistencies) {
        setInconsistencies(c.inconsistencies);
      }
      if (c.timeline) {
        setTimeline(c.timeline);
      }
      if (c.adviser) {
        setDisplayAdviser(c.adviser);
      }
    },
    [applyApiCaseContext],
  );

  const refreshCaseFromApi = useCallback(async () => {
    if (USE_MOCK_DATA) return;
    if (caseFetchRef.current) return caseFetchRef.current;

    const promise = (async () => {
      try {
        const c = await queryClient.fetchQuery({
          queryKey: caseCheckKeys.detail(caseData.id),
          queryFn: () => getCase(caseData.id),
        });
        applyApiCaseFields(c);
      } catch (err) {
        console.error("Failed to refresh case:", err);
      }
    })();

    caseFetchRef.current = promise.finally(() => {
      caseFetchRef.current = null;
    });
    return caseFetchRef.current;
  }, [applyApiCaseFields, caseData.id, queryClient]);

  const detailQuery = useCaseCheckDetail(caseData.id, { enabled: !USE_MOCK_DATA });

  const resetCaseDetailScopedState = useCallback(() => {
    setCurrentReview(null);
    setCurrentAssessment(null);
    setCurrentCompleteness(null);
    setRemedialActions([]);
    setInconsistencies([]);
    setTimeline(null);
  }, []);

  useEffect(() => {
    if (USE_MOCK_DATA) return;
    const c = detailQuery.data;
    if (!c || c.id !== caseData.id) {
      resetCaseDetailScopedState();
      return;
    }
    applyApiCaseFields(c);
  }, [USE_MOCK_DATA, applyApiCaseFields, caseData.id, detailQuery.data, resetCaseDetailScopedState]);

  const refreshDocumentsFromAgenticMission = useCallback(() => {
    void fetchDocuments();
    void refreshCaseFromApi();
  }, [fetchDocuments, refreshCaseFromApi]);

  const [ingestTaskHint, setIngestTaskHint] = useState<string | null>(null);

  const caseProgress = useCaseProgress({
    caseId: caseData.id,
    companyId,
    caseSnapshot: { hasReview: liveCase.hasReview },
    onRefresh: refreshDocumentsFromAgenticMission,
    enabled: agenticCaseCheck && !USE_MOCK_DATA,
    initialTaskId: ingestTaskHint,
  });

  const documentAdditionQueue = useDocumentAdditionQueue({
    caseId: caseData.id,
    onTaskIdChange: setIngestTaskHint,
    onSubmitComplete: () => {
      void refreshCaseFromApi();
      void fetchDocuments();
    },
  });

  const progressTaskId = ingestTaskHint ?? caseProgress.taskId;
  const headerProgressLabel = caseProgress.hasResolvedActiveTask ? caseProgress.label : null;
  const headerRunnerInFlight = Boolean(
    progressTaskId &&
      (caseProgress.taskStream.isInProgress || caseProgress.taskStream.isLoadingInitial),
  );
  const headerProgressVariant = caseCheckProgressIndicatorVariant(
    caseProgress.missionLink,
    headerRunnerInFlight,
  );
  const isAgenticProcessing = caseProgress.isProcessing;
  const headerProgressDisplayLabel =
    headerProgressLabel &&
    (headerProgressVariant === "blocked" || isCaseCheckMissionPaused(caseProgress.missionLink))
      ? compactCaseProgressLabel(headerProgressLabel, caseProgress.missionLink)
      : headerProgressLabel;
  const showMissionAttentionBanner =
    Boolean(caseProgress.missionLink) &&
    (isAgenticProcessing ||
      shouldShowMissionAttention(caseProgress.missionLink) ||
      Boolean(caseProgress.missionLink?.chain_failed));
  const [missionProgressOpen, setMissionProgressOpen] = useState(false);
  const handleProgressClick = useCallback(() => {
    setMissionProgressOpen(true);
  }, []);

  const onProgressClick =
    caseProgress.missionLink?.is_active || progressTaskId || caseProgress.missionLink?.needs_attention
      ? handleProgressClick
      : undefined;

  const [recoverRerunOpen, setRecoverRerunOpen] = useState(false);
  const [recoverRerunBusy, setRecoverRerunBusy] = useState(false);
  const [retryClassifyRerunOpen, setRetryClassifyRerunOpen] = useState(false);
  const [retryClassifyDocIds, setRetryClassifyDocIds] = useState<string[] | undefined>(undefined);
  const [retryClassifyBusy, setRetryClassifyBusy] = useState(false);
  const [previousReviewsOpen, setPreviousReviewsOpen] = useState(false);
  const [carryRetryBusy, setCarryRetryBusy] = useState(false);
  const [startOverConfirmOpen, setStartOverConfirmOpen] = useState(false);

  const reviewOnRerunMeta = useMemo(() => parseCaseReviewOnRerun(crmMetadata), [crmMetadata]);
  const sourceReviewRunId = guidedReview.session?.id ?? null;

  const openPreviousReviews = useCallback(() => setPreviousReviewsOpen(true), []);

  const runRecoverCaseCheck = useCallback(
    async (options: CaseRerunConfirmOptions = {}) => {
      setRecoverRerunBusy(true);
      try {
        await recoverCaseCheck(caseData.id, options);
        await caseProgress.refreshMissionLink();
        await refreshCaseFromApi();
      } catch {
        showError("Could not recover file check", "Try again or check Case Checking for details.");
      } finally {
        setRecoverRerunBusy(false);
        setRecoverRerunOpen(false);
      }
    },
    [caseData.id, caseProgress, refreshCaseFromApi, showError],
  );

  const handleRecoverCaseCheck = useCallback(() => {
    setRecoverRerunOpen(true);
  }, []);

  const runRetryClassification = useCallback(
    async (documentIds?: string[], options: CaseRerunConfirmOptions = {}) => {
      setRetryClassifyBusy(true);
      try {
        await triggerCaseCheckIngest(caseData.id, {
          source: "classify_only",
          ...(documentIds && documentIds.length > 0 ? { document_ids: documentIds } : {}),
          ...options,
        });
        await caseProgress.refreshMissionLink();
        await refreshCaseFromApi();
        await fetchDocuments();
      } catch (err) {
        const message =
          err instanceof Error && /409|already processing|already running/i.test(err.message)
            ? "This case is already processing documents or running a file-check step."
            : "Try again or check Case Checking for details.";
        showError("Could not retry classification", message);
      } finally {
        setRetryClassifyBusy(false);
        setRetryClassifyRerunOpen(false);
        setRetryClassifyDocIds(undefined);
      }
    },
    [caseData.id, caseProgress, fetchDocuments, refreshCaseFromApi, showError],
  );

  const handleRetryClassification = useCallback(
    (documentIds?: string[]) => {
      if (reviewProgress.hasProgress) {
        setRetryClassifyDocIds(documentIds);
        setRetryClassifyRerunOpen(true);
        return;
      }
      void runRetryClassification(documentIds);
    },
    [reviewProgress.hasProgress, runRetryClassification],
  );

  const wasAgenticProcessingRef = useRef(isAgenticProcessing);
  useEffect(() => {
    const wasProcessing = wasAgenticProcessingRef.current;
    wasAgenticProcessingRef.current = isAgenticProcessing;
    if (!wasProcessing || isAgenticProcessing || !(inWorkflowMode || inSummaryMode)) return;
    void reloadGuidedReviewSession();
    void refreshCaseFromApi();
  }, [
    refreshCaseFromApi,
    reloadGuidedReviewSession,
    inSummaryMode,
    inWorkflowMode,
    isAgenticProcessing,
  ]);

  const handleRetryCarryRestore = useCallback(async () => {
    setCarryRetryBusy(true);
    try {
      const result = await applyPendingCarryRestore(caseData.id);
      await refreshCaseFromApi();
      await reloadGuidedReviewSession();
      if (result.applied) {
        showSuccess("Review restored", "Your saved guided review was applied to this case check.");
      } else if (result.skipped && result.reason) {
        showWarning("Could not carry review", result.reason);
      }
    } catch {
      showError("Could not restore review", "Try again or pick a session under Previous reviews.");
    } finally {
      setCarryRetryBusy(false);
    }
  }, [caseData.id, refreshCaseFromApi, reloadGuidedReviewSession, showError, showSuccess, showWarning]);

  const openMissionPath = useCallback(
    (path: string, label: string) => {
      if (isOsUiActive) {
        osNavigate(path, label);
        return;
      }
      navigate(path);
    },
    [isOsUiActive, navigate, osNavigate],
  );

  const handleOpenMission = useCallback(() => {
    const link = caseProgress.missionLink;
    const path = link?.mission_href ?? (link?.mission_id ? `/missions/${link.mission_id}` : null);
    if (!path) return;
    openMissionPath(path, "Mission");
  }, [caseProgress.missionLink, openMissionPath]);

  const [crmModalState, setCrmModalState] = useState<{
    open: boolean;
    scope: "suggested" | "all";
  }>({ open: false, scope: "all" });
  const [crmIngestError, setCrmIngestError] = useState<string | null>(null);

  const canBrowseCrmDocuments =
    Boolean(caseData.id) &&
    crmMetadata?.source === "progeny" &&
    Boolean(crmMetadata.account_id);

  const handleUploadFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      if (caseProgress.isProcessing) {
        setCrmIngestError("This case is already processing documents or running a file-check step.");
        return;
      }
      setCrmIngestError(null);
      documentAdditionQueue.clearError();
      void documentAdditionQueue.uploadFiles(list);
    },
    [caseProgress.isProcessing, documentAdditionQueue],
  );

  const handleBrowseCrm = useCallback(() => {
    setCrmIngestError(null);
    documentAdditionQueue.clearError();
    setCrmModalState({ open: true, scope: "all" });
  }, [documentAdditionQueue]);

  const handleCrmDocumentsAdded = useCallback(
    (progenyDocumentIds: string[], labels?: Record<string, string>) => {
      if (progenyDocumentIds.length === 0) return;

      if (caseProgress.isProcessing) {
        setCrmIngestError("This case is already processing documents or running a file-check step.");
        return;
      }

      setCrmIngestError(null);
      documentAdditionQueue.clearError();
      void documentAdditionQueue.addProgenyDocumentIds(progenyDocumentIds, labels);
      setCrmModalState((prev) => ({ ...prev, open: false }));
    },
    [caseProgress.isProcessing, documentAdditionQueue],
  );

  const documentQueueProps = useMemo(
    () => ({
      queue: documentAdditionQueue.queue,
      caseId: caseData.id,
      sourceReviewRunId,
      isBusy: documentAdditionQueue.isBusy,
      processingBlocked: caseProgress.isProcessing,
      reviewProgress,
      onRemoveItem: (itemId: string) => void documentAdditionQueue.removeItem(itemId),
      onSubmitNow: (options: CaseRerunConfirmOptions) =>
        void documentAdditionQueue.submitNow({ showTaskProgress: false, ...options }),
      onScheduleTonight: (options: CaseRerunConfirmOptions) => void documentAdditionQueue.scheduleTonight(options),
      onCancelSchedule: () => void documentAdditionQueue.cancelSchedule(),
    }),
    [
      caseData.id,
      caseProgress.isProcessing,
      documentAdditionQueue,
      reviewProgress,
      sourceReviewRunId,
    ],
  );

  const clearedPriorReview = guidedReview.session?.cleared_prior_review;
  const showPostRerunNotice =
    (inWorkflowMode || inSummaryMode) &&
    (Boolean(clearedPriorReview && !clearedPriorReview.acknowledged_at) ||
      reviewOnRerunMeta?.mode === "carry" ||
      (isAgenticProcessing && reviewOnRerunMeta?.mode === "fresh"));

  const documentIngestProps = useMemo(
    () => ({
      onUploadFiles: handleUploadFiles,
      onBrowseCrm: canBrowseCrmDocuments ? handleBrowseCrm : undefined,
      onCrmDocumentsAdded: canBrowseCrmDocuments ? handleCrmDocumentsAdded : undefined,
      isProcessing: documentAdditionQueue.isBusy,
      showIngestProgress: false,
      stepLabel: "",
      progressPercent: null,
      processedCount: null,
      totalCount: null,
      ingestError: documentAdditionQueue.error ?? crmIngestError,
      canBrowseCrm: canBrowseCrmDocuments,
      isAgenticProcessing,
    }),
    [
      canBrowseCrmDocuments,
      crmIngestError,
      documentAdditionQueue.error,
      documentAdditionQueue.isBusy,
      handleBrowseCrm,
      handleCrmDocumentsAdded,
      handleUploadFiles,
      isAgenticProcessing,
    ],
  );

  const [scrollToChecklistType, setScrollToChecklistType] = useState<string | null>(null);

  const navigateToMissingDocument = useCallback(
    (documentType: string) => {
      if (inSummaryMode) setShellMode("detail");
      if (inWorkflowMode) handleWorkflowStepChange("checklist");
      setActiveTab("completeness");
      setScrollToChecklistType(documentType);
    },
    [inSummaryMode, inWorkflowMode, handleWorkflowStepChange],
  );

  const clearScrollToChecklistType = useCallback(() => {
    setScrollToChecklistType(null);
  }, []);

  const missingDocuments = currentCompleteness?.missing_documents ?? [];

  const remedialTabProps = {
    documentIngest: documentIngestProps,
    missingDocuments,
    onScrollToChecklistItem: navigateToMissingDocument,
  };

  useCaseCheckingRealtime({
    enabled: !USE_MOCK_DATA,
    companyId: viewerCompanyId,
    skipCompanyFilter: isAniStaff(user),
    caseId: caseData.id,
    onCaseChange: () => {
      void refreshCaseFromApi();
    },
    onDocumentsChange: () => {
      void fetchDocuments();
    },
  });

  const handleDocumentsChanged = useCallback(async () => {
    await fetchDocuments();
  }, [fetchDocuments]);

  const fetchDocumentsRef = useRef(fetchDocuments);
  fetchDocumentsRef.current = fetchDocuments;

  useEffect(() => {
    if (USE_MOCK_DATA) return;
    void fetchDocumentsRef.current();
  }, [caseData.id]);

  const handleDeleteCase = async () => {
    setIsDeleting(true);
    try {
      await deleteCase(caseData.id);
      onBack();
    } catch (err) {
      console.error("Failed to delete case:", err);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Use API remedial actions if available, otherwise fall back to mock data
  const displayRemedialActions: ApiRemedialAction[] =
    remedialActions.length > 0 ? remedialActions : (caseData.remedialActions as ApiRemedialAction[]);

  const effectiveRemedialActions = useMemo(() => {
    const edits = guidedReview.session?.remedial_edits;
    if (!edits?.length) return displayRemedialActions;
    return mergeRemedialActions(displayRemedialActions, edits).filter(isOpenEffectiveAction);
  }, [displayRemedialActions, guidedReview.session?.remedial_edits]);

  const insights = useMemo(
    () =>
      computeCaseDetailInsights({
        documentCount: documents.length,
        completeness: currentCompleteness,
        assessment: currentAssessment,
        ruleSet,
        caseType: detectedCaseType || caseData.caseType || null,
        caseTypes: caseData.caseTypes ?? null,
        transactionSubtype: caseData.transactionSubtype ?? null,
        inconsistencies,
        remedialActions: effectiveRemedialActions,
      }),
    [
      documents.length,
      currentCompleteness,
      currentAssessment,
      ruleSet,
      detectedCaseType,
      caseData.caseType,
      caseData.caseTypes,
      caseData.transactionSubtype,
      inconsistencies,
      effectiveRemedialActions,
    ],
  );

  const insightNavItems = useMemo(() => buildCaseInsightNavItems(insights), [insights]);
  const reviewerNotesArtifact = useMemo(
    () => pickReviewerNotesArtifact(caseArtifacts),
    [caseArtifacts],
  );

  const caseDetailTabs = useMemo((): SegmentedTab[] => {
    const tabs: SegmentedTab[] = [
      { id: "summary", label: "Overview" },
      {
        id: "documents",
        label: "Documents",
        count: insights.documentCount,
        showCountAlways: true,
      },
      { id: "timeline", label: "Timeline" },
    ];

    if (insights.missingDocuments !== null) {
      tabs.push({
        id: "completeness",
        label: "Checklist",
        count: insights.missingDocuments,
        countLabel:
          insights.missingDocuments > 0 ? `${insights.missingDocuments} missing` : undefined,
        countVariant: insights.missingDocuments > 0 ? "attention" : "neutral",
      });
    } else {
      tabs.push({ id: "completeness", label: "Checklist" });
    }

    tabs.push({
      id: "inconsistencies",
      label: "Inconsistencies",
      count: insights.failedInconsistencies + insights.unclearInconsistencies,
      countLabel:
        insights.failedInconsistencies > 0
          ? `${insights.failedInconsistencies} failed`
          : insights.unclearInconsistencies > 0
            ? `${insights.unclearInconsistencies} unclear`
            : undefined,
      countVariant:
        insights.failedInconsistencies > 0
          ? "attention"
          : insights.unclearInconsistencies > 0
            ? "warning"
            : "neutral",
    });

    const rulesBadge = rulesTabBadge(insights.failedRules, insights.unclearRules);
    tabs.push({
      id: "assessment",
      label: "Rules",
      ...rulesBadge,
    });

    tabs.push({
      id: "remedial",
      label: "Actions",
      count: insights.openRemedialActions,
      countLabel:
        insights.severeRemedialActions > 0
          ? `${insights.severeRemedialActions} high`
          : insights.openRemedialActions > 0
            ? `${insights.openRemedialActions} open`
            : undefined,
      countVariant: insights.severeRemedialActions > 0 ? "attention" : "neutral",
    });

    if (showReviewShell) {
      tabs.push({ id: "adjudication", label: "Adjudication" });
    }
    tabs.push({ id: "artefacts", label: "Exports" });
    return tabs;
  }, [insights, showReviewShell]);

  const inDetailMode = !showReviewShell || shellMode === "detail";

  const reviewDocuments = useMemo(
    () =>
      documents.map((doc) => ({
        id: doc.id ?? "",
        name: doc.name,
        classification: doc.classification,
        fileType: doc.fileType ?? "",
        createdAt: doc.createdAt ?? null,
        keyDates: doc.keyDates ?? null,
      })),
    [documents],
  );

  const scopeNote = getAdviceScopeReasoning(liveCase);
  const displayGrade = caseReviewRollupGrade(currentReview, liveCase.grade);
  const reviewerLine = displayAdviser ? displayAdviser : null;

  /** Match ResultBar: approve when case grade is suitable (no remedial path). */
  const showApproveCaseAction = displayGrade === "suitable";

  return (
    <div className="h-full flex flex-col relative min-h-0">
      <div className={clsx(CC_INNER, "space-y-4 pb-24")}>
        {(showBackButton || showApproveCaseAction) && !showReviewShell ? (
          <div className="flex items-center gap-3">
            {showBackButton ? (
              <>
                <Button
                  plain size="unstyled"
                  type="button"
                  onClick={onBack}
                  className="p-2 rounded-lg text-slate-400 transition-colors hover:bg-slate-100 dark:hover:bg-white/[0.06] hover:text-slate-700 dark:hover:text-slate-200"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" />
                </Button>
                <div className="flex-1 min-w-0">
                  <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {displayClientTitle}
                  </h1>
                  {clientSubline ? (
                    <p className="mt-0.5 text-sm text-slate-400">{clientSubline}</p>
                  ) : null}
                </div>
              </>
            ) : (
              <span className="flex-1" />
            )}
            {showApproveCaseAction ? (
              <Button
                type="button"
                color="emerald"
                className="shrink-0"
                icon={<FontAwesomeIcon icon={faCircleCheck} className="w-3.5 h-3.5" />}
              >
                Approve Case
              </Button>
            ) : null}
          </div>
        ) : null}

        {showBackButton && showReviewShell ? (
          <Button
            plain size="unstyled"
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 px-0 text-sm text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-slate-200"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" />
            Back
          </Button>
        ) : null}

        {showReviewShell ? (
          <CaseReviewHeader
            clientTitle={displayClientTitle}
            hideClientTitle={!showBackButton}
            scopeNote={scopeNote}
            grade={displayGrade}
            humanReview={caseData.humanReview}
            reviewerLine={reviewerLine}
            shellMode={shellMode}
            onShellModeChange={changeShellMode}
            showShellModeToggle={!reviewOnly}
            showGrade={liveCase.hasReview || !isAgenticProcessing}
            progressLabel={headerProgressDisplayLabel}
            progressTitle={
              headerProgressDisplayLabel &&
              headerProgressLabel &&
              headerProgressDisplayLabel !== headerProgressLabel
                ? headerProgressLabel
                : undefined
            }
            progressVariant={headerProgressVariant}
            onProgressClick={onProgressClick}
            trailingActions={
              reviewOnly ? null : (
                <DestructiveTrashIconButton
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={isDeleting}
                  title="Delete case"
                  aria-label="Delete case"
                />
              )
            }
          />
        ) : (
          <CaseContextBar
            clientName={liveCase.client}
            reviewProfile={liveCase.reviewProfile}
            adviceScope={liveCase.adviceScope}
            advisedParties={liveCase.advisedParties}
            adviceScopeReasoning={liveCase.adviceScopeReasoning}
            adviceType={liveCase.adviceType}
            preAdviceRequired={liveCase.preAdviceRequired}
            profileLabelBySlug={profileLabelBySlug}
            caseTypeLabel={formatSummaryCaseType(
              detectedCaseType ?? liveCase.caseType,
              liveCase.caseTypes,
            )}
            variant="headline"
            onAdjudication={
              activeTab === "adjudication" ? undefined : () => changeActiveTab("adjudication")
            }
            trailingActions={
              <DestructiveTrashIconButton
                onClick={() => setShowDeleteDialog(true)}
                disabled={isDeleting}
                title="Delete case"
                aria-label="Delete case"
              />
            }
          />
        )}

        {inSummaryMode || inWorkflowMode ? (
          inWorkflowMode ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-xs font-medium uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                Guided review
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <GuidedReviewSaveMenu
                  currentTitle={guidedReview.session?.title}
                  disabled={!guidedReview.session || guidedReview.restarting}
                  onRename={(title) => guidedReview.renameReviewTitle(title)}
                  onSaveCopy={async (title) => {
                    const ok = await guidedReview.saveReviewCopy(title);
                    if (ok) showSuccess("Review copy saved", "You can restore it from Previous reviews.");
                    return ok;
                  }}
                />
                <Button
                  outline
                  type="button"
                  onClick={openPreviousReviews}
                  data-testid="guided-review-previous-reviews"
                >
                  Previous reviews
                </Button>
                <Button
                  outline
                  disabled={guidedReview.restarting || !guidedReview.session}
                  icon={<FontAwesomeIcon icon={faRotate} className="h-3.5 w-3.5" />}
                  onClick={() => setStartOverConfirmOpen(true)}
                  data-testid="guided-review-start-over"
                >
                  {guidedReview.restarting ? "Starting over…" : "Start over"}
                </Button>
              </div>
            </div>
          ) : null
        ) : (
          <div className="overflow-x-auto">
            <SegmentedTabs
              className="min-w-0"
              showDivider
              ariaLabel="Case sections"
              panelIdPrefix="case-tab-panel"
              tabTestIdPrefix="case-tab"
              activeId={activeTab}
              onChange={(id) => changeActiveTab(id as CaseDetailTab)}
              tabs={caseDetailTabs}
            />
          </div>
        )}

        <div className="mt-3 min-h-0">
          {showPostRerunNotice ? (
            <div className="mb-4">
              <ReviewPostRerunNotice
                cleared={clearedPriorReview}
                reviewOnRerun={reviewOnRerunMeta}
                isReCheckInProgress={isAgenticProcessing}
                carryRetryBusy={carryRetryBusy}
                acknowledging={guidedReview.acknowledgingCleared}
                onOpenPreviousReviews={openPreviousReviews}
                onRetryCarry={() => void handleRetryCarryRestore()}
                onAcknowledge={() =>
                  void guidedReview.acknowledgeClearedReview().then((ok) => {
                    if (!ok) showError("Could not dismiss notice", "Try again.");
                  })
                }
              />
            </div>
          ) : null}
          {inSummaryMode ? (
            <CaseSummaryView
              caseId={caseData.id}
              companyId={companyId}
              caseReview={currentReview}
              grade={caseReviewRollupGrade(currentReview, liveCase.grade)}
              displayAdviser={displayAdviser}
              fallbackAdviser={caseData.adviser}
              fallbackGrade={displayGrade}
              openRemedialCount={insights.openRemedialActions}
              severeRemedialCount={insights.severeRemedialActions}
              sectionLabel="Outcome view"
              isAgenticProcessing={isAgenticProcessing}
              showMissionAttentionBanner={showMissionAttentionBanner}
              agenticProgressLabel={headerProgressLabel}
              missionLink={caseProgress.missionLink}
              onRetryChain={() => void handleRecoverCaseCheck()}
              onRetryTask={() => void handleRecoverCaseCheck()}
              onAddDocuments={() => changeActiveTab("documents")}
              onOpenMission={caseProgress.missionLink?.mission_id ? handleOpenMission : undefined}
              remedialsContent={
                <RemedialTab
                  actions={effectiveRemedialActions}
                  assessment={currentAssessment}
                  inconsistencies={inconsistencies}
                  {...remedialTabProps}
                />
              }
            />
          ) : null}
          {inWorkflowMode ? (
            guidedReview.loading ? (
              <p className="text-sm text-slate-500">Loading review progress…</p>
            ) : (
            <CaseReviewWorkflow
              step={reviewWorkflowStep}
              onStepChange={changeWorkflowStep}
              completedSteps={completedWorkflowSteps}
              canAdvance={!completenessAdvanceBlocked && !assessmentAdvanceBlocked}
              advanceLabel={reviewWorkflowAdvanceLabel}
              caseCheckInProgress={isAgenticProcessing}
              caseId={caseData.id}
              documents={reviewDocuments}
              documentsLoading={isLoadingDocs}
              narrativeStep={
                <ReviewNarrativeStep
                  grade={caseReviewRollupGrade(currentReview, liveCase.grade)}
                  caseReview={currentReview}
                  timeline={timeline}
                  displayAdviser={displayAdviser}
                  fallbackAdviser={caseData.adviser}
                />
              }
              documentsStep={
                <DocumentsTab
                  documents={documents}
                  isLoading={isLoadingDocs}
                  caseId={caseData.id}
                  crmMetadata={crmMetadata}
                  onDocumentsChanged={handleDocumentsChanged}
                  reviewWorkflowMode
                  documentIngest={documentIngestProps}
                  documentQueue={documentQueueProps}
                  onRetryClassification={handleRetryClassification}
                  isRetryingClassification={retryClassifyBusy}
                />
              }
              checklistStep={
                <CompletenessTab
                  caseType={detectedCaseType || caseData.caseType || "unknown"}
                  caseTypes={liveCase.caseTypes}
                  transactionSubtype={liveCase.transactionSubtype}
                  documents={documents}
                  apiCompleteness={currentCompleteness}
                  ruleSet={ruleSet}
                  ruleSetLoading={ruleSetLoading}
                  ruleSetError={ruleSetError}
                  reviewWorkflowMode
                  checklistDisputes={checklistDisputes}
                  onChecklistDisputeChange={guidedReview.handleChecklistDisputeChange}
                  completenessReview={completenessReview}
                  onCompletenessReviewChange={handleCompletenessSave}
                  onCompletenessDecisionDirtyChange={setCompletenessDecisionDirty}
                  agentCompletenessCall={agentCompletenessCall}
                  onViewDocument={(doc) => setWorkflowPreviewDoc(doc)}
                  documentIngest={documentIngestProps}
                  scrollToItemType={scrollToChecklistType}
                  onScrolledToItem={clearScrollToChecklistType}
                />
              }
              rulesStep={
                <ReviewProblemStatementRulesStep
                  key={guidedReview.session?.id ?? "no-session"}
                  caseId={caseData.id}
                  reviewSessionId={guidedReview.session?.id}
                  reviewRun={guidedReview.session}
                  onReviewRunChange={guidedReview.handleSessionReplace}
                  caseDocuments={apiDocuments}
                  caseDocumentsLoading={isLoadingDocs}
                />
              }
              assessmentStep={
                <ReviewOverallAssessmentStep
                  caseId={caseData.id}
                  companyId={companyId}
                  caseReview={currentReview}
                  fallbackGrade={displayGrade}
                  onAssessmentComplete={() => guidedReview.markStepComplete("assessment")}
                  onAssessmentDirtyChange={setAssessmentDecisionDirty}
                />
              }
              actionsStep={
                guidedReview.session ? (
                  <ReviewActionsStep
                    caseId={caseData.id}
                    reviewRunId={guidedReview.session.id}
                    agentActions={displayRemedialActions}
                    remedialEdits={guidedReview.session.remedial_edits ?? []}
                    assessment={currentAssessment}
                    inconsistencies={inconsistencies}
                    onEditsChange={guidedReview.handleSessionReplace}
                  />
                ) : (
                  <RemedialTab
                    actions={displayRemedialActions}
                    assessment={currentAssessment}
                    inconsistencies={inconsistencies}
                    {...remedialTabProps}
                  />
                )
              }
              summaryStep={
                <ReviewCaseSummaryStep
                  caseId={caseData.id}
                  companyId={companyId}
                  clientName={liveCase.client}
                  caseReview={currentReview}
                  fallbackGrade={displayGrade}
                  displayAdviser={displayAdviser}
                  psRunId={guidedReview.psRunId ?? undefined}
                  reviewRunId={guidedReview.session?.id}
                  onGoToSummary={() => changeShellMode("summary")}
                />
              }
            />
            )
          ) : null}
          {inDetailMode ? (
            <div
              className={CC_TAB_PANEL_PT}
              role="tabpanel"
              id={`case-tab-panel-${activeTab}`}
              aria-labelledby={`case-tab-${activeTab}`}
            >
          {activeTab === "summary" && (
            <SummaryTab
              caseReview={currentReview}
              caseData={caseData}
              displayAdviser={displayAdviser}
              insightNavItems={insightNavItems}
              reviewerNotesArtifact={reviewerNotesArtifact}
              onNavigateTab={changeActiveTab}
            />
          )}
          {activeTab === "documents" && (
            <DocumentsTab
              documents={documents}
              isLoading={isLoadingDocs}
              caseId={caseData.id}
              crmMetadata={crmMetadata}
              onDocumentsChanged={handleDocumentsChanged}
              initialDocumentId={openDocumentId}
              onInitialDocumentConsumed={() => setOpenDocumentId(null)}
              documentIngest={documentIngestProps}
              documentQueue={documentQueueProps}
              onRetryClassification={handleRetryClassification}
              isRetryingClassification={retryClassifyBusy}
            />
          )}
          {activeTab === "completeness" && (
            <CompletenessTab
              caseType={detectedCaseType || caseData.caseType || "unknown"}
              caseTypes={liveCase.caseTypes}
              transactionSubtype={liveCase.transactionSubtype}
              documents={documents}
              apiCompleteness={currentCompleteness}
              ruleSet={ruleSet}
              ruleSetLoading={ruleSetLoading}
              ruleSetError={ruleSetError}
              onViewDocument={(doc) => {
                if (doc.id) {
                  setOpenDocumentId(doc.id);
                  changeActiveTab("documents");
                }
              }}
              documentIngest={documentIngestProps}
              scrollToItemType={scrollToChecklistType}
              onScrolledToItem={clearScrollToChecklistType}
            />
          )}
          {activeTab === "assessment" && (
            <AssessmentTab
              documents={documents}
              apiAssessment={currentAssessment}
              ruleSet={ruleSet}
              ruleSetLoading={ruleSetLoading}
              ruleSetError={ruleSetError}
              caseType={detectedCaseType || caseData.caseType || null}
            />
          )}
          {activeTab === "inconsistencies" && <InconsistenciesTab inconsistencies={inconsistencies} />}
          {activeTab === "timeline" && (
            <TimelineTab
              timeline={timeline}
              chronologyChecks={applicableChronologyChecks(
                ruleSet,
                detectedCaseType || caseData.caseType || null,
                liveCase.caseTypes,
              )}
              inconsistencies={inconsistencies}
            />
          )}
          {activeTab === "remedial" && (
            <RemedialTab
              actions={displayRemedialActions}
              assessment={currentAssessment}
              inconsistencies={inconsistencies}
              {...remedialTabProps}
            />
          )}
          {activeTab === "adjudication" && (
            <AdjudicationTab
              caseId={caseData.id}
              humanReviewRouting={currentReview?.human_review ?? null}
              hasReview={Boolean(currentReview)}
            />
          )}
          {activeTab === "artefacts" && (
            <ArtefactsTab
              caseId={caseData.id}
              fallbackTitle={caseData.client || `Case #${caseData.id.slice(-6)}`}
              hasAssessment={Boolean(currentAssessment?.groups?.length)}
              hasCompleteness={Boolean(currentCompleteness)}
              remedialCount={displayRemedialActions.length}
            />
          )}
            </div>
          ) : null}
        </div>
      </div>

      {crmModalState.open && caseData.id ? (
        <AvailableDocumentsModal
          open={crmModalState.open}
          onClose={() => {
            setCrmModalState((state) => ({ ...state, open: false }));
            setCrmIngestError(null);
          }}
          caseId={caseData.id}
          scope={crmModalState.scope}
          onDocumentsAdded={handleCrmDocumentsAdded}
          ingestBlocked={caseProgress.isProcessing}
          ingestBlockedMessage={crmIngestError}
        />
      ) : null}

      <ConfirmDeleteDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteCase}
        title="Are you sure you want to delete?"
        description={`You are about to delete "${caseData.client || `Case #${caseData.id.slice(-6)}`}". This action cannot be undone and will permanently remove the case.`}
        confirmLabel="Yes, Delete"
        isLoading={isDeleting}
      />

      <CaseCheckMissionProgressDialog
        open={missionProgressOpen}
        onClose={() => setMissionProgressOpen(false)}
        title={headerProgressLabel ?? "Case check progress"}
        missionLink={caseProgress.missionLink}
        taskId={progressTaskId}
        taskStream={caseProgress.taskStream}
      />

      <ConfirmCaseRerunDialog
        open={recoverRerunOpen}
        onClose={() => setRecoverRerunOpen(false)}
        onConfirm={(options) => void runRecoverCaseCheck(options)}
        action="recover"
        progress={reviewProgress}
        caseId={caseData.id}
        sourceReviewRunId={sourceReviewRunId}
        isLoading={recoverRerunBusy}
      />

      <ConfirmCaseRerunDialog
        open={retryClassifyRerunOpen}
        onClose={() => {
          setRetryClassifyRerunOpen(false);
          setRetryClassifyDocIds(undefined);
        }}
        onConfirm={(options) => void runRetryClassification(retryClassifyDocIds, options)}
        action="retry_classification"
        progress={reviewProgress}
        caseId={caseData.id}
        sourceReviewRunId={sourceReviewRunId}
        isLoading={retryClassifyBusy}
      />

      <ConfirmDeleteDialog
        open={startOverConfirmOpen}
        onClose={() => setStartOverConfirmOpen(false)}
        onConfirm={() => {
          setStartOverConfirmOpen(false);
          void guidedReview.restartReview().then((ok) => {
            if (ok) changeWorkflowStep("narrative");
          });
        }}
        title="Start a fresh review?"
        description="Your current review is kept in Previous reviews. You will work through the guided review from the beginning."
        confirmLabel="Start over"
        cancelLabel="Cancel"
        isDestructive={false}
        confirmButtonColor="indigo"
        isLoading={guidedReview.restarting}
      />

      <PreviousReviewsModal
        open={previousReviewsOpen}
        onClose={() => setPreviousReviewsOpen(false)}
        caseId={caseData.id}
        currentReviewRunId={guidedReview.session?.id}
        onRestored={() => {
          void reloadGuidedReviewSession();
          void refreshCaseFromApi();
        }}
      />

      {workflowPreviewDoc ? (
        <DocumentDetailDrawer
          caseId={caseData.id}
          document={workflowPreviewDoc}
          onClose={() => setWorkflowPreviewDoc(null)}
          onDocumentsChanged={() => {
            void handleDocumentsChanged();
            void refreshCaseFromApi();
          }}
          onRetryClassification={handleRetryClassification}
          isRetryingClassification={retryClassifyBusy}
          retryDisabled={Boolean(
            caseProgress.isProcessing ||
              isAgenticProcessing ||
              documentAdditionQueue.isBusy,
          )}
        />
      ) : null}
    </div>
  );
}

function formatSummaryCaseType(
  type: string | null | undefined,
  caseTypes?: string[] | null,
): string | null {
  if (!type || type === "unknown") return null;
  const labelFor = (slug: string) => {
    const known = CASE_TYPE_LABELS[slug as CaseType];
    if (known) return known;
    return slug.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  };
  const primary = labelFor(type);
  const concrete = (caseTypes ?? []).filter(
    (t) => t && t !== "unknown" && t !== "multi-product" && t !== type,
  );
  if (type === "multi-product" && concrete.length > 0) {
    return `${primary} (${concrete.map(labelFor).join(", ")})`;
  }
  if (concrete.length > 0) {
    return `${primary} (+ ${concrete.map(labelFor).join(", ")})`;
  }
  return primary;
}

/* ============================================
   OVERVIEW TAB — verdict + file-status navigator
   ============================================ */

function SummaryTab({
  caseData,
  caseReview,
  displayAdviser,
  insightNavItems,
  reviewerNotesArtifact,
  onNavigateTab,
}: {
  caseData: MockCase;
  caseReview: ApiCaseReview | null;
  displayAdviser?: string;
  insightNavItems: CaseInsightNavItem[];
  reviewerNotesArtifact: Artifact | null;
  onNavigateTab: (tab: CaseDetailTab) => void;
}) {
  const gradeRec = caseReviewRollupGrade(caseReview, caseData.grade);

  return (
    <div className="space-y-4">
      <CaseReviewOverviewPanel
        grade={gradeRec}
        caseReview={caseReview}
        displayAdviser={displayAdviser}
        fallbackAdviser={caseData.adviser}
      />

      <div className={CC_PANEL_FLUSH}>
        <p className={clsx(CC_SECTION_LABEL, "px-4 pt-3 pb-2 mb-0")}>File status</p>
        <ul className="divide-y divide-slate-200 dark:divide-white/5">
          {insightNavItems.map((item) => (
            <li key={item.tab}>
              <CaseInsightNavRow item={item} onNavigate={() => onNavigateTab(item.tab)} />
            </li>
          ))}
        </ul>
      </div>

      {reviewerNotesArtifact ? (
        <div className={CC_PANEL}>
          <p className={clsx(CC_SECTION_LABEL, "mb-3")}>Reviewer notes</p>
          <ArtifactCard artifact={reviewerNotesArtifact} variant="compact" />
        </div>
      ) : null}
    </div>
  );
}

function CaseInsightNavRow({
  item,
  onNavigate,
}: {
  item: CaseInsightNavItem;
  onNavigate: () => void;
}) {
  return (
    <CaseCheckingListRow
      variant="flush"
      onClick={onNavigate}
      ariaLabel={`Open ${item.label}`}
      testId={`case-insight-${item.tab}`}
      innerClassName="items-center"
    >
      <span className="flex-1 min-w-0 text-sm text-slate-700 dark:text-slate-200">{item.label}</span>
      <span
        className={clsx(
          "text-sm tabular-nums shrink-0",
          item.attention && item.attentionTone === "warning"
            ? clsx(CC_STAT_ATTENTION, "font-medium")
            : item.attention
              ? clsx(CC_STAT_NEGATIVE, "font-medium")
              : "text-slate-400",
        )}
      >
        {item.detail}
      </span>
      <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3 text-slate-600 shrink-0" />
    </CaseCheckingListRow>
  );
}

/* ============================================
   RESULT BAR - STICKY FOOTER (Simplified)
   ============================================ */
function ResultBar({
  caseData,
  profileLabelBySlug,
}: {
  caseData: MockCase;
  profileLabelBySlug?: ReadonlyMap<string, string>;
}) {
  const hasIssues = caseData.grade !== "suitable";
  const contextLines = buildCaseContextSummary(
    {
      clientName: caseData.client,
      reviewProfile: caseData.reviewProfile,
      adviceScope: caseData.adviceScope,
      advisedParties: caseData.advisedParties,
      adviceScopeReasoning: caseData.adviceScopeReasoning,
      adviceType: caseData.adviceType,
      preAdviceRequired: caseData.preAdviceRequired,
    },
    profileLabelBySlug,
  );
  const contextText = contextLines
    .filter((r) => r.term !== "Scope note")
    .map((r) => `${r.term}: ${r.detail}`)
    .join(" · ");

  if (!contextText && !hasIssues) {
    return null;
  }

  return (
    <div className={clsx(CC_PANEL, "py-2.5")}>
      <div
        className={clsx(
          "flex items-center gap-4",
          contextText && hasIssues ? "justify-between" : hasIssues ? "justify-end" : "justify-start",
        )}
      >
        {contextText ? (
          <p
            className="text-sm text-slate-500 dark:text-slate-400 min-w-0"
            data-testid="case-result-context"
          >
            {contextText}
          </p>
        ) : null}
      </div>
    </div>
  );
}


/* ============================================
   DOCUMENTS TAB
   ============================================ */

export function DocumentsTab({
  documents,
  isLoading,
  caseId,
  crmMetadata,
  onDocumentsChanged,
  initialDocumentId,
  onInitialDocumentConsumed,
  reviewWorkflowMode = false,
  documentIngest,
  documentQueue,
  onRetryClassification,
  isRetryingClassification = false,
}: {
  documents: MockDocument[];
  isLoading?: boolean;
  caseId?: string;
  crmMetadata?: CaseMetadata | null;
  onDocumentsChanged?: () => void;
  initialDocumentId?: string | null;
  onInitialDocumentConsumed?: () => void;
  reviewWorkflowMode?: boolean;
  documentIngest?: CaseDocumentIngestProps;
  documentQueue?: {
    queue: DocumentAdditionQueue | null;
    caseId?: string;
    sourceReviewRunId?: string | null;
    isBusy: boolean;
    processingBlocked: boolean;
    reviewProgress?: ReviewProgressSummary | null;
    onRemoveItem: (itemId: string) => void;
    onSubmitNow: (options: CaseRerunConfirmOptions) => void;
    onScheduleTonight: (options: CaseRerunConfirmOptions) => void;
    onCancelSchedule: () => void;
  };
  onRetryClassification?: (documentIds?: string[]) => void;
  isRetryingClassification?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState<DocumentSortMode>("added");
  const [classificationFilter, setClassificationFilter] = useState<DocumentClassificationFilterId>(
    DOCUMENT_CLASSIFICATION_FILTER_ALL,
  );
  const [selectedDoc, setSelectedDoc] = useState<MockDocument | null>(null);
  const classificationFailures = useMemo(
    () => collectClassificationFailures(documents),
    [documents],
  );
  const classificationFilterOptions = useMemo(
    () => buildDocumentClassificationFilterOptions(documents),
    [documents],
  );

  useEffect(() => {
    if (classificationFilter === DOCUMENT_CLASSIFICATION_FILTER_ALL) return;
    const stillValid = classificationFilterOptions.some((o) => o.id === classificationFilter);
    if (!stillValid) setClassificationFilter(DOCUMENT_CLASSIFICATION_FILTER_ALL);
  }, [classificationFilter, classificationFilterOptions]);

  useEffect(() => {
    if (!initialDocumentId) return;
    const doc = documents.find((d) => d.id === initialDocumentId);
    if (doc) setSelectedDoc(doc);
    onInitialDocumentConsumed?.();
  }, [initialDocumentId, documents, onInitialDocumentConsumed]);
  const [showAvailableDocsModal, setShowAvailableDocsModal] = useState(false);

  const canBrowseCrmDocuments =
    Boolean(caseId) &&
    crmMetadata?.source === "progeny" &&
    Boolean(crmMetadata.account_id);

  const documentById = useMemo(() => {
    const map = new Map<string, ReviewDocumentRow>();
    for (const doc of documents) {
      if (doc.id) map.set(doc.id, doc as ReviewDocumentRow);
    }
    return map;
  }, [documents]);

  const listSections = useMemo(
    () =>
      buildDocumentListSections({
        queue: documentQueue?.queue,
        documents: documents as ReviewDocumentRow[],
        search,
        classificationFilter,
        sortMode,
      }),
    [classificationFilter, documentQueue?.queue, documents, search, sortMode],
  );

  const { queuedRows, reviewRows } = listSections;
  const queueItemCount = documentQueue?.queue?.items?.length ?? 0;
  const hasAnythingOnCase = documents.length > 0 || queueItemCount > 0;
  const queueActionsDisabled = Boolean(documentQueue?.isBusy || documentQueue?.processingBlocked);

  if (isLoading && documents.length === 0 && queueItemCount === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-indigo-500 animate-spin" />
        <span className="ml-3 text-slate-500 dark:text-slate-400">Loading documents...</span>
      </div>
    );
  }

  return (
    <div className={clsx(reviewWorkflowMode ? "space-y-5" : "space-y-4")}>
      <CaseDocumentsToolbar
        search={search}
        onSearchChange={setSearch}
        sortMode={sortMode}
        onSortModeChange={setSortMode}
        searchInputId="case-documents-search"
        documentIngest={caseId && documentIngest ? documentIngest : undefined}
        showBrowseCrmFallback={!reviewWorkflowMode && Boolean(caseId) && canBrowseCrmDocuments && !documentIngest}
        onBrowseCrmFallback={() => setShowAvailableDocsModal(true)}
        testIdPrefix="case-documents"
        classificationFilter={classificationFilter}
        onClassificationFilterChange={setClassificationFilter}
        classificationFilterOptions={classificationFilterOptions}
        comfortable={reviewWorkflowMode}
      />

      {documentIngest?.ingestError ? (
        <div className={clsx(CC_PANEL, "text-sm", CC_ERROR_TEXT)} role="alert">
          {documentIngest.ingestError}
        </div>
      ) : null}

      <ClassificationFailedBanner
        failures={classificationFailures}
        collapsible={reviewWorkflowMode}
        onRetryAll={
          onRetryClassification
            ? () => {
                const failedIds = classificationFailures
                  .map((f) => f.id)
                  .filter((id): id is string => Boolean(id));
                onRetryClassification(failedIds.length > 0 ? failedIds : undefined);
              }
            : undefined
        }
        isRetrying={isRetryingClassification}
        retryDisabled={Boolean(
          documentIngest?.isProcessing ||
            documentIngest?.isAgenticProcessing ||
            documentQueue?.isBusy ||
            documentQueue?.processingBlocked,
        )}
      />

      {documentQueue ? (
        <DocumentQueueBatch
          queue={documentQueue.queue}
          caseId={documentQueue.caseId}
          sourceReviewRunId={documentQueue.sourceReviewRunId}
          isBusy={documentQueue.isBusy}
          processingBlocked={documentQueue.processingBlocked}
          reviewProgress={documentQueue.reviewProgress}
          onSubmitNow={documentQueue.onSubmitNow}
          onScheduleTonight={documentQueue.onScheduleTonight}
          onCancelSchedule={documentQueue.onCancelSchedule}
        >
          {queuedRows.map((item) => {
            const linked = item.document_id ? documentById.get(item.document_id) : undefined;
            const classificationLabel =
              linked?.classification && linked.classification !== "Pending..."
                ? formatClassification(linked.classification)
                : null;
            return (
              <CaseDocumentQueueRow
                key={item.id}
                item={item}
                classificationLabel={classificationLabel}
                disabled={queueActionsDisabled}
                onRemove={() => documentQueue.onRemoveItem(item.id)}
              />
            );
          })}
        </DocumentQueueBatch>
      ) : null}

      {/* Documents list */}
      <div className={clsx(reviewWorkflowMode ? "space-y-3 pt-1" : "space-y-2")}>
        {!hasAnythingOnCase ? (
          <div className={clsx(CC_PANEL, "py-10 text-center text-sm text-slate-500")}>
            No documents on file yet
          </div>
        ) : queuedRows.length === 0 && reviewRows.length === 0 ? (
          <div className={clsx(CC_PANEL, "py-10 text-center text-sm text-slate-500")}>
            No documents match your search
          </div>
        ) : (
          <>
            {reviewRows.map((doc, i) => {
            const reviewDoc = doc as MockDocument;
            const docIcon = resolveDocListIcon(reviewDoc);
            const isClassifying = isDocumentClassifying(reviewDoc);
            const isClassificationFailed = isDocumentClassificationFailed(reviewDoc);
            const listSubtitle = formatDocumentListSubtitle(reviewDoc);

            return (
              <MissionControlCard
                key={doc.id ?? i}
                className={CC_LIST_CARD_SHELL}
                onClick={() => setSelectedDoc(reviewDoc)}
                icon={
                  <div
                    className={clsx(
                      "flex h-9 w-9 items-center justify-center rounded-lg",
                      docIcon.bg,
                      docIcon.text,
                    )}
                  >
                    {isClassifying ? (
                      <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={docIcon.icon} className="w-4 h-4" />
                    )}
                  </div>
                }
                title={formatDocumentListTitle(doc)}
                subtitle={
                  <span className="inline-flex flex-col items-start gap-0.5">
                    {listSubtitle ? (
                      <span className="text-[11px] text-slate-500">{listSubtitle}</span>
                    ) : null}
                    <span
                      className={clsx(
                        "inline-flex mt-1 px-2 py-0.5 text-xs font-medium rounded-full ring-1 ring-slate-200 dark:ring-white/10",
                        isClassifying ? "text-slate-500" : isClassificationFailed ? "text-rose-700 ring-rose-500/20 dark:text-rose-300" : "text-slate-400",
                      )}
                      title={doc.classificationError ?? undefined}
                    >
                      {formatClassification(doc.classification)}
                    </span>
                    <DocumentContentUnreadableHint doc={reviewDoc} className="mt-0.5" />
                  </span>
                }
                status={
                  <div
                    className="flex items-center gap-3"
                    role="presentation"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {!reviewWorkflowMode && caseId && doc.id ? (
                      <CaseDocumentFileActions
                        caseId={caseId}
                        doc={{
                          id: doc.id,
                          filename: doc.name,
                          file_type: doc.fileType ?? doc.name.split(".").pop() ?? "",
                        }}
                        disabled={isClassifying}
                        variant="labeled"
                        fixedSlots
                        testIdPrefix={`doc-${doc.id}`}
                      />
                    ) : (
                      <div className="grid grid-cols-[6.5rem_4.5rem] items-center gap-x-3" aria-hidden>
                        <span className="h-9 shrink-0" />
                        <span className="h-9 shrink-0" />
                      </div>
                    )}
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center">
                      {!isClassifying ? (
                        <DocumentClassificationStatusBadge valid={doc.valid ?? false} />
                      ) : (
                        <span className="h-7 w-7 shrink-0" aria-hidden />
                      )}
                    </div>
                  </div>
                }
              />
            );
            })}
          </>
        )}
      </div>

      {documents.length > 0 && !reviewWorkflowMode ? (
        <div className={CC_LEGEND}>
          <div className="flex items-center gap-2">
            <DocumentClassificationStatusBadge valid size="sm" />
            <span>Classified</span>
          </div>
          <div className="flex items-center gap-2">
            <DocumentClassificationStatusBadge valid={false} size="sm" />
            <span>Needs attention</span>
          </div>
        </div>
      ) : null}

      {/* Document Detail Drawer */}
      {selectedDoc && (
        <DocumentDetailDrawer
          caseId={caseId}
          document={selectedDoc}
          onClose={() => setSelectedDoc(null)}
          onDocumentsChanged={onDocumentsChanged}
          onRetryClassification={onRetryClassification}
          isRetryingClassification={isRetryingClassification}
          retryDisabled={Boolean(
            documentIngest?.isProcessing ||
              documentIngest?.isAgenticProcessing ||
              documentQueue?.isBusy ||
              documentQueue?.processingBlocked,
          )}
        />
      )}

      {caseId && canBrowseCrmDocuments && documentIngest?.onCrmDocumentsAdded ? (
        <AvailableDocumentsModal
          open={showAvailableDocsModal}
          onClose={() => setShowAvailableDocsModal(false)}
          caseId={caseId}
          onDocumentsAdded={documentIngest.onCrmDocumentsAdded}
          ingestBlocked={documentIngest.isProcessing || documentIngest.isAgenticProcessing}
          ingestBlockedMessage={documentIngest.ingestError}
        />
      ) : null}
    </div>
  );
}

function formatClassification(classification: string): string {
  if (classification === "Classifying..." || classification === "Pending..." || classification === "Classification failed") {
    return classification;
  }
  const labels: Record<string, string> = {
    "fact-find": "Fact Find",
    "file-note": "File Note",
    "suitability-report": "Suitability Report",
    csa: "Client Service Agreement",
    "oxford-risk": "Risk Profile",
    "voyant-cashflow": "Cashflow Report",
    illustration: "Illustration",
    kfd: "Key Features",
    "application-form": "Application",
    "id-verification": "ID Verification",
    smartsearch: "SmartSearch",
    "instruction-to-proceed": "Instruction to Proceed",
    "annual-review-letter": "Annual Review",
    "pension-statement": "Pension Statement",
    "platform-report": "Platform Report",
    correspondence: "Correspondence",
    unknown: "Unknown",
  };
  return labels[classification] || classification.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function DocumentDetailDrawer({
  caseId,
  document,
  onClose,
  onDocumentsChanged,
  onRetryClassification,
  isRetryingClassification = false,
  retryDisabled = false,
}: {
  caseId?: string;
  document: MockDocument;
  onClose: () => void;
  onDocumentsChanged?: () => void;
  onRetryClassification?: (documentIds?: string[]) => void;
  isRetryingClassification?: boolean;
  retryDisabled?: boolean;
}) {
  const [showReasoning, setShowReasoning] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showError } = useNotification();
  const hasAiData =
    document.summary ||
    document.redFlags?.length ||
    document.keyDates ||
    document.keyParties ||
    document.keyFigures;
  const highFlags = document.redFlags?.filter((f) => f.severity === "high") || [];
  const mediumFlags = document.redFlags?.filter((f) => f.severity === "medium") || [];
  const lowFlags = document.redFlags?.filter((f) => f.severity === "low") || [];
  const docIcon = resolveDocListIcon(document);
  const isClassifying = isDocumentClassifying(document);
  const isClassificationFailed = isDocumentClassificationFailed(document);

  const handleConfirmDelete = async () => {
    if (!caseId || !document.id) return;
    setIsDeleting(true);
    try {
      await deleteCaseDocument(caseId, document.id);
      setConfirmDeleteOpen(false);
      onClose();
      onDocumentsChanged?.();
    } catch {
      showError("Could not remove document", "Try again or refresh the case.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
    <Drawer
      isOpen={true}
      onClose={onClose}
      title={
        <div className="flex min-w-0 items-center gap-2.5">
          <div
            className={clsx(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
              docIcon.bg,
              docIcon.text,
            )}
          >
            {isClassifying ? (
              <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
            ) : (
              <FontAwesomeIcon icon={docIcon.icon} className="h-4 w-4" />
            )}
          </div>
          <span className="truncate">{document.name}</span>
        </div>
      }
      width="max-w-lg"
      headerActions={
        caseId && document.id ? (
          <div className="flex items-center gap-0.5">
            <CaseDocumentFileActions
              caseId={caseId}
              doc={{
                id: document.id,
                filename: document.name,
                file_type: document.fileType ?? document.name.split(".").pop() ?? "",
              }}
              disabled={isClassifying || isDeleting}
              testIdPrefix={`doc-drawer-${document.id}`}
            />
            <DestructiveTrashIconButton
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={isClassifying || isDeleting}
              title="Remove document from case"
              aria-label={`Remove ${document.name} from case`}
              data-testid={`doc-drawer-${document.id}-delete`}
              className="h-8 w-8 rounded"
            />
          </div>
        ) : undefined
      }
    >
      <div className="space-y-5 px-6 pb-6 pt-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={clsx(
              "inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ring-1 ring-slate-200 dark:ring-white/10",
              isClassifying ? "text-slate-500" : isClassificationFailed ? "text-rose-700 ring-rose-500/20 dark:text-rose-300" : "text-slate-400",
            )}
          >
            {formatClassification(document.classification)}
          </span>
          <DocumentContentUnreadableHint doc={document} />
        </div>

        {document.classificationError ? (
          <ClassificationFailedBanner
            compactTitle="Classification failed"
            failures={[{ id: document.id, name: document.name, classificationError: document.classificationError }]}
            onRetryAll={
              onRetryClassification && document.id
                ? () => onRetryClassification([document.id as string])
                : undefined
            }
            isRetrying={isRetryingClassification}
            retryDisabled={retryDisabled || isClassifying || isDeleting}
          />
        ) : null}

        {document.summary ? (
          <section>
            <p className={CC_SECTION_LABEL}>Summary</p>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{document.summary}</p>
          </section>
        ) : null}

        {document.redFlags && document.redFlags.length > 0 ? (
          <section>
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <p className={clsx(CC_SECTION_LABEL, "mb-0")}>Red flags</p>
              <div className="flex flex-wrap justify-end gap-x-2 gap-y-1 text-xs text-slate-500 shrink-0">
                {highFlags.length > 0 ? (
                  <span className={CC_STAT_NEGATIVE}>{highFlags.length} high</span>
                ) : null}
                {mediumFlags.length > 0 ? (
                  <span className="text-amber-400">{mediumFlags.length} medium</span>
                ) : null}
                {lowFlags.length > 0 ? (
                  <span>{lowFlags.length} low</span>
                ) : null}
              </div>
            </div>
            <div className="space-y-3">
              {highFlags.map((flag, i) => (
                <RedFlagItem key={`high-${i}`} flag={flag} />
              ))}
              {mediumFlags.map((flag, i) => (
                <RedFlagItem key={`medium-${i}`} flag={flag} />
              ))}
              {lowFlags.map((flag, i) => (
                <RedFlagItem key={`low-${i}`} flag={flag} />
              ))}
            </div>
          </section>
        ) : null}

        {document.keyParties && Object.keys(document.keyParties).length > 0 ? (
          <section>
            <p className={CC_SECTION_LABEL}>Key parties</p>
            <div className={clsx(CC_PANEL, "space-y-2 p-3")}>
              {Object.entries(document.keyParties).map(([key, value]) => (
                <div key={key} className="flex justify-between gap-3 text-sm">
                  <span className="text-slate-500">{formatKeyLabel(key)}</span>
                  <span className="font-medium text-slate-700 dark:text-slate-200 text-right">{value}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {document.keyDates && Object.keys(document.keyDates).length > 0 ? (
          <section>
            <p className={CC_SECTION_LABEL}>Key dates</p>
            <div className={clsx(CC_PANEL, "space-y-2 p-3")}>
              {Object.entries(document.keyDates).map(([key, value]) => (
                <div key={key} className="flex justify-between gap-3 text-sm">
                  <span className="text-slate-500">{formatKeyLabel(key)}</span>
                  <span className="font-medium text-slate-700 dark:text-slate-200 text-right">{value}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {document.keyFigures && Object.keys(document.keyFigures).length > 0 ? (
          <section>
            <p className={CC_SECTION_LABEL}>Key figures</p>
            <div className={clsx(CC_PANEL, "space-y-2 p-3 max-h-64 overflow-y-auto")}>
              {Object.entries(document.keyFigures).map(([key, value]) => (
                <div key={key} className="flex justify-between gap-3 text-sm">
                  <span className="text-slate-500">{formatKeyLabel(key)}</span>
                  <span className="font-medium text-slate-700 dark:text-slate-200 text-right">
                    {typeof value === "object" ? JSON.stringify(value) : String(value)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {document.reasoning ? (
          <Accordion
            isExpanded={showReasoning}
            onToggle={() => setShowReasoning((open) => !open)}
            className="border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02]"
            headerClassName="hover:bg-slate-50 dark:hover:bg-white/[0.03] px-3 py-2.5"
            contentClassName="border-slate-200 dark:border-white/10 px-3 py-3"
            header={
              <span className="text-xs font-medium text-slate-400">Why this document type?</span>
            }
          >
            <p className="text-sm text-slate-400 leading-relaxed">{document.reasoning}</p>
          </Accordion>
        ) : null}

        {!hasAiData && document.valid ? (
          <div className={clsx(CC_PANEL, "p-4")}>
            <p className="text-sm text-slate-500">
              Classification complete. Additional analysis will appear here when available.
            </p>
          </div>
        ) : null}

        {!document.valid && !document.classificationError ? (
          <div className={clsx(CC_PANEL, "p-4 flex items-center gap-3")}>
            <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 text-indigo-400 animate-spin shrink-0" />
            <p className="text-sm text-slate-500">Classification in progress…</p>
          </div>
        ) : null}
      </div>
    </Drawer>
    <ConfirmDeleteDialog
      open={confirmDeleteOpen}
      onClose={() => setConfirmDeleteOpen(false)}
      onConfirm={() => void handleConfirmDelete()}
      title="Remove document from case?"
      description={`Remove "${document.name}" from this case? You can re-upload or import it again later if needed.`}
      confirmLabel="Remove"
      isLoading={isDeleting}
    />
    </>
  );
}

function RedFlagItem({ flag }: { flag: RedFlag }) {
  const dotColor =
    flag.severity === "high" ? "bg-red-500" : flag.severity === "medium" ? "bg-amber-500" : "bg-slate-400";

  return (
    <div className="flex items-start gap-2.5">
      <div className={clsx("w-1.5 h-1.5 rounded-full mt-2 shrink-0", dotColor)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-600 dark:text-slate-300">{flag.description}</p>
        {flag.remedial_action ? (
          <p className="text-xs text-slate-500 mt-1.5 pl-3 border-l border-slate-200 dark:border-white/10 leading-relaxed">
            {flag.remedial_action}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function formatKeyLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function getDocIcon(type: string) {
  switch (type) {
    case "fact-find": return faFileLines;
    case "file-note": return faFilePen;
    case "suitability": return faFileContract;
    case "disclosure": return faFileShield;
    default: return faFile;
  }
}

type DocListIcon = { icon: IconDefinition; bg: string; text: string };

function resolveDocListIcon(doc: MockDocument): DocListIcon {
  const fileVisual = getFileIconVisual(null, doc.name);
  if (fileVisual) {
    return { icon: fileVisual.icon, bg: fileVisual.tone.bg, text: fileVisual.tone.text };
  }

  const classification = doc.classification.toLowerCase();
  if (classification.includes("suitability")) {
    return { icon: faFileContract, bg: "bg-emerald-500/10", text: "text-emerald-800 dark:text-emerald-300" };
  }
  if (classification.includes("kfd") || classification.includes("key-fact")) {
    return { icon: faFileShield, bg: "bg-indigo-500/10", text: "text-indigo-700 dark:text-indigo-300" };
  }
  if (classification.includes("correspondence") || classification.includes("file-note")) {
    return { icon: faFilePen, bg: "bg-amber-500/10", text: "text-amber-700 dark:text-amber-300" };
  }
  if (classification.includes("fact-find")) {
    return { icon: faFileLines, bg: "bg-indigo-500/10", text: "text-indigo-700 dark:text-indigo-300" };
  }

  return { icon: getDocIcon(doc.type), bg: "bg-slate-100 dark:bg-white/5", text: "text-slate-600 dark:text-slate-300" };
}

/* ============================================
   COMPLETENESS TAB
   ============================================ */

/** Build checklist rows from the active rule set joined with the case's documents. */
function buildChecklistFromRuleSet(
  ruleSet: RuleSetVersion,
  caseType: string,
  documents: MockDocument[],
  apiCompleteness: ApiCompleteness | null,
  caseTypes?: string[] | null,
  transactionSubtype?: string | null,
): {
  universal: ChecklistItem[];
  caseSpecific: ChecklistItem[];
  subtypeSections: { subtype: string; title: string; items: ChecklistItem[] }[];
  conditional: ChecklistItem[];
} {
  const tab2 = ruleSet.document_requirements ?? [];
  if (tab2.length > 0) {
    const applicable = tab2.filter((r) =>
      documentRequirementApplies(r, caseType, caseTypes, transactionSubtype),
    );

    const completenessChecked =
      apiCompleteness != null && apiCompleteness.status !== "pending";

    const missingAnySet = new Set(
      (apiCompleteness?.missing_documents ?? []).map((m) => m.document_type),
    );
    const notApplicableByType = new Map<string, string>();
    for (const na of apiCompleteness?.not_applicable ?? []) {
      notApplicableByType.set(na.document_type, na.reason);
    }
    const equivalences = ruleSet.document_equivalences || {};
    const displayName = (slug: string): string => ruleSet.document_display_names[slug] ?? slug;
    const normaliseType = (type: string): string[] => {
      const acc = new Set<string>([type]);
      for (const [canonical, alts] of Object.entries(equivalences)) {
        if (canonical === type || alts.includes(type)) {
          acc.add(canonical);
          for (const a of alts) acc.add(a);
        }
      }
      return [...acc];
    };
    const satisfiedIdsByType = new Map<string, string[]>();
    for (const s of apiCompleteness?.satisfied_requirements ?? []) {
      satisfiedIdsByType.set(s.document_type, s.document_ids ?? []);
    }

    const resolveLinkedDocs = (
      ids: string[],
      typeLabel?: string,
    ): ChecklistLinkedDocument[] => {
      const seen = new Set<string>();
      const out: ChecklistLinkedDocument[] = [];
      for (const id of ids) {
        const doc = documents.find((d) => d.id === id);
        if (!doc || seen.has(doc.name)) continue;
        seen.add(doc.name);
        out.push({
          id: doc.id,
          name: doc.name,
          typeLabel: typeLabel ?? displayName(doc.type),
        });
      }
      return out;
    };

    const makeRowInner = (type: string, required: boolean, conditionNote?: string): ChecklistItem => {
      const typeVariants = normaliseType(type);
      const validDocs = documents.filter((d) => typeVariants.includes(d.type) && d.valid);
      const invalidDocs = documents.filter((d) => typeVariants.includes(d.type) && !d.valid);
      const naReason = notApplicableByType.get(type);
      const isNotApplicable = completenessChecked && Boolean(naReason);
      const satisfiedIds = satisfiedIdsByType.get(type) ?? [];
      const hasSatisfyingDoc = satisfiedIds.length > 0 || validDocs.length > 0;
      const present = !completenessChecked
        ? false
        : isNotApplicable
          ? false
          : !missingAnySet.has(type) && (required || hasSatisfyingDoc);
      const blocking = required;
      const isMissingRequired = completenessChecked && blocking && !present && !isNotApplicable;
      const equivalentSlugs = equivalences[type] ?? [];
      let linkedDocuments: ChecklistLinkedDocument[] | undefined;
      if (present && completenessChecked) {
        const cited = resolveLinkedDocs(satisfiedIds);
        const fallback =
          cited.length > 0
            ? cited
            : validDocs.map((d) => ({
              id: d.id,
              name: d.name,
              typeLabel: displayName(type),
            }));
        linkedDocuments = fallback.length > 0 ? fallback : undefined;
      } else if (isMissingRequired && equivalentSlugs.length > 0) {
        const related = documents
          .filter((d) => d.valid && equivalentSlugs.includes(d.type))
          .map((d) => ({
            id: d.id,
            name: d.name,
            typeLabel: displayName(d.type),
          }));
        linkedDocuments = related.length > 0 ? related : undefined;
      }
      return {
        name: displayName(type),
        type: type as DocumentType,
        required,
        blocking,
        present,
        checked: completenessChecked,
        notApplicable: isNotApplicable ? { reason: naReason! } : undefined,
        hasInvalidDoc:
          completenessChecked && !isNotApplicable && invalidDocs.length > 0 && validDocs.length === 0,
        note:
          conditionNote ??
          (isMissingRequired
            ? (apiCompleteness?.missing_documents ?? []).find((m) => m.document_type === type)?.reason
            : undefined),
        conditionNote,
        linkedDocuments,
      };
    };

    const universalSlugs = [
      ...new Set(
        applicable
          .filter(
            (r) =>
              r.presence === "required" &&
              r.case_types.includes("ALL") &&
              !r.transaction_subtype,
          )
          .map((r) => r.document_slug),
      ),
    ];
    const caseSpecificSlugs = [
      ...new Set(
        applicable
          .filter(
            (r) =>
              r.presence === "required" &&
              !r.case_types.includes("ALL") &&
              !r.transaction_subtype,
          )
          .map((r) => r.document_slug),
      ),
    ];
    const subtypeBuckets = new Map<string, Set<string>>();
    for (const req of applicable) {
      if (req.presence !== "required" || !req.transaction_subtype) continue;
      const bucket = subtypeBuckets.get(req.transaction_subtype) ?? new Set<string>();
      bucket.add(req.document_slug);
      subtypeBuckets.set(req.transaction_subtype, bucket);
    }
    const conditionalReqs = applicable.filter((r) => r.presence === "conditional");

    return {
      universal: universalSlugs.map((t) => makeRowInner(t, true)),
      caseSpecific: caseSpecificSlugs.map((t) => makeRowInner(t, true)),
      subtypeSections: [...subtypeBuckets.entries()].map(([subtype, slugs]) => ({
        subtype,
        title: transactionSubtypeLabel(subtype),
        items: [...slugs].map((t) => makeRowInner(t, true)),
      })),
      conditional: conditionalReqs
        .map((r) => makeRowInner(r.document_slug, false, r.condition || "Conditional"))
        .filter((row) => row.present || row.notApplicable || missingAnySet.has(row.type)),
    };
  }

  const caseReq = ruleSet.case_type_requirements.find((r) => r.case_type === caseType)
    ?? ruleSet.case_type_requirements.find((r) => r.case_type === "unknown")
    ?? { case_type: caseType, required: [], recommended: [] };

  const completenessChecked =
    apiCompleteness != null && apiCompleteness.status !== "pending";

  // Any doc the API reports as missing. Blocking is decided by the rule set's
  // `required` param passed into makeRow, not by the API's `.required` flag —
  // conditional docs must never turn red on the tab or the row.
  const missingAnySet = new Set(
    (apiCompleteness?.missing_documents ?? []).map((m) => m.document_type),
  );

  const notApplicableByType = new Map<string, string>();
  for (const na of apiCompleteness?.not_applicable ?? []) {
    notApplicableByType.set(na.document_type, na.reason);
  }

  const equivalences = ruleSet.document_equivalences || {};
  // Build a lookup from any doc type → its canonical type (for equivalences both ways).
  const normaliseType = (type: string): string[] => {
    const acc = new Set<string>([type]);
    for (const [canonical, alts] of Object.entries(equivalences)) {
      if (canonical === type || alts.includes(type)) {
        acc.add(canonical);
        for (const a of alts) acc.add(a);
      }
    }
    return [...acc];
  };

  const displayName = (slug: string): string => ruleSet.document_display_names[slug] ?? slug;

  const satisfiedIdsByType = new Map<string, string[]>();
  for (const s of apiCompleteness?.satisfied_requirements ?? []) {
    satisfiedIdsByType.set(s.document_type, s.document_ids ?? []);
  }

  const resolveLinkedDocs = (
    ids: string[],
    typeLabel?: string,
  ): ChecklistLinkedDocument[] => {
    const seen = new Set<string>();
    const out: ChecklistLinkedDocument[] = [];
    for (const id of ids) {
      const doc = documents.find((d) => d.id === id);
      if (!doc || seen.has(doc.name)) continue;
      seen.add(doc.name);
      out.push({
        id: doc.id,
        name: doc.name,
        typeLabel: typeLabel ?? displayName(doc.type),
      });
    }
    return out;
  };

  const makeRow = (type: string, required: boolean): ChecklistItem => {
    const typeVariants = normaliseType(type);
    const validDocs = documents.filter((d) => typeVariants.includes(d.type) && d.valid);
    const invalidDocs = documents.filter((d) => typeVariants.includes(d.type) && !d.valid);
    const naReason = notApplicableByType.get(type);
    const isNotApplicable = completenessChecked && Boolean(naReason);
    const satisfiedIds = satisfiedIdsByType.get(type) ?? [];
    const hasSatisfyingDoc = satisfiedIds.length > 0 || validDocs.length > 0;
    // Present only when the API did not report this type as missing AND there is
    // an actual document satisfying it. Required types fall back to "not missing"
    // for backwards-compat, but recommended types require a real doc so they
    // don't show a free green tick (e.g. MiCap with no document on file).
    const present = !completenessChecked
      ? false
      : isNotApplicable
        ? false
        : !missingAnySet.has(type) && (required || hasSatisfyingDoc);
    const blocking = required;
    const isMissingRequired = completenessChecked && blocking && !present && !isNotApplicable;
    const equivalentSlugs = equivalences[type] ?? [];
    let linkedDocuments: ChecklistLinkedDocument[] | undefined;
    if (present && completenessChecked) {
      const cited = resolveLinkedDocs(satisfiedIdsByType.get(type) ?? []);
      const fallback =
        cited.length > 0
          ? cited
          : validDocs.map((d) => ({
            id: d.id,
            name: d.name,
            typeLabel: displayName(type),
          }));
      linkedDocuments = fallback.length > 0 ? fallback : undefined;
    } else if (isMissingRequired && equivalentSlugs.length > 0) {
      const related = documents
        .filter((d) => d.valid && equivalentSlugs.includes(d.type))
        .map((d) => ({
          id: d.id,
          name: d.name,
          typeLabel: displayName(d.type),
        }));
      linkedDocuments = related.length > 0 ? related : undefined;
    }
    return {
      name: displayName(type),
      type: type as DocumentType,
      required,
      blocking,
      present,
      checked: completenessChecked,
      notApplicable: isNotApplicable ? { reason: naReason! } : undefined,
      hasInvalidDoc: completenessChecked && !isNotApplicable && invalidDocs.length > 0 && validDocs.length === 0,
      note: isMissingRequired
        ? (apiCompleteness?.missing_documents ?? []).find((m) => m.document_type === type)?.reason
        : undefined,
      linkedDocuments,
    };
  };

  const universalRequired = ruleSet.universal_required ?? [];
  const universalRecommended = (ruleSet.universal_recommended ?? []).filter((t) => !universalRequired.includes(t));
  const universalSet = new Set([...universalRequired, ...universalRecommended]);

  const universal: ChecklistItem[] = [
    ...universalRequired.map((t) => makeRow(t, true)),
    ...universalRecommended.map((t) => makeRow(t, false)),
  ];

  // Case-specific: only show items that aren't already in universal.
  const caseRequired = caseReq.required.filter((t) => !universalSet.has(t));
  const caseRecommended = caseReq.recommended.filter((t) => !universalSet.has(t) && !caseRequired.includes(t));

  const caseSpecific: ChecklistItem[] = [
    ...caseRequired.map((t) => makeRow(t, true)),
    ...caseRecommended.map((t) => makeRow(t, false)),
  ];

  return { universal, caseSpecific, subtypeSections: [], conditional: [] };
}

function ChecklistOutcomeSummary({
  items,
  completenessChecked,
  showOptionalGaps = false,
}: {
  items: ChecklistItem[];
  completenessChecked: boolean;
  showOptionalGaps?: boolean;
}) {
  if (!completenessChecked) {
    return <span className="text-xs text-slate-500">{items.length} items</span>;
  }
  const counts = summarizeChecklistItems(items);
  const parts: { key: string; label: string; className: string }[] = [];
  if (counts.present > 0) {
    parts.push({ key: "present", label: `${counts.present} on file`, className: CC_STAT_POSITIVE });
  }
  if (counts.blockingMissing > 0) {
    parts.push({ key: "missing", label: `${counts.blockingMissing} missing`, className: CC_STAT_NEGATIVE });
  }
  if (showOptionalGaps && counts.optionalOpen > 0) {
    parts.push({
      key: "optional",
      label: `${counts.optionalOpen} not provided`,
      className: "text-slate-500",
    });
  }
  if (counts.na > 0) {
    parts.push({ key: "na", label: `${counts.na} N/A`, className: "text-slate-500" });
  }
  if (parts.length === 0) {
    return <span className="text-xs text-slate-500">Not checked</span>;
  }
  return (
    <span className="text-xs tabular-nums">
      {parts.map((part, i) => (
        <React.Fragment key={part.key}>
          {i > 0 ? <span className="text-slate-600"> · </span> : null}
          <span className={part.className}>{part.label}</span>
        </React.Fragment>
      ))}
    </span>
  );
}

function ChecklistSection({
  title,
  items,
  documents,
  completenessChecked,
  onViewDocument,
  showOptionalGaps = false,
  reviewWorkflowMode = false,
  checklistDisputes,
  onChecklistDisputeChange,
  isExpanded,
  onExpandedChange,
  sectionPriority,
}: {
  title: string;
  items: ChecklistItem[];
  documents: MockDocument[];
  completenessChecked: boolean;
  onViewDocument?: (doc: MockDocument) => void;
  showOptionalGaps?: boolean;
  reviewWorkflowMode?: boolean;
  checklistDisputes?: Record<string, string>;
  onChecklistDisputeChange?: (itemKey: string, note: string) => void;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  sectionPriority: number;
}) {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = isExpanded ?? internalExpanded;
  const toggleExpanded = () => {
    const next = !expanded;
    if (onExpandedChange) onExpandedChange(next);
    else setInternalExpanded(next);
  };

  return (
    <Accordion
      isExpanded={expanded}
      onToggle={toggleExpanded}
      className={clsx(CC_PANEL_FLUSH, "!rounded-xl border-0 ring-1 ring-slate-200 dark:ring-white/5")}
      headerClassName="hover:bg-slate-50 dark:hover:bg-white/[0.03] !px-4 !py-3"
      contentClassName="!p-0 border-slate-200 dark:border-white/5"
      header={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">{title}</span>
          <ChecklistOutcomeSummary
            items={items}
            completenessChecked={completenessChecked}
            showOptionalGaps={showOptionalGaps}
          />
        </div>
      }
    >
      <div className="divide-y divide-slate-200 dark:divide-white/5">
        {items.map((item, i) => {
          const itemKey = `${title}::${item.type}`;
          return (
            <ChecklistRow
              key={`${title}-${i}-${item.type}`}
              item={item}
              sectionTitle={title}
              sectionPriority={sectionPriority}
              documents={documents}
              onViewDocument={onViewDocument}
              reviewWorkflowMode={reviewWorkflowMode}
              disputeNote={checklistDisputes?.[itemKey]}
              onDisputeChange={
                onChecklistDisputeChange
                  ? (note) => onChecklistDisputeChange(itemKey, note)
                  : undefined
              }
            />
          );
        })}
      </div>
    </Accordion>
  );
}

function buildChecklistScrollCandidates(
  universal: ChecklistItem[],
  caseSpecific: ChecklistItem[],
  subtypeSections: { subtype: string; title: string; items: ChecklistItem[] }[],
  conditional: ChecklistItem[],
  caseTypeLabel: string,
): ChecklistScrollCandidate[] {
  const candidates: ChecklistScrollCandidate[] = [];
  const push = (items: ChecklistItem[], sectionKey: string, sectionPriority: number) => {
    for (const item of items) {
      candidates.push({
        documentType: item.type,
        sectionKey,
        sectionPriority,
        missing: isChecklistItemMissing(item),
      });
    }
  };

  push(universal, CHECKLIST_SECTION_UNIVERSAL, CHECKLIST_SECTION_PRIORITY.universal);
  push(caseSpecific, `${caseTypeLabel} requirements`, CHECKLIST_SECTION_PRIORITY.caseSpecific);
  subtypeSections.forEach((section, index) => {
    push(
      section.items,
      `${section.title} requirements`,
      CHECKLIST_SECTION_PRIORITY.subtypeBase + index,
    );
  });
  push(conditional, CHECKLIST_SECTION_CONDITIONAL, CHECKLIST_SECTION_PRIORITY.conditional);
  return candidates;
}

function CompletenessTab({
  caseType,
  caseTypes,
  transactionSubtype,
  documents,
  apiCompleteness,
  ruleSet,
  ruleSetLoading,
  ruleSetError,
  onViewDocument,
  reviewWorkflowMode = false,
  checklistDisputes,
  onChecklistDisputeChange,
  completenessReview,
  onCompletenessReviewChange,
  onCompletenessDecisionDirtyChange,
  agentCompletenessCall = "pending",
  documentIngest,
  scrollToItemType,
  onScrolledToItem,
}: {
  caseType: string;
  caseTypes?: string[] | null;
  transactionSubtype?: string | null;
  documents: MockDocument[];
  apiCompleteness: ApiCompleteness | null;
  ruleSet: RuleSetVersion | null;
  ruleSetLoading: boolean;
  ruleSetError: string | null;
  onViewDocument?: (doc: MockDocument) => void;
  reviewWorkflowMode?: boolean;
  checklistDisputes?: Record<string, string>;
  onChecklistDisputeChange?: (itemKey: string, note: string) => void;
  completenessReview?: CompletenessReviewState | null;
  onCompletenessReviewChange?: (next: CompletenessReviewState) => void;
  onCompletenessDecisionDirtyChange?: (dirty: boolean) => void;
  agentCompletenessCall?: AgentCompletenessCall;
  documentIngest?: CaseDocumentIngestProps;
  scrollToItemType?: string | null;
  onScrolledToItem?: () => void;
}) {
  const [expandedSectionKeys, setExpandedSectionKeys] = useState<Set<string>>(() => new Set());

  const caseTypeLabel = CASE_TYPE_LABELS[caseType as CaseType]
    ?? caseType.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const checklistData = useMemo(() => {
    if (!ruleSet) return null;
    const built = buildChecklistFromRuleSet(
      ruleSet,
      caseType,
      documents,
      apiCompleteness,
      caseTypes,
      transactionSubtype,
    );
    const allChecklistItems = [
      ...built.universal,
      ...built.caseSpecific,
      ...built.subtypeSections.flatMap((section) => section.items),
      ...built.conditional,
    ];
    const missingItems = [
      ...new Map(
        allChecklistItems
          .filter(isChecklistItemMissing)
          .map((item) => [item.type, item]),
      ).values(),
    ];
    return {
      ...built,
      allChecklistItems,
      missingItems,
      typeToSectionKey: buildChecklistScrollTargetMap(
        buildChecklistScrollCandidates(
          built.universal,
          built.caseSpecific,
          built.subtypeSections,
          built.conditional,
          caseTypeLabel,
        ),
      ),
    };
  }, [ruleSet, caseType, documents, apiCompleteness, caseTypes, transactionSubtype, caseTypeLabel]);

  const scrollToItem = useCallback(
    (documentType: string) => {
      const sectionKey = checklistData?.typeToSectionKey.get(documentType);
      if (sectionKey) {
        setExpandedSectionKeys((prev) => new Set(prev).add(sectionKey));
      }
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          scrollToChecklistItem(documentType);
        });
      });
    },
    [checklistData?.typeToSectionKey],
  );

  useEffect(() => {
    if (!scrollToItemType) return;
    scrollToItem(scrollToItemType);
    onScrolledToItem?.();
  }, [scrollToItemType, scrollToItem, onScrolledToItem]);

  if (ruleSetLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-6">
        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
        <span>Loading checklist…</span>
      </div>
    );
  }

  if (ruleSetError) {
    return (
      <div className={clsx(CC_PANEL, "text-sm", CC_ERROR_TEXT)}>
        Failed to load rule set: {ruleSetError}
      </div>
    );
  }

  if (!checklistData) {
    return (
      <div className={clsx(CC_PANEL, "text-sm text-amber-800/90 dark:text-amber-800 dark:text-amber-200/90")}>
        No published rule set for your company yet. An administrator can publish one from{" "}
        <OsLink to="/case-checking/rules" className="underline font-medium">
          Rule sets
        </OsLink>
        .
      </div>
    );
  }

  const { universal, caseSpecific, subtypeSections, conditional, missingItems } = checklistData;
  const completenessChecked =
    apiCompleteness != null && apiCompleteness.status !== "pending";

  const isComplete = completenessChecked && apiCompleteness!.status === "complete";

  const hasMissingItems = missingItems.length > 0;

  const checklistReviewProps =
    reviewWorkflowMode && hasMissingItems
      ? { reviewWorkflowMode: true, checklistDisputes, onChecklistDisputeChange }
      : {};

  const sectionExpansionProps = (title: string) => ({
    isExpanded: expandedSectionKeys.has(title),
    onExpandedChange: (expanded: boolean) => {
      setExpandedSectionKeys((prev) => {
        const next = new Set(prev);
        if (expanded) next.add(title);
        else next.delete(title);
        return next;
      });
    },
  });

  const missingLabels = missingItems.map((item) => item.name);

  return (
    <div className="space-y-4">
      {reviewWorkflowMode && completenessChecked ? (
        <CompletenessAgentStatusBanner
          agentCall={agentCompletenessCall}
          missingLabels={missingLabels}
          agentSummary={apiCompleteness?.summary}
        />
      ) : null}

      {hasMissingItems && documentIngest ? (
        <MissingDocumentCards
          items={missingItems}
          documentIngest={documentIngest}
          onScrollToItem={scrollToItem}
        />
      ) : null}

      {!completenessChecked && (
        <div className={clsx(CC_PANEL, "text-sm text-slate-400")} data-testid="completeness-pending">
          Completeness has not been checked yet. Run the completeness check to see which documents are on file.
        </div>
      )}

      {completenessChecked && apiCompleteness?.summary && !hasMissingItems && !reviewWorkflowMode ? (
        <div
          className={clsx(
            CC_PANEL,
            isComplete ? "border-l-4 border-l-emerald-500/80" : "border-l-4 border-l-amber-500/80",
          )}
          data-testid="completeness-summary"
        >
          <p className="text-sm text-slate-600 dark:text-slate-300">{apiCompleteness.summary}</p>
        </div>
      ) : null}

      <div className="space-y-3">
        {universal.length > 0 ? (
          <ChecklistSection
            title={CHECKLIST_SECTION_UNIVERSAL}
            sectionPriority={CHECKLIST_SECTION_PRIORITY.universal}
            items={universal}
            documents={documents}
            completenessChecked={completenessChecked}
            onViewDocument={onViewDocument}
            {...checklistReviewProps}
            {...sectionExpansionProps(CHECKLIST_SECTION_UNIVERSAL)}
          />
        ) : null}

        {caseSpecific.length > 0 ? (
          <ChecklistSection
            title={`${caseTypeLabel} requirements`}
            sectionPriority={CHECKLIST_SECTION_PRIORITY.caseSpecific}
            items={caseSpecific}
            documents={documents}
            completenessChecked={completenessChecked}
            onViewDocument={onViewDocument}
            {...checklistReviewProps}
            {...sectionExpansionProps(`${caseTypeLabel} requirements`)}
          />
        ) : null}

        {subtypeSections.map((section, index) =>
          section.items.length > 0 ? (
            <ChecklistSection
              key={section.subtype}
              title={`${section.title} requirements`}
              sectionPriority={CHECKLIST_SECTION_PRIORITY.subtypeBase + index}
              items={section.items}
              documents={documents}
              completenessChecked={completenessChecked}
              onViewDocument={onViewDocument}
              {...checklistReviewProps}
              {...sectionExpansionProps(`${section.title} requirements`)}
            />
          ) : null,
        )}

        {conditional.length > 0 ? (
          <ChecklistSection
            title={CHECKLIST_SECTION_CONDITIONAL}
            sectionPriority={CHECKLIST_SECTION_PRIORITY.conditional}
            items={conditional}
            documents={documents}
            completenessChecked={completenessChecked}
            onViewDocument={onViewDocument}
            showOptionalGaps
            {...checklistReviewProps}
            {...sectionExpansionProps(CHECKLIST_SECTION_CONDITIONAL)}
          />
        ) : null}
      </div>

      {universal.length === 0 &&
        caseSpecific.length === 0 &&
        subtypeSections.every((s) => s.items.length === 0) &&
        conditional.length === 0 ? (
        <div className={clsx(CC_PANEL, "text-sm text-slate-500")}>
          No document requirements defined for case type <span className="font-mono">{caseType}</span>.
          Add them in{" "}
          <OsLink to="/case-checking/rules" className="underline font-medium">
            Rule sets
          </OsLink>
          .
        </div>
      ) : null}

      {reviewWorkflowMode && completenessChecked && onCompletenessReviewChange ? (
        <ReviewCompletenessVerdict
          value={completenessReview ?? null}
          agentCall={agentCompletenessCall}
          onSave={onCompletenessReviewChange}
          onDirtyChange={onCompletenessDecisionDirtyChange}
        />
      ) : null}

      {!reviewWorkflowMode ? (
      <div className={CC_LEGEND}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircleCheck} className={clsx("w-4 h-4", ICON_CHECK_PASSED)} />
          <span>Present</span>
        </div>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircleXmark} className="w-4 h-4 text-red-500" />
          <span>Required but missing</span>
        </div>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircleMinus} className="w-4 h-4 text-slate-400" />
          <span>Not applicable (justified)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full border-2 border-slate-300 dark:border-slate-600" />
          <span>Optional, not provided</span>
        </div>
      </div>
      ) : null}
    </div>
  );
}

function ChecklistDocumentLinks({
  documents: links,
  onOpen,
}: {
  documents: ChecklistLinkedDocument[];
  onOpen?: (id: string | undefined, name: string) => void;
}) {
  return (
    <div className="space-y-0.5 mt-0.5">
      {links.map((rel) => (
        <p key={rel.id ?? rel.name} className="text-xs text-slate-500 dark:text-slate-400">
          {onOpen ? (
            <Button
              plain size="unstyled"
              type="button"
              className="px-0 text-left text-xs underline decoration-slate-400/60 hover:text-indigo-600 dark:hover:text-indigo-400"
              onClick={() => onOpen(rel.id, rel.name)}
            >
              {rel.typeLabel ? `${rel.typeLabel}: ${rel.name}` : rel.name}
            </Button>
          ) : (
            <span>{rel.typeLabel ? `${rel.typeLabel}: ${rel.name}` : rel.name}</span>
          )}
        </p>
      ))}
    </div>
  );
}

function ChecklistRow({
  item,
  sectionTitle,
  sectionPriority,
  documents,
  onViewDocument,
  reviewWorkflowMode = false,
  disputeNote,
  onDisputeChange,
}: {
  item: ChecklistItem;
  sectionTitle: string;
  sectionPriority: number;
  documents: MockDocument[];
  onViewDocument?: (doc: MockDocument) => void;
  reviewWorkflowMode?: boolean;
  disputeNote?: string;
  onDisputeChange?: (note: string) => void;
}) {
  const [draftNote, setDraftNote] = useState(disputeNote ?? "");
  const [editingDispute, setEditingDispute] = useState(false);

  useEffect(() => {
    setDraftNote(disputeNote ?? "");
  }, [disputeNote]);

  const isNotApplicable = Boolean(item.notApplicable);
  const isMissing = item.checked && item.blocking && !item.present && !isNotApplicable;
  const isPresent = item.checked && item.present;
  const isOptionalOpen = item.checked && !item.blocking && !item.present && !isNotApplicable;

  const openRelated = (id: string | undefined, name: string) => {
    if (!onViewDocument) return;
    const doc = documents.find((d) => (id && d.id === id) || d.name === name);
    if (doc) onViewDocument(doc);
  };

  return (
    <div
      className={clsx(
        "flex items-center justify-between px-4 py-3",
        isMissing && "bg-red-950/20",
        isNotApplicable && "bg-slate-50 dark:bg-white/[0.02]",
      )}
      data-checklist-type={item.type}
      data-checklist-section={sectionTitle}
      data-checklist-section-priority={String(sectionPriority)}
      data-checklist-missing={isMissing ? "true" : "false"}
      data-testid={`checklist-row-${item.type}`}
    >
      <div className="flex items-center gap-3">
        {!item.checked ? (
          <div className="w-5 h-5 rounded-full border-2 border-slate-300 dark:border-slate-600" />
        ) : isNotApplicable ? (
          <FontAwesomeIcon icon={faCircleMinus} className="w-5 h-5 text-slate-400 dark:text-slate-500" />
        ) : isPresent ? (
          <FontAwesomeIcon icon={faCircleCheck} className={clsx("w-5 h-5", ICON_CHECK_PASSED)} />
        ) : isMissing ? (
          <FontAwesomeIcon icon={faCircleXmark} className="w-5 h-5 text-red-500" />
        ) : (
          <div className="w-5 h-5 rounded-full border-2 border-slate-300 dark:border-slate-600" />
        )}
        <div>
          <p
            className={clsx(
              "font-medium",
              !item.checked
                ? "text-slate-500"
                : isNotApplicable
                  ? "text-slate-400"
                  : isPresent
                    ? "text-slate-700 dark:text-slate-200"
                    : isMissing
                      ? CC_STAT_NEGATIVE
                      : "text-slate-500",
            )}
          >
            {item.name}
          </p>
          {(isMissing && item.note) || item.conditionNote ? (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {item.conditionNote ?? item.note}
            </p>
          ) : null}
          {item.linkedDocuments && item.linkedDocuments.length > 0 && (
            <ChecklistDocumentLinks
              documents={item.linkedDocuments}
              onOpen={onViewDocument ? openRelated : undefined}
            />
          )}
          {isNotApplicable && item.notApplicable && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{item.notApplicable.reason}</p>
          )}
        </div>
      </div>
      {reviewWorkflowMode && isMissing && onDisputeChange ? (
        <div className="flex max-w-[11rem] shrink-0 flex-col items-end gap-1.5">
          {disputeNote ? (
            <span className={clsx("text-[10px] font-medium", CC_STAT_ATTENTION)}>Disputed</span>
          ) : (
            <span className={clsx("text-xs", CC_STAT_NEGATIVE)}>Missing</span>
          )}
          {!editingDispute ? (
            <div className="flex gap-2">
              <Button
                plain size="unstyled"
                type="button"
                className="px-0 text-[10px] font-medium text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                onClick={() => onDisputeChange("")}
              >
                Accept
              </Button>
              <Button
                plain size="unstyled"
                type="button"
                className={clsx(CC_ACCENT_LINK, "px-0 text-[10px]")}
                onClick={() => setEditingDispute(true)}
              >
                Dispute
              </Button>
            </div>
          ) : (
            <div className="w-full space-y-1">
              <textarea
                className="w-full resize-none rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/50 px-2 py-1.5 text-[11px] text-slate-700 dark:text-slate-200 placeholder:text-slate-500"
                rows={2}
                placeholder="Why is this not missing?"
                value={draftNote}
                onChange={(e) => setDraftNote(e.target.value)}
              />
              <div className="flex justify-end gap-1">
                <Button
                  plain size="unstyled"
                  type="button"
                  className="px-0 text-[10px] text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                  onClick={() => {
                    setDraftNote(disputeNote ?? "");
                    setEditingDispute(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  plain size="unstyled"
                  type="button"
                  className={clsx(CC_ACCENT_LINK, "px-0 text-[10px]")}
                  onClick={() => {
                    onDisputeChange(draftNote);
                    setEditingDispute(false);
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
      <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">
        {!item.checked
          ? "—"
          : isNotApplicable
            ? "N/A"
            : isPresent
              ? "On file"
              : isMissing
                ? "Missing"
                : isOptionalOpen
                  ? "Optional"
                  : "Required"}
      </span>
      )}
    </div>
  );
}

/* ============================================
   ASSESSMENT TAB
   ============================================ */
function iconForAssessmentGroup(groupId: string) {
  if (groupId === "kyc") return faUserCheck;
  if (groupId === "suitability") return faScaleBalanced;
  if (groupId === "disclosure") return faFileShield;
  return faClipboardCheck;
}

function AssessmentTab({
  documents,
  apiAssessment,
  ruleSet,
  ruleSetLoading,
  ruleSetError,
  caseType,
}: {
  documents: MockDocument[];
  apiAssessment: ApiAssessment | null;
  ruleSet: RuleSetVersion | null;
  ruleSetLoading: boolean;
  ruleSetError: string | null;
  caseType: string | null;
}) {
  const hasApiData = Boolean(apiAssessment?.groups?.length);
  const [confidenceFilter, setConfidenceFilter] = useState<RuleConfidenceFilter>("all");
  const documentNames = useMemo(
    () => new Map(documents.flatMap((d) => (d.id ? [[d.id, d.name] as const] : []))),
    [documents],
  );

  const activeCaseType = activeCaseTypeForScope(caseType);
  const ruleInScope = (applicable: string[] | undefined): boolean =>
    ruleAppliesToCase(activeCaseType, applicable);

  type GroupRow = {
    group_id: string;
    group_name: string;
    status: AssessmentPillStatus;
    rules: AssessmentRule[];
  };

  type RubricRulePreview = {
    id: string;
    label: string;
    description: string;
    conditional: boolean;
  };

  // Build indexes from the active rule set so we can enrich API results
  // (description/conditional are not stored on saved assessments) and
  // produce the empty-state rubric from the published rule set.
  const ruleById = new Map<
    string,
    { description: string; conditional: boolean; label: string; applicable_case_types: string[] }
  >();
  const rulesByGroup = new Map<string, RubricRulePreview[]>();
  const groupOrder: { id: string; name: string }[] = [];
  if (ruleSet) {
    const sortedGroups = [...ruleSet.groups].sort((a, b) => a.order - b.order);
    for (const g of sortedGroups) {
      groupOrder.push({ id: g.id, name: g.name });
      rulesByGroup.set(g.id, []);
    }
    const sortedRules = [...ruleSet.rules].sort((a, b) => a.order - b.order);
    for (const r of sortedRules) {
      const applicable = r.applicable_case_types ?? [];
      ruleById.set(r.id, {
        description: r.description,
        conditional: r.conditional,
        label: r.label,
        applicable_case_types: applicable,
      });
      if (!ruleInScope(applicable)) continue;
      const bucket = rulesByGroup.get(r.group_id);
      if (bucket) {
        bucket.push({ id: r.id, label: r.label, description: r.description, conditional: r.conditional });
      }
    }
  }

  let groups: GroupRow[] = [];
  if (hasApiData && apiAssessment) {
    groups = apiAssessment.groups.map((g) => ({
      group_id: g.group_id,
      group_name: g.group_name,
      status: g.status as AssessmentPillStatus,
      rules: g.rules
        .map((r) => {
          const meta = ruleById.get(r.rule_id);
          return {
            id: r.rule_id,
            label: r.label || meta?.label || r.rule_id,
            description: meta?.description ?? "",
            conditional: meta?.conditional ?? false,
            status: r.status as AssessmentRule["status"],
            evidence: r.evidence ?? null,
            citations: r.citations ?? [],
            confidence: (r.confidence as AssessmentRule["confidence"]) ?? null,
            applicable_case_types: meta?.applicable_case_types ?? [],
          };
        })
        // Defensive: if a saved assessment includes a rule that doesn't apply to
        // this case (e.g. case type changed), drop it so the UI matches scope.
        .filter((r) => ruleInScope(r.applicable_case_types))
        .map(({ applicable_case_types: _ignored, ...rule }) => rule),
    }));
  } else if (ruleSet) {
    groups = groupOrder.map((g) => {
      const bucket = rulesByGroup.get(g.id) ?? [];
      return {
        group_id: g.id,
        group_name: g.name,
        status: "pending" as AssessmentPillStatus,
        rules: bucket.map((r) => ({ ...r, status: "pending" as const, evidence: null, confidence: null })),
      };
    });
  }

  // Hide groups that have no rules left after case-type filtering — an empty
  // section would just look broken.
  groups = groups.filter((g) => g.rules.length > 0);

  const allRules = groups.flatMap((g) => g.rules);
  const confidenceCounts = countConfidenceLevels(allRules);
  const hasAnyConfidence = confidenceCounts.green + confidenceCounts.amber + confidenceCounts.red > 0;
  const filteredGroups = groups
    .map((g) => ({
      ...g,
      rules: g.rules.filter((r) => ruleMatchesConfidenceFilter(r, confidenceFilter)),
    }))
    .filter((g) => g.rules.length > 0);
  const filterActive = confidenceFilter !== "all";

  return (
    <div className="space-y-4" data-testid="assessment-tab">
      {!hasApiData && ruleSetLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-6">
          <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
          <span>Loading rubric…</span>
        </div>
      )}

      {!hasApiData && !ruleSetLoading && ruleSetError && (
        <div className={clsx(CC_PANEL, "text-sm", CC_ERROR_TEXT)}>
          Failed to load rule set: {ruleSetError}
        </div>
      )}

      {!hasApiData && !ruleSetLoading && !ruleSetError && !ruleSet && (
        <div className={clsx(CC_PANEL, "text-sm text-amber-800/90 dark:text-amber-800 dark:text-amber-200/90")}>
          No published rule set for your company yet. An administrator can publish one from{" "}
          <OsLink to="/case-checking/rules" className="underline font-medium">
            Rule sets
          </OsLink>
          .
        </div>
      )}

      {ruleSet && !activeCaseType && (
        <div className={clsx(CC_PANEL, "py-3 text-xs text-slate-400")}>
          Showing universal checks only. Case-type-specific checks will appear once the case type is detected.
        </div>
      )}

      {hasApiData && hasAnyConfidence ? (
        <ConfidenceFilterBar
          value={confidenceFilter}
          onChange={setConfidenceFilter}
          counts={confidenceCounts}
        />
      ) : null}

      <div className="space-y-3">
        {filteredGroups.map((g) => (
          <AssessmentRubricSection
            key={g.group_id}
            title={g.group_name}
            icon={iconForAssessmentGroup(g.group_id)}
            rules={g.rules}
            forceExpanded={filterActive}
            documentName={(id) => documentNames.get(id)}
          />
        ))}
        {filterActive && filteredGroups.length === 0 ? (
          <div className={clsx(CC_PANEL, "text-sm text-slate-500")}>
            No rules match this confidence filter.
          </div>
        ) : null}
      </div>

      {groups.length > 0 && (
        <div className={CC_LEGEND}>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCircleCheck} className={clsx("w-4 h-4", ICON_CHECK_PASSED)} />
            <span>Pass</span>
          </div>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCircleXmark} className="w-4 h-4 text-red-500" />
            <span>Fail</span>
          </div>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} className="w-4 h-4 text-amber-500" />
            <span>Unclear / not enough evidence</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-slate-300 dark:border-slate-600" />
            <span>Not yet assessed</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-4 px-1 rounded text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700/60">
              N/A
            </span>
            <span>Not applicable to this case</span>
          </div>
        </div>
      )}
    </div>
  );
}

interface AssessmentRule {
  id: string;
  label: string;
  description: string;
  conditional?: boolean;
  status: "passed" | "failed" | "unclear" | "pending" | "na";
  evidence: string | null;
  citations?: RuleCitationSnapshot[];
  confidence?: "green" | "amber" | "red" | null;
}

type RuleConfidenceFilter = "all" | "red" | "amber" | "green";

function countConfidenceLevels(rules: AssessmentRule[]) {
  return {
    red: rules.filter((r) => r.confidence === "red").length,
    amber: rules.filter((r) => r.confidence === "amber").length,
    green: rules.filter((r) => r.confidence === "green").length,
  };
}

function ruleMatchesConfidenceFilter(rule: AssessmentRule, filter: RuleConfidenceFilter): boolean {
  if (filter === "all") return true;
  return rule.confidence === filter;
}

function ConfidenceFilterBar({
  value,
  onChange,
  counts,
}: {
  value: RuleConfidenceFilter;
  onChange: (next: RuleConfidenceFilter) => void;
  counts: { red: number; amber: number; green: number };
}) {
  const options = [
    { id: "all" as const, label: "All" },
    {
      id: "red" as const,
      label: `Low ${counts.red}`,
      disabled: counts.red === 0,
    },
    {
      id: "amber" as const,
      label: `Medium ${counts.amber}`,
      disabled: counts.amber === 0,
    },
    {
      id: "green" as const,
      label: `High ${counts.green}`,
      disabled: counts.green === 0,
    },
  ];

  return (
    <div className={clsx(CC_PANEL, "py-3")}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className={clsx(CC_SECTION_LABEL, "mb-0.5")}>Agent confidence</p>
          <p className="text-xs text-slate-500">
            How sure the agent is of each verdict — filter to triage uncertain checks.
          </p>
        </div>
        <SegmentedPillToggle
          options={options}
          value={value}
          onChange={onChange}
          className="max-w-full flex-wrap"
          aria-label="Agent confidence"
          data-testid="confidence-filter"
          optionTestIdPrefix="confidence-filter"
        />
      </div>
    </div>
  );
}

function summarizeRuleOutcomes(rules: AssessmentRule[]) {
  const assessed = rules.filter((r) => r.status !== "na");
  return {
    passed: assessed.filter((r) => r.status === "passed").length,
    failed: assessed.filter((r) => r.status === "failed").length,
    unclear: assessed.filter((r) => r.status === "unclear").length,
    pending: assessed.filter((r) => r.status === "pending").length,
  };
}

function RuleOutcomeSummary({ rules }: { rules: AssessmentRule[] }) {
  const counts = summarizeRuleOutcomes(rules);
  const parts: { key: string; label: string; className: string }[] = [];
  if (counts.passed > 0) {
    parts.push({ key: "passed", label: `${counts.passed} passed`, className: CC_STAT_POSITIVE });
  }
  if (counts.unclear > 0) {
    parts.push({ key: "unclear", label: `${counts.unclear} unclear`, className: "text-amber-400" });
  }
  if (counts.failed > 0) {
    parts.push({ key: "failed", label: `${counts.failed} failed`, className: CC_STAT_NEGATIVE });
  }
  if (counts.pending > 0) {
    parts.push({ key: "pending", label: `${counts.pending} pending`, className: "text-slate-500" });
  }
  if (parts.length === 0) {
    return <span className="text-xs text-slate-500">No checks</span>;
  }
  return (
    <span className="text-xs tabular-nums">
      {parts.map((part, i) => (
        <React.Fragment key={part.key}>
          {i > 0 ? <span className="text-slate-600"> · </span> : null}
          <span className={part.className}>{part.label}</span>
        </React.Fragment>
      ))}
    </span>
  );
}

function AssessmentRubricSection({
  title,
  icon,
  rules,
  forceExpanded = false,
  documentName,
}: {
  title: string;
  icon: IconDefinition;
  rules: AssessmentRule[];
  forceExpanded?: boolean;
  documentName: (documentId: string) => string | undefined;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const open = forceExpanded || isExpanded;
  const hasFailed = rules.some((r) => r.status === "failed");

  return (
    <div className={CC_PANEL_FLUSH}>
      <Button
        plain
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3 min-w-0">
          <FontAwesomeIcon icon={icon} className="w-4 h-4 text-slate-400 shrink-0" />
          <span className="font-medium text-slate-900 dark:text-slate-100 truncate">{title}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <RuleOutcomeSummary rules={rules} />
          <FontAwesomeIcon
            icon={faChevronRight}
            className={clsx(
              "w-3 h-3 text-slate-500 transition-transform",
              open && "rotate-90",
              hasFailed && !open && clsx(CC_STAT_NEGATIVE, "opacity-80"),
            )}
          />
        </div>
      </Button>

      {open && (
        <div className="border-t border-slate-200 dark:border-white/5 divide-y divide-slate-200 dark:divide-white/5">
          {rules.map((rule) => (
            <div key={rule.id} className="px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {rule.status === "passed" && (
                    <FontAwesomeIcon icon={faCircleCheck} className={clsx("w-4 h-4", ICON_CHECK_PASSED)} />
                  )}
                  {rule.status === "failed" && (
                    <FontAwesomeIcon icon={faCircleXmark} className="w-4 h-4 text-red-500" />
                  )}
                  {rule.status === "unclear" && (
                    <FontAwesomeIcon icon={faTriangleExclamation} className="w-4 h-4 text-amber-500" />
                  )}
                  {rule.status === "pending" && (
                    <div className="w-4 h-4 rounded-full border-2 border-slate-300 dark:border-slate-600" />
                  )}
                  {rule.status === "na" && (
                    <span className="inline-flex items-center justify-center h-4 px-1 rounded text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700/60">
                      N/A
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{rule.label}</p>
                  {rule.description && rule.description !== rule.label && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {rule.description}
                    </p>
                  )}
                  {rule.evidence && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 italic">
                      {rule.evidence}
                    </p>
                  )}
                  {rule.citations?.length ? (
                    <div className="text-xs">
                      <RuleCitations
                        citations={rule.citations}
                        documentName={documentName}
                        testId={`assessment-rule-citations-${rule.id}`}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type ExportRowId =
  | "completeness"
  | "assessment_rules"
  | "remedials"
  | "peer_xlsx"
  | "rules_audit"
  | "ps_agent_docx"
  | "ps_comparison_docx"
  | "aq_file_review_docx";

function ArtefactsTab({
  caseId,
  fallbackTitle,
  hasAssessment,
  hasCompleteness,
  remedialCount,
}: {
  caseId: string;
  fallbackTitle: string;
  hasAssessment: boolean;
  hasCompleteness: boolean;
  remedialCount: number;
}) {
  const companyId = useCaseCheckingTenantId();
  const showFileReviewExports = supportsFileReviewExport(companyId);
  const [artifacts, setArtifacts] = useState<Artifact[] | null>(null);
  const [artifactsLoading, setArtifactsLoading] = useState(true);
  const [artifactsError, setArtifactsError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<ExportRowId | null>(null);
  const [previewing, setPreviewing] = useState<ExportRowId | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const safeStub = fallbackTitle.replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 60);

  useEffect(() => {
    let cancelled = false;
    setArtifactsLoading(true);
    setArtifactsError(null);
    listCaseArtifacts(caseId)
      .then((rows) => {
        if (!cancelled) setArtifacts(rows);
      })
      .catch((e) => {
        if (!cancelled) {
          setArtifactsError(e instanceof Error ? e.message : "Failed to load artefacts");
        }
      })
      .finally(() => {
        if (!cancelled) setArtifactsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const runDownload = async (id: ExportRowId, fn: () => Promise<void>) => {
    setDownloading(id);
    setExportError(null);
    try {
      await fn();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setExportError(detail || (e instanceof Error ? e.message : "Download failed"));
    } finally {
      setDownloading(null);
    }
  };

  const runPreview = async (id: ExportRowId, fn: () => Promise<void>) => {
    setPreviewing(id);
    setExportError(null);
    try {
      await fn();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setExportError(detail || (e instanceof Error ? e.message : "Preview failed"));
    } finally {
      setPreviewing(null);
    }
  };

  const exportRows: {
    id: ExportRowId;
    title: string;
    description: string;
    extension: ".xlsx" | ".docx";
    disabled?: boolean;
    action: () => void;
    preview?: () => void;
  }[] = [
      {
        id: "completeness",
        title: "Document completeness",
        description: "Checklist with red / amber / green rows — missing docs, concerns, and on-file items.",
        extension: ".xlsx",
        action: () =>
          runDownload("completeness", () =>
            downloadCompletenessXlsx(caseId, `completeness_${safeStub}.xlsx`)
          ),
      },
      {
        id: "assessment_rules",
        title: "Assessment rules",
        description: "Every rule with agent status, RAG, and evidence — filterable in Excel.",
        extension: ".xlsx",
        disabled: !hasAssessment,
        action: () =>
          runDownload("assessment_rules", () =>
            downloadAssessmentRulesXlsx(caseId, `assessment_rules_${safeStub}.xlsx`)
          ),
      },
      {
        id: "remedials",
        title: "Remedial actions",
        description: "All remedials with priority-based RAG for triage and sign-off.",
        extension: ".xlsx",
        disabled: remedialCount === 0,
        action: () =>
          runDownload("remedials", () =>
            downloadRemedialsXlsx(caseId, `remedials_${safeStub}.xlsx`)
          ),
      },
      {
        id: "peer_xlsx",
        title: "Peer review workbook",
        description: "Offline workbook with agent verdicts and empty columns for human peer review.",
        extension: ".xlsx",
        disabled: !hasAssessment,
        action: () =>
          runDownload("peer_xlsx", () =>
            downloadPeerReviewXlsx(caseId, `peer_review_${safeStub}.xlsx`)
          ),
      },
      {
        id: "rules_audit",
        title: "Rules audit",
        description:
          "Published rule set with this case's document and rule results; yellow columns for reviewer notes and recommended changes.",
        extension: ".xlsx",
        action: () =>
          runDownload("rules_audit", () => downloadRulesAuditXlsx(caseId, `rules_audit_${safeStub}.xlsx`)),
      },
    ];

  if (showFileReviewExports) {
    exportRows.push(
      {
        id: "ps_agent_docx",
        title: "Coverage review (agent)",
        description: "Word export of problem-statement ratings and agent narratives.",
        extension: ".docx",
        disabled: !hasAssessment,
        preview: () =>
          runPreview("ps_agent_docx", () => viewProblemStatementsAgentPdf(caseId)),
        action: () =>
          runDownload("ps_agent_docx", () =>
            downloadProblemStatementsAgentDocx(caseId, `coverage_review_agent_${safeStub}.docx`),
          ),
      },
      {
        id: "ps_comparison_docx",
        title: "Coverage review (agent vs human)",
        description: "Word export comparing agent narratives to human peer sign-off.",
        extension: ".docx",
        disabled: !hasAssessment,
        preview: () =>
          runPreview("ps_comparison_docx", () => viewProblemStatementsComparisonPdf(caseId)),
        action: () =>
          runDownload("ps_comparison_docx", () =>
            downloadProblemStatementsComparisonDocx(
              caseId,
              `coverage_review_comparison_${safeStub}.docx`,
            ),
          ),
      },
      {
        id: "aq_file_review_docx",
        title: "Advice Quality — File Review",
        description:
          "File review document with human grades, actions, inconsistencies, and rule feedback.",
        extension: ".docx",
        disabled: !hasAssessment,
        preview: () =>
          runPreview("aq_file_review_docx", () => viewAdviceQualityFileReviewPdf(caseId)),
        action: () =>
          runDownload("aq_file_review_docx", () =>
            downloadAdviceQualityFileReviewDocx(
              caseId,
              `advice_quality_file_review_${safeStub}.docx`,
            ),
          ),
      },
    );
  }

  return (
    <div className="space-y-6" data-testid="case-checking-artefacts-tab">
      <section className="space-y-3">
        <p className={CC_SECTION_LABEL}>Ani-generated artefacts</p>
        {artifactsLoading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Loading artefacts…
          </div>
        )}
        {artifactsError && (
          <div className="rounded-xl border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/40 p-3 text-sm text-rose-800 dark:text-rose-200">
            {artifactsError}
          </div>
        )}
        {!artifactsLoading && !artifactsError && (!artifacts || artifacts.length === 0) && (
          <div className={clsx(CC_PANEL, "py-8 text-center text-sm text-slate-500")}>
            No artefacts generated for this case yet.
          </div>
        )}
        {!artifactsLoading && artifacts && artifacts.length > 0 && (
          <div className="space-y-2">
            {artifacts.map((artifact) => (
              <ArtifactCard key={artifact._id} artifact={artifact} />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <p className={CC_SECTION_LABEL}>Export library</p>
        {exportError && (
          <div className="rounded-xl border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/40 p-3 text-sm text-rose-800 dark:text-rose-200">
            {exportError}
          </div>
        )}

        <div className={CC_PANEL_FLUSH}>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader className="w-[32%]">Export</TableHeader>
                <TableHeader className="w-[44%] hidden sm:table-cell">Description</TableHeader>
                <TableHeader className="w-44 text-right">Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {exportRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="align-top">
                    {row.preview ? (
                      <button
                        type="button"
                        className="inline-flex max-w-full items-center gap-1.5 rounded-md text-left font-medium text-slate-900 hover:text-slate-700 hover:bg-slate-950/5 disabled:opacity-50 dark:text-white dark:hover:text-slate-100 dark:hover:bg-white/10"
                        disabled={row.disabled || downloading !== null || previewing !== null}
                        onClick={row.preview}
                        aria-label={`Open ${row.title} PDF in a new tab`}
                        data-testid={`exports-preview-${row.id}`}
                      >
                        {previewing === row.id ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} className="w-3 h-3 shrink-0 animate-spin" />
                            <span>Opening preview…</span>
                          </>
                        ) : (
                          <>
                            <span>{row.title}</span>
                            <FontAwesomeIcon
                              icon={faArrowUpRightFromSquare}
                              className="w-3 h-3 shrink-0 text-slate-400 dark:text-slate-500"
                            />
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="font-medium text-slate-900 dark:text-white">{row.title}</div>
                    )}
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 sm:hidden">{row.description}</p>
                  </TableCell>
                  <TableCell className="align-top text-sm text-slate-600 dark:text-slate-300 hidden sm:table-cell">
                    {row.description}
                  </TableCell>
                  <TableCell className="text-right align-middle">
                    <div className="inline-flex justify-end">
                      <Button
                        plain
                        size="sm"
                        disabled={row.disabled || downloading !== null || previewing !== null}
                        icon={
                          downloading === row.id ? (
                            <FontAwesomeIcon icon={faSpinner} className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <FontAwesomeIcon icon={faDownload} className="w-3.5 h-3.5" />
                          )
                        }
                        onClick={row.action}
                        data-testid={`exports-download-${row.id}`}
                      >
                        {row.extension === ".docx" ? "DOCX" : "XLSX"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {!hasCompleteness && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Run a completeness check to populate the checklist export with requirement rows.
          </p>
        )}
      </section>
    </div>
  );
}

/* ============================================
   INCONSISTENCIES TAB
   ============================================ */

function shortenDocFilename(name: string, max = 40): string {
  if (!name) return "Document";
  if (name.length <= max) return name;
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot) : "";
  const base = dot > 0 ? name.slice(0, dot) : name;
  const keep = Math.max(8, max - ext.length - 1);
  return `${base.slice(0, keep)}…${ext}`;
}

/** Drop leading `snake_case_key=` noise from API value strings when present. */
function formatInconsistencyValue(raw: string): string {
  const eq = raw.indexOf("=");
  if (eq <= 0) return raw;
  const key = raw.slice(0, eq).trim();
  const detail = raw.slice(eq + 1).trim();
  if (/^[a-z][a-z0-9_]*$/i.test(key)) return detail;
  return raw;
}

function InconsistencyRow({ row, embedded = false }: { row: CaseInconsistency; embedded?: boolean }) {
  const rollup = inconsistencyRollupStatus(row);
  const isPass = rollup === "pass";
  const [expanded, setExpanded] = useState(false);
  const sourceCount = row.values.length;
  const showExpand = !isPass && sourceCount > 0;

  const footer =
    expanded && sourceCount > 0 ? (
      <div className="border-t border-slate-200 px-4 pb-4 pt-3 dark:border-white/5">
        <div className="ml-5 space-y-2">
          {row.values.map((v, j) => (
            <div
              key={`${v.document_id}-${j}`}
              className="rounded-lg bg-slate-50 px-3 py-2 dark:bg-white/[0.03]"
            >
              <p
                className="truncate text-xs text-slate-500"
                title={v.filename || v.document_id}
              >
                {shortenDocFilename(v.filename || v.document_id)}
              </p>
              <p className="mt-0.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                {formatInconsistencyValue(v.value)}
              </p>
            </div>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <CaseCheckingListRow
      variant={embedded ? "flush" : "panel"}
      onClick={showExpand ? () => setExpanded((open) => !open) : undefined}
      ariaExpanded={expanded}
      ariaLabel={
        showExpand
          ? `${row.check_id || "Finding"}, ${expanded ? "collapse" : "expand"} details`
          : undefined
      }
      innerClassName={embedded ? "px-0" : undefined}
      footer={footer}
    >
      <div className={clsx("w-1.5 h-1.5 rounded-full mt-2 shrink-0", INCONSISTENCY_ROLLUP_DOT[rollup])} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            {row.check_id ? `${row.check_id}` : "Finding"}
          </p>
          <span
            className={clsx(
              "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset",
              INCONSISTENCY_ROLLUP_BADGE[rollup],
            )}
          >
            {INCONSISTENCY_ROLLUP_LABEL[rollup]}
          </span>
        </div>
        <p
          className={clsx(
            "mt-1 text-sm leading-relaxed text-slate-400",
            !expanded && !isPass && !embedded && "line-clamp-2",
          )}
        >
          {row.message}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2 pt-0.5">
        {showExpand && !expanded && sourceCount > 0 ? (
          <span className="text-xs text-slate-500 tabular-nums">
            {sourceCount} source{sourceCount === 1 ? "" : "s"}
          </span>
        ) : null}
        {showExpand ? (
          <FontAwesomeIcon
            icon={faChevronRight}
            className={clsx("w-3 h-3 text-slate-600 transition-transform", expanded && "rotate-90")}
          />
        ) : null}
      </div>
    </CaseCheckingListRow>
  );
}

function applicableChronologyChecks(
  ruleSet: RuleSetVersion | null,
  caseType: string | null,
  caseTypes?: string[] | null,
): ChronologyCheck[] {
  if (!ruleSet?.chronology_checks?.length) return [];
  const scopedTypes = new Set(caseTypes ?? []);
  if (caseType) scopedTypes.add(caseType);
  return ruleSet.chronology_checks.filter((check) => {
    const applicable = check.applicable_case_types ?? [];
    if (applicable.length === 0) return true;
    if (scopedTypes.size === 0) return true;
    return applicable.some((t) => scopedTypes.has(t));
  });
}

function TimelineTab({
  timeline,
  chronologyChecks,
  inconsistencies,
}: {
  timeline: CaseTimeline | null;
  chronologyChecks: ChronologyCheck[];
  inconsistencies: CaseInconsistency[];
}) {
  const inconsistencyById = new Map(
    inconsistencies
      .filter((row) => row.check_id)
      .map((row) => [row.check_id!.toLowerCase(), row]),
  );
  const chronologyOutcomes = chronologyChecks.map((check) => ({
    check,
    row: inconsistencyById.get(check.id.toLowerCase()),
  }));
  const chronologyCounts = countInconsistencyRollups(
    chronologyOutcomes.map((o) => o.row).filter((row): row is CaseInconsistency => Boolean(row)),
  );
  const hasStory = Boolean(timeline?.narrative?.trim());

  if (!hasStory && chronologyChecks.length === 0) {
    return (
      <div className={clsx(CC_PANEL, "py-10 text-center")}>
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">No timeline yet</p>
        <p className="text-slate-500 mt-1 text-sm">
          The agent builds a case timeline after document triage.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {hasStory ? <TimelineStoryPanel timeline={timeline} /> : null}
      {timeline?.events?.length ? (
        <div className={CC_PANEL_FLUSH}>
          <p className={clsx(CC_SECTION_LABEL, "px-4 pt-3 pb-2 mb-0")}>Events</p>
          <ul className="divide-y divide-slate-200 dark:divide-white/5">
            {timeline.events.map((ev, i) => (
              <li key={`${ev.event}-${i}`} className="px-4 py-3">
                <p className="text-sm text-slate-700 dark:text-slate-200">{ev.event}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {[ev.date, ev.date_basis].filter(Boolean).join(" · ") || "Undated"}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {chronologyChecks.length > 0 ? (
        <div className={CC_PANEL_FLUSH}>
          <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2">
            <p className={clsx(CC_SECTION_LABEL, "!mb-0")}>Timing & chronology checks</p>
            <div className="flex gap-2 text-xs shrink-0">
              {chronologyCounts.fail > 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 dark:bg-white/[0.04] px-2 py-1 ring-1 ring-slate-200 dark:ring-white/10">
                  <span className={clsx("w-1.5 h-1.5 rounded-full", INCONSISTENCY_ROLLUP_DOT.fail)} />
                  <span className="font-medium text-slate-600 dark:text-slate-300 tabular-nums">{chronologyCounts.fail}</span>
                  <span className="text-slate-500">failed</span>
                </span>
              ) : null}
              {chronologyCounts.unclear > 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 dark:bg-white/[0.04] px-2 py-1 ring-1 ring-slate-200 dark:ring-white/10">
                  <span className={clsx("w-1.5 h-1.5 rounded-full", INCONSISTENCY_ROLLUP_DOT.unclear)} />
                  <span className="font-medium text-slate-600 dark:text-slate-300 tabular-nums">{chronologyCounts.unclear}</span>
                  <span className="text-slate-500">unclear</span>
                </span>
              ) : null}
            </div>
          </div>
          <ul className="divide-y divide-slate-200 dark:divide-white/5 border-t border-slate-200 dark:border-white/5">
            {chronologyOutcomes.map(({ check, row }) => (
              <li key={check.id} className="px-4 py-4">
                {row ? (
                  <InconsistencyRow row={row} embedded />
                ) : (
                  <div className="flex items-start gap-3">
                    <span className={clsx("w-1.5 h-1.5 rounded-full mt-2 shrink-0", "bg-slate-500")} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{check.id}</p>
                      <p className="text-sm text-slate-400 mt-1 leading-relaxed">{check.description}</p>
                      <p className="text-xs text-slate-500 mt-1">Not evaluated yet — re-run inconsistencies after timeline is built.</p>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function InconsistenciesTab({ inconsistencies }: { inconsistencies: CaseInconsistency[] }) {
  const issues = inconsistencies.filter(
    (row) =>
      row.status !== "pass" &&
      row.check_id?.toUpperCase() !== "BACKSTOP" &&
      !isChronologyCheckId(row.check_id),
  );
  const backstop = inconsistencies.filter((row) => row.check_id?.toUpperCase() === "BACKSTOP");
  const passed = inconsistencies.filter((row) => row.status === "pass");
  const [showPassed, setShowPassed] = useState(false);

  if (issues.length === 0 && backstop.length === 0 && passed.length === 0) {
    return (
      <div className={clsx(CC_PANEL, "py-10 text-center")}>
        <FontAwesomeIcon icon={faCircleCheck} className={clsx("w-12 h-12 mb-4", ICON_CHECK_PASSED)} />
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">No inconsistencies found</p>
        <p className="text-slate-500 mt-1 text-sm">Nothing conflicts across the documents on file.</p>
      </div>
    );
  }

  const orderedIssues = [...issues].sort((a, b) => {
    const order = { fail: 0, unclear: 1, na: 2, pass: 3 };
    return order[inconsistencyRollupStatus(a)] - order[inconsistencyRollupStatus(b)];
  });

  const issueCounts = countInconsistencyRollups(orderedIssues);

  return (
    <div className="space-y-4">
      {orderedIssues.length > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className={CC_SECTION_LABEL}>Cross-document consistency</p>
            <div className="flex gap-2 text-xs shrink-0">
              {issueCounts.fail > 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 dark:bg-white/[0.04] px-2 py-1 ring-1 ring-slate-200 dark:ring-white/10">
                  <span className={clsx("w-1.5 h-1.5 rounded-full", INCONSISTENCY_ROLLUP_DOT.fail)} />
                  <span className="font-medium text-slate-600 dark:text-slate-300 tabular-nums">{issueCounts.fail}</span>
                  <span className="text-slate-500">failed</span>
                </span>
              ) : null}
              {issueCounts.unclear > 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 dark:bg-white/[0.04] px-2 py-1 ring-1 ring-slate-200 dark:ring-white/10">
                  <span className={clsx("w-1.5 h-1.5 rounded-full", INCONSISTENCY_ROLLUP_DOT.unclear)} />
                  <span className="font-medium text-slate-600 dark:text-slate-300 tabular-nums">{issueCounts.unclear}</span>
                  <span className="text-slate-500">unclear</span>
                </span>
              ) : null}
            </div>
          </div>
          {orderedIssues.map((row, i) => (
            <InconsistencyRow key={`issue-${row.check_id ?? row.field}-${i}`} row={row} />
          ))}
        </div>
      ) : null}

      {backstop.length > 0 ? (
        <div className="space-y-3">
          <p className={CC_SECTION_LABEL}>Other findings</p>
          {backstop.map((row, i) => (
            <InconsistencyRow key={`backstop-${row.field}-${i}`} row={row} />
          ))}
        </div>
      ) : null}

      {passed.length > 0 ? (
        <div className="space-y-2">
          <Button
            plain
            type="button"
            onClick={() => setShowPassed((open) => !open)}
            className="flex w-full items-center justify-between rounded-lg bg-slate-50 dark:bg-white/[0.03] px-3 py-2 text-left ring-1 ring-slate-200 dark:ring-white/10"
          >
            <span className="text-sm text-slate-600 dark:text-slate-300">
              Passed checks
              <span className="ml-2 text-slate-500 tabular-nums">({passed.length})</span>
            </span>
            <FontAwesomeIcon
              icon={faChevronRight}
              className={clsx("w-3 h-3 text-slate-600 transition-transform", showPassed && "rotate-90")}
            />
          </Button>
          {showPassed ? (
            <div className="space-y-2">
              {passed.map((row, i) => (
                <InconsistencyRow key={`pass-${row.check_id ?? i}`} row={row} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ============================================
   REMEDIAL TAB
   ============================================ */
function summarizeActionPriorities(actions: (RemedialAction | ApiRemedialAction)[]) {
  const open = actions.filter(isOpenRemedialAction);
  return {
    high: open.filter((a) => a.priority === "high").length,
    medium: open.filter((a) => a.priority === "medium").length,
    low: open.filter((a) => a.priority === "low").length,
  };
}

function ActionPrioritySummary({ actions }: { actions: (RemedialAction | ApiRemedialAction)[] }) {
  const counts = summarizeActionPriorities(actions);
  const parts: { key: string; label: string; className: string }[] = [];
  if (counts.high > 0) {
    parts.push({ key: "high", label: `${counts.high} high`, className: CC_STAT_NEGATIVE });
  }
  if (counts.medium > 0) {
    parts.push({ key: "medium", label: `${counts.medium} medium`, className: "text-amber-400" });
  }
  if (counts.low > 0) {
    parts.push({ key: "low", label: `${counts.low} low`, className: "text-slate-500" });
  }
  return (
    <span className="text-xs tabular-nums">
      {parts.map((part, i) => (
        <React.Fragment key={part.key}>
          {i > 0 ? <span className="text-slate-600"> · </span> : null}
          <span className={part.className}>{part.label}</span>
        </React.Fragment>
      ))}
    </span>
  );
}

function RemedialRoleSection({
  role,
  actions,
  documentIngest,
  missingDocuments = [],
  onScrollToChecklistItem,
}: {
  role: CaseRole;
  actions: (RemedialAction | ApiRemedialAction)[];
  documentIngest?: CaseDocumentIngestProps;
  missingDocuments?: MissingDocument[];
  onScrollToChecklistItem?: (documentType: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Accordion
      isExpanded={expanded}
      onToggle={() => setExpanded((open) => !open)}
      className={clsx(CC_PANEL_FLUSH, "!rounded-xl border-0 ring-1 ring-slate-200 dark:ring-white/5")}
      headerClassName="hover:bg-slate-50 dark:hover:bg-white/[0.03] !px-4 !py-3"
      contentClassName="!p-0 border-slate-200 dark:border-white/5"
      header={
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
            {CASE_ROLE_LABELS[role]}
          </span>
          <ActionPrioritySummary actions={actions} />
        </div>
      }
    >
      <div className="divide-y divide-slate-200 dark:divide-white/5">
        {actions.map((action, i) => {
          const showUpload = Boolean(documentIngest) && isUploadMissingDocumentAction(action);
          const resolved = showUpload
            ? resolveUploadActionDocument(action as ApiRemedialAction, missingDocuments)
            : null;
          const actionKey = "id" in action && action.id ? action.id : String(i);

          return (
            <div key={i} className="flex items-start gap-3 p-4">
              <div
                className={clsx(
                  "mt-2 h-1.5 w-1.5 shrink-0 rounded-full",
                  action.priority === "high"
                    ? "bg-red-500"
                    : action.priority === "low"
                      ? "bg-slate-400"
                      : "bg-amber-500",
                )}
              />
              <div className="min-w-0 flex-1 space-y-3">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{action.title}</p>
                {showUpload && documentIngest ? (
                  <>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      {action.description ? (
                        <p className="min-w-0 flex-1 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                          {action.description}
                        </p>
                      ) : (
                        <div className="min-w-0 flex-1" />
                      )}
                      <div className="shrink-0">
                        <CaseDocumentIngestButtons
                          documentIngest={documentIngest}
                          compact
                          testIdPrefix={`remedial-action-${actionKey}`}
                        />
                      </div>
                    </div>
                    {resolved?.documentType && onScrollToChecklistItem ? (
                      <button
                        type="button"
                        className={CC_SUBTLE_LINK}
                        onClick={() => onScrollToChecklistItem(resolved.documentType)}
                        data-testid={`remedial-view-checklist-${actionKey}`}
                      >
                        View in checklist
                      </button>
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm leading-relaxed text-slate-400">{action.description}</p>
                )}
                {ruleIdsFor(action).length > 0 ? (
                  <div className="mt-2 flex flex-wrap items-center gap-1">
                    <span className="text-[10px] uppercase tracking-wide text-slate-500">Covers</span>
                    {ruleIdsFor(action).map((rid) => (
                      <span
                        key={rid}
                        className="inline-flex items-center rounded bg-slate-50 px-1.5 py-0.5 text-[10px] font-mono text-slate-600 ring-1 ring-inset ring-slate-200 dark:bg-white/[0.02] dark:text-slate-300 dark:ring-white/10"
                      >
                        {rid}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Accordion>
  );
}

function RemedialTab({
  actions,
  assessment,
  inconsistencies,
  documentIngest,
  missingDocuments = [],
  onScrollToChecklistItem,
}: {
  actions: (RemedialAction | ApiRemedialAction)[];
  assessment?: ApiAssessment | null;
  inconsistencies?: CaseInconsistency[] | null;
  documentIngest?: CaseDocumentIngestProps;
  missingDocuments?: MissingDocument[];
  onScrollToChecklistItem?: (documentType: string) => void;
}) {
  const openActions = actions.filter(isOpenRemedialAction);
  const coverageGaps = findRuleCoverageGaps(assessment ?? null, actions, inconsistencies);
  const failedGaps = coverageGaps.filter((g) => g.status === "failed");
  const unclearGaps = coverageGaps.filter((g) => g.status === "unclear");

  if (openActions.length === 0 && coverageGaps.length === 0) {
    return (
      <div className={clsx(CC_PANEL, "py-10 text-center")}>
        <FontAwesomeIcon icon={faCircleCheck} className={clsx("w-12 h-12 mb-4", ICON_CHECK_PASSED)} />
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">No actions required</p>
        <p className="text-slate-500 mt-1 text-sm">This case passed all assessment criteria.</p>
      </div>
    );
  }

  const byRole = CASE_ROLES.reduce((acc, role) => {
    acc[role] = openActions.filter((a) => a.role === role);
    return acc;
  }, {} as Record<CaseRole, typeof openActions>);

  return (
    <div className="space-y-3">
      {failedGaps.length > 0 ? (
        <CoverageGapBanner tone="danger" title="Failed rules with no remedial" gaps={failedGaps} />
      ) : null}
      {unclearGaps.length > 0 ? (
        <CoverageGapBanner tone="warning" title="Unclear rules with no remedial" gaps={unclearGaps} />
      ) : null}
      {CASE_ROLES.map((role) => {
        const roleActions = byRole[role];
        if (roleActions.length === 0) return null;
        return (
          <RemedialRoleSection
            key={role}
            role={role}
            actions={roleActions}
            documentIngest={documentIngest}
            missingDocuments={missingDocuments}
            onScrollToChecklistItem={onScrollToChecklistItem}
          />
        );
      })}
    </div>
  );
}

/* ============================================
   NEW CASE DIALOG
   ============================================ */

function NewCaseDialog({
  onClose,
  onCreated,
  agenticLaunch = false,
  companyId,
  onMissionLaunched,
}: {
  onClose: () => void;
  onCreated?: (caseId: string) => void;
  agenticLaunch?: boolean;
  companyId?: string;
  onMissionLaunched?: (missionId: string, taskId: string | null) => void;
}) {
  const agentName = caseCheckAgentName(companyId);
  const [agenticError, setAgenticError] = useState<string | null>(null);
  const [isAgenticProcessing, setIsAgenticProcessing] = useState(false);
  const [agenticProcessingStep, setAgenticProcessingStep] = useState("");

  const fileUpload = useCaseNewCaseFileUpload({
    companyId,
    flowKind: "create_case",
    mockData: USE_MOCK_DATA,
  });

  const handleLaunchAgenticMission = async () => {
    setAgenticError(null);
    setIsAgenticProcessing(true);
    setAgenticProcessingStep(`Starting file check with ${agentName}…`);
    try {
      const result = await AgenticV2API.createCaseCheckMission({});
      onMissionLaunched?.(result.mission_id, result.task_id);
    } catch (err) {
      setAgenticError(
        err instanceof Error ? err.message : "Could not start mission — try again.",
      );
    } finally {
      setIsAgenticProcessing(false);
      setAgenticProcessingStep("");
    }
  };

  const handleCreateCase = async () => {
    const caseId = await fileUpload.submit();
    if (!caseId) return;
    if (onCreated) {
      onCreated(caseId);
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size="2xl"
    >
      <div className="border-b border-slate-200 dark:border-slate-700">
        <DialogTitle>
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="text-lg font-medium text-slate-900 dark:text-white">New Case</span>
              {USE_MOCK_DATA && (
                <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded">
                  MOCK MODE
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 dark:hover:bg-slate-700"
              aria-label="Close dialog"
            >
              <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
            </button>
          </div>
        </DialogTitle>
      </div>

      {/* Content */}
      <DialogBody className="p-6 pt-6 overflow-y-auto flex-1">
        {agenticLaunch ? (
          <div className="space-y-4">
            {agenticError ? (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-700 dark:text-red-300 text-sm">{agenticError}</p>
              </div>
            ) : null}
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {agentName} runs your FCA file check in Mission Control — upload advice
              files in the capture task, then the mission continues through triage,
              assessment, reviewer notes, and report automatically.
            </p>
            {isAgenticProcessing && agenticProcessingStep ? (
              <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg flex items-center gap-3">
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-indigo-500 animate-spin" />
                <p className="text-indigo-700 dark:text-indigo-300 text-sm font-medium">{agenticProcessingStep}</p>
              </div>
            ) : null}
          </div>
        ) : (
          <CaseNewCaseFileUpload
            files={fileUpload.files}
            isProcessing={fileUpload.isProcessing}
            processingStep={fileUpload.processingStep}
            fileInputRef={fileUpload.fileInputRef}
            onFileSelect={fileUpload.handleFileSelect}
            onPickFiles={() =>
              USE_MOCK_DATA ? fileUpload.handleMockUpload() : fileUpload.fileInputRef.current?.click()
            }
            error={fileUpload.error}
          />
        )}
      </DialogBody>

      {/* Footer */}
      <div className="border-t border-slate-200 dark:border-slate-700">
        <DialogActions>
          <div className="w-full">
            {agenticLaunch ? (
              <Button
                type="button"
                color="indigo"
                onClick={() => void handleLaunchAgenticMission()}
                disabled={isAgenticProcessing}
                className="w-full py-3 !rounded-xl"
                icon={isAgenticProcessing ? <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" /> : undefined}
              >
                {isAgenticProcessing ? "Starting…" : `Start file check with ${agentName}`}
              </Button>
            ) : (
              <Button
                type="button"
                color="indigo"
                onClick={() => void handleCreateCase()}
                disabled={!fileUpload.hasFiles || fileUpload.isProcessing}
                className="w-full py-3 !rounded-xl"
                icon={fileUpload.isProcessing ? <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" /> : undefined}
              >
                {fileUpload.isProcessing ? "Processing..." : USE_MOCK_DATA ? "Start Analysis" : "Create Case"}
              </Button>
            )}
          </div>
        </DialogActions>
      </div>
    </Dialog>
  );
}

/* ============================================
   TYPES & MOCK DATA
   ============================================ */

// Document classification types
type DocumentType =
  | "fact-find"
  | "file-note"
  | "suitability-report"
  | "csa"
  | "oxford-risk"
  | "smartsearch-idd"
  | "charges-spreadsheet"
  | "voyant-cashflow"
  | "exception-form"
  | "valuation"
  | "kiid"
  | "illustration"
  | "fe-performance"
  | "off-cip-research"
  | "source-of-funds"
  | "cgt-report"
  | "tax-calculation"
  | "segment-confirmation"
  | "sustainability-confirmation"
  | "annuity-quote"
  | "kfd"
  | "terms-conditions"
  | "cost-comparison"
  | "ceding-valuation"
  | "br19"
  | "drawdown-illustration"
  | "expenditure-breakdown"
  | "factsheet-brochure"
  | "micap-report"
  | "application-form"
  | "trust-deed"
  | "registration-cert"
  | "trustee-fact-find"
  | "trustee-aml"
  | "mortgage-fact-find"
  | "affordability-calc"
  | "payslips"
  | "bank-statements"
  | "open-market-quotes"
  | "dip"
  | "health-questionnaire"
  | "tvas-report"
  | "correspondence"
  | "other";

// Case types
type CaseType =
  | "annual-review"
  | "fund-switch"
  | "top-up"
  | "isa-gia-withdrawal"
  | "bond-withdrawal"
  | "pension-withdrawal"
  | "platform-transfer"
  | "new-business"
  | "first-fad-drawdown"
  | "br-vct-seis"
  | "new-trust"
  | "existing-trust"
  | "mortgage"
  | "annuity"
  | "other";

// Human-readable case type names
const CASE_TYPE_LABELS: Record<CaseType, string> = {
  "annual-review": "Annual Review (no change)",
  "fund-switch": "Fund Switch",
  "top-up": "Top Up",
  "isa-gia-withdrawal": "ISA/GIA Withdrawal",
  "bond-withdrawal": "Bond Withdrawal",
  "pension-withdrawal": "Ad hoc Pension Withdrawal",
  "platform-transfer": "Platform Transfer",
  "new-business": "New Business",
  "first-fad-drawdown": "First-time FAD Drawdown",
  "br-vct-seis": "BR/VCT/SEIS",
  "new-trust": "New Trust",
  "existing-trust": "Existing Trust",
  "mortgage": "Mortgage",
  "annuity": "Annuity",
  "other": "Other",
};

interface MockCase {
  id: string;
  companyId?: string;
  adviser: string;
  paraplanner: string;
  client: string;
  date: string;
  grade: Grade;
  /** True once a final review exists — drives board grade columns. */
  hasReview: boolean;
  /** Per-role grades from the latest review (empty until review is generated). */
  roleGrades?: RoleGrade[];
  /** Formal compliance routing from the latest review. */
  humanReview?: HumanReviewRouting | null;
  stage: Stage;
  preAdviceRequired: boolean;
  adviceType: string;
  caseType: CaseType;
  caseTypes?: string[];
  transactionSubtype?: string | null;
  reviewProfile?: string | null;
  adviceScope?: AdviceScope | null;
  advisedParties?: AdvisedParty[];
  adviceScopeReasoning?: string | null;
  assessment: MockAssessmentOverview;
  documents: MockDocument[];
  checklist: ChecklistItem[];
  remedialActions: Array<RemedialAction | ApiRemedialAction>;
  /** CRM/source linkage when the case is synced from Progeny (etc.). */
  metadata?: CaseMetadata | null;
}

function contextPatchFromApi(c: ApiCase): Partial<MockCase> {
  return {
    client: c.client_name || "",
    adviser: c.adviser || "",
    paraplanner: c.paraplanner || "",
    preAdviceRequired: c.pre_advice_required ?? false,
    adviceType: c.advice_type || "",
    caseType: (c.case_type as CaseType) || "other",
    caseTypes: c.case_types ?? undefined,
    transactionSubtype: c.transaction_subtype ?? null,
    reviewProfile: c.review_profile ?? null,
    adviceScope: c.advice_scope ?? null,
    advisedParties: c.advised_parties ?? [],
    adviceScopeReasoning: c.advice_scope_reasoning ?? null,
    ...(c.review
      ? {
        hasReview: true,
        humanReview: c.review.human_review ?? null,
        grade: caseReviewRollupGrade(c.review, c.grade ?? "unclear"),
        roleGrades: c.review.role_grades ?? [],
      }
      : {}),
  };
}

function mapApiCaseToMockCase(c: ApiCase): MockCase {
  return {
    id: c.id,
    companyId: c.company_id,
    adviser: c.adviser || "",
    paraplanner: c.paraplanner || "",
    client: c.client_name || "",
    date: c.created_at
      ? new Date(c.created_at).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
      : "",
    grade: caseReviewRollupGrade(c.review, c.grade ?? "unclear"),
    hasReview: Boolean(c.review),
    roleGrades: c.review?.role_grades ?? [],
    humanReview: c.review?.human_review ?? null,
    stage: (c.stage as Stage) || "ingestion",
    preAdviceRequired: c.pre_advice_required ?? false,
    adviceType: c.advice_type || "",
    caseType: (c.case_type as CaseType) || "other",
    caseTypes: c.case_types ?? undefined,
    transactionSubtype: c.transaction_subtype ?? null,
    reviewProfile: c.review_profile ?? null,
    adviceScope: c.advice_scope ?? null,
    advisedParties: c.advised_parties ?? [],
    adviceScopeReasoning: c.advice_scope_reasoning ?? null,
    assessment: mapAssessmentFromApi(c.assessment),
    documents: [],
    checklist: [],
    remedialActions: c.remedial_actions ?? [],
    metadata: c.metadata ?? null,
  };
}

interface MockDocument {
  id?: string;
  name: string;
  fileType?: string;
  type: DocumentType;
  classification: string;
  classificationError?: string | null;
  valid: boolean;
  // AI-extracted data
  summary?: string | null;
  confidence?: number | null;
  reasoning?: string | null;
  redFlags?: RedFlag[] | null;
  keyDates?: Record<string, string> | null;
  keyParties?: Record<string, string> | null;
  keyFigures?: Record<string, CaseDocumentKeyFigureValue> | null;
  createdAt?: string | null;
  metadata?: {
    content_readable?: boolean;
    unreadable_reason?: string | null;
    classification_source?: string;
  } | null;
}

interface RedFlag {
  severity: "high" | "medium" | "low";
  category: string;
  description: string;
  location?: string;
  remedial_action?: string;
}

interface ChecklistItem {
  name: string;
  type: DocumentType;
  required: boolean;
  /** Blocking missing per completeness API (drives red missing state and tab badge). */
  blocking?: boolean;
  present: boolean;
  /** False until a completeness check has been persisted on the case. */
  checked: boolean;
  notApplicable?: { reason: string };
  hasInvalidDoc?: boolean;
  note?: string;
  conditionNote?: string;
  linkedDocuments?: ChecklistLinkedDocument[];
}

interface ChecklistLinkedDocument {
  id?: string;
  name: string;
  typeLabel?: string;
}

interface RemedialAction {
  title: string;
  description: string;
  role: CaseRole;
  priority: "high" | "medium" | "low";
  status?: "pending" | "in_progress" | "complete";
  rule_ids?: string[];
  rule_id?: string | null;
}

// Map API classification strings to DocumentType
// The API now returns our exact types, but we keep mappings for backwards compatibility
function mapClassificationToType(classification: string | null): DocumentType {
  if (!classification) return "other";

  const normalized = classification.toLowerCase();

  // Check if it's already a valid DocumentType
  const validTypes: DocumentType[] = [
    "fact-find", "file-note", "suitability-report", "csa", "oxford-risk",
    "smartsearch-idd", "charges-spreadsheet", "voyant-cashflow", "exception-form",
    "valuation", "kiid", "illustration", "fe-performance", "off-cip-research",
    "source-of-funds", "cgt-report", "tax-calculation", "segment-confirmation",
    "sustainability-confirmation", "annuity-quote", "kfd", "cost-comparison",
    "ceding-valuation", "br19", "drawdown-illustration", "expenditure-breakdown",
    "factsheet-brochure", "micap-report", "application-form", "trust-deed",
    "registration-cert", "trustee-fact-find", "trustee-aml", "mortgage-fact-find",
    "affordability-calc", "payslips", "bank-statements", "open-market-quotes",
    "dip", "health-questionnaire", "tvas-report", "correspondence", "other"
  ];

  if (validTypes.includes(normalized as DocumentType)) {
    return normalized as DocumentType;
  }

  // Legacy mappings for backwards compatibility with old classifications
  const legacyMapping: Record<string, DocumentType> = {
    "suitability": "suitability-report",
    "disclosure": "csa",
    "client-agreement": "csa",
    "risk-profile": "oxford-risk",
    "aml-document": "smartsearch-idd",
    "id-verification": "smartsearch-idd",
    "key-features": "kfd",
    "fee-agreement": "charges-spreadsheet",
  };

  return legacyMapping[normalized] || "other";
}

const MOCK_OVERVIEW_ALL_SUFFICIENT: MockAssessmentOverview = {
  groups: [
    { group_id: "kyc", group_name: "Know Your Client", status: "sufficient" },
    { group_id: "suitability", group_name: "Suitability", status: "sufficient" },
    { group_id: "disclosure", group_name: "Supporting Documents / Disclosure", status: "sufficient" },
  ],
};

const MOCK_OVERVIEW_UNSUITABLE: MockAssessmentOverview = {
  groups: [
    { group_id: "kyc", group_name: "Know Your Client", status: "sufficient" },
    { group_id: "suitability", group_name: "Suitability", status: "insufficient" },
    { group_id: "disclosure", group_name: "Supporting Documents / Disclosure", status: "insufficient" },
  ],
};

const MOCK_OVERVIEW_DEV_NEEDS: MockAssessmentOverview = {
  groups: [
    { group_id: "kyc", group_name: "Know Your Client", status: "sufficient" },
    { group_id: "suitability", group_name: "Suitability", status: "sufficient" },
    { group_id: "disclosure", group_name: "Supporting Documents / Disclosure", status: "insufficient" },
  ],
};

const MOCK_OVERVIEW_UNCLEAR: MockAssessmentOverview = {
  groups: [
    { group_id: "kyc", group_name: "Know Your Client", status: "insufficient" },
    { group_id: "suitability", group_name: "Suitability", status: "sufficient" },
    { group_id: "disclosure", group_name: "Supporting Documents / Disclosure", status: "sufficient" },
  ],
};

const MOCK_CASES: MockCase[] = [
  {
    id: "427885",
    adviser: "Sarah Mitchell",
    paraplanner: "Tom Henderson",
    client: "Margaret Thompson",
    date: "22 Jan 2026",
    grade: "suitable",
    hasReview: true,
    humanReview: "no_formal_review",
    stage: "complete",
    preAdviceRequired: false,
    adviceType: "Pension Review",
    caseType: "annual-review",
    assessment: MOCK_OVERVIEW_ALL_SUFFICIENT,
    documents: [
      { name: "FactFind_Thompson_2026.pdf", type: "fact-find", classification: "Fact Find", valid: true },
      { name: "FileNote_22Jan.docx", type: "file-note", classification: "File Note", valid: true },
      { name: "SuitabilityReport.pdf", type: "suitability-report", classification: "Suitability Report", valid: true },
      { name: "ClientAgreement.pdf", type: "csa", classification: "Client Service Agreement", valid: true },
      { name: "OxfordRisk.pdf", type: "oxford-risk", classification: "Oxford Risk Profile", valid: true },
      { name: "Smartsearch.pdf", type: "smartsearch-idd", classification: "Smartsearch + IDD", valid: true },
      { name: "Charges.xlsx", type: "charges-spreadsheet", classification: "Charges Spreadsheet", valid: true },
      { name: "Valuation_Jan2026.pdf", type: "valuation", classification: "Valuation", valid: true },
    ],
    checklist: [],
    remedialActions: [],
  },
  {
    id: "426985",
    adviser: "James Richardson",
    paraplanner: "Sophie Clarke",
    client: "Dorothy Anderson",
    date: "22 Jan 2026",
    grade: "unsuitable",
    hasReview: true,
    humanReview: "priority_human",
    stage: "remedial",
    preAdviceRequired: true,
    adviceType: "Annuity Purchase",
    caseType: "annuity",
    assessment: MOCK_OVERVIEW_UNSUITABLE,
    documents: [
      { name: "FactFind_Anderson.pdf", type: "fact-find", classification: "Fact Find", valid: true },
      { name: "MeetingNotes.docx", type: "file-note", classification: "File Note", valid: true },
      { name: "SuitabilityReport.pdf", type: "suitability-report", classification: "Suitability Report", valid: false },
    ],
    checklist: [],
    remedialActions: [
      { title: "Update Suitability Report", description: "Current report uses outdated template. Re-generate using v3.2 template with updated fee disclosures.", role: "paraplanner", priority: "high" },
      { title: "Obtain Client Agreement", description: "Missing signed client agreement. Arrange for client signature.", role: "ce", priority: "high" },
      { title: "Add Annuity Comparison", description: "Pre-advice requirement for annuity purchase: include comparison of at least 3 providers.", role: "paraplanner", priority: "medium" },
    ],
  },
  {
    id: "425112",
    adviser: "Emma Williams",
    paraplanner: "Daniel Foster",
    client: "Robert Clarke",
    date: "21 Jan 2026",
    grade: "dev-needs",
    hasReview: true,
    humanReview: "no_formal_review",
    stage: "complete",
    preAdviceRequired: false,
    adviceType: "ISA Transfer",
    caseType: "platform-transfer",
    assessment: MOCK_OVERVIEW_DEV_NEEDS,
    documents: [
      { name: "FactFind_Clarke.pdf", type: "fact-find", classification: "Fact Find", valid: true },
      { name: "FileNote.docx", type: "file-note", classification: "File Note", valid: true },
      { name: "Suitability.pdf", type: "suitability-report", classification: "Suitability Report", valid: true },
      { name: "KFD_Platform.pdf", type: "kfd", classification: "KFD + T&Cs", valid: true },
      { name: "CostComparison.xlsx", type: "cost-comparison", classification: "Cost Comparison", valid: true },
    ],
    checklist: [],
    remedialActions: [],
  },
  {
    id: "424890",
    adviser: "Michael Brown",
    paraplanner: "Rachel Green",
    client: "Susan Taylor",
    date: "20 Jan 2026",
    grade: "suitable",
    hasReview: true,
    humanReview: "no_formal_review",
    stage: "complete",
    preAdviceRequired: false,
    adviceType: "Savings Review",
    caseType: "annual-review",
    assessment: MOCK_OVERVIEW_ALL_SUFFICIENT,
    documents: [
      { name: "FactFind_Taylor.pdf", type: "fact-find", classification: "Fact Find", valid: true },
      { name: "Notes_Jan20.docx", type: "file-note", classification: "File Note", valid: true },
      { name: "SuitabilityReport.pdf", type: "suitability-report", classification: "Suitability Report", valid: true },
      { name: "CSA_Taylor.pdf", type: "csa", classification: "Client Service Agreement", valid: true },
      { name: "OxfordRisk_Taylor.pdf", type: "oxford-risk", classification: "Oxford Risk Profile", valid: true },
      { name: "AML_Taylor.pdf", type: "smartsearch-idd", classification: "Smartsearch + IDD", valid: true },
      { name: "Charges_Taylor.xlsx", type: "charges-spreadsheet", classification: "Charges Spreadsheet", valid: true },
      { name: "Valuation_Taylor.pdf", type: "valuation", classification: "Valuation", valid: true },
    ],
    checklist: [],
    remedialActions: [],
  },
  {
    id: "423456",
    adviser: "Lisa Johnson",
    paraplanner: "Mark Wilson",
    client: "William Davis",
    date: "19 Jan 2026",
    grade: "unclear",
    hasReview: false,
    stage: "assessment",
    preAdviceRequired: true,
    adviceType: "Pension Transfer >£1m",
    caseType: "platform-transfer",
    assessment: MOCK_OVERVIEW_UNCLEAR,
    documents: [
      { name: "FactFind_Davis.pdf", type: "fact-find", classification: "Fact Find", valid: false },
      { name: "FileNote.docx", type: "file-note", classification: "File Note", valid: true },
      { name: "SuitabilityReport.pdf", type: "suitability-report", classification: "Suitability Report", valid: true },
    ],
    checklist: [],
    remedialActions: [
      { title: "Complete Fact Find", description: "Sections 4-6 (employment history, existing provisions, attitude to risk) are incomplete.", role: "planner", priority: "high" },
      { title: "Obtain TVAS Report", description: "Required for pension transfers over £30k. Missing from case file.", role: "paraplanner", priority: "high" },
    ],
  },
];
